/* eslint-disable */
import { API_URL } from '../../config';
import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppData } from '../../context/AppDataContext';
import { formatCurrency } from '../../utils/helpers';
import { splitTaxExpenses, capitalCostsSummary, isCapitalCategory, CAPITAL_COSTS_NOTE } from '../../utils/expenseCategories';
import { MTD_SCOPE_TEXT, mtdQuarterData, mtdDetailItems, mtdJointQuarterData, mtdJointDetailItems, mtdJointExcelRows } from '../../utils/mtdScope';
import { JOINT_FILING_NOTE, SHARE_PROMPT, ownershipMatches, isJointProperty, propertyShare, shareOf, splitTaxRows, sumTaxRows, sumJointYourShare, jointTaxExcelRows, signedMoney, shareLabel } from '../../utils/jointShare';
import { serviceRequestsAPI, companiesAPI } from '../../services/api';
import { isIrishProperty, effectiveJurisdiction } from '../../utils/propertyCountry';
import { normCurrency, formatMoney, sortCurrencies, companyExpenseCurrency, yearDetail, monthlyByCurrency, monthlySummaryRows, totalRowsByCurrency, capitalByCurrency, capitalRows, companyTotalsByCurrency, taxReportOverheads } from '../../utils/currencyTotals';
import * as XLSX from 'xlsx';
import { useBilling } from '../billing/BillingContext';
import { UpgradeModal } from '../billing/UpgradePrompts';

export default function Reports() {
  const navigate = useNavigate();
  // COMPANY-007: the raw sets from context are aliased, then re-exported below
  // as company-scoped `properties` and `companyExpenses`. Every report in this
  // file already reads those two names, so scoping them once here applies the
  // company filter to all 13 reports without touching each report's own logic
  // -- far less risk than 13 separate filter implementations in a 2,700-line file.
  const { properties: allProperties, payments, expenses, companyExpenses: allCompanyExpenses, tenants } = useAppData();
  const { canAccess } = useBilling();
  const reportsUnlocked = canAccess('reports');
  const [upgradeModal, setUpgradeModal] = useState(false);
  const [exportRange, setExportRange]   = useState('all');
  const [activeReport, setActiveReport] = useState(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
  const [selectedOwnership, setSelectedOwnership] = useState('all');
  const [selectedCurrency, setSelectedCurrency] = useState('all');
  const [mtdTaxYear, setMtdTaxYear] = useState(`${new Date().getFullYear()-1}-${new Date().getFullYear()}`);
  const [mtdQuarter, setMtdQuarter] = useState('all');
  const [monthlyYear, setMonthlyYear] = useState(new Date().getFullYear().toString());
  const [monthlyOwnership, setMonthlyOwnership] = useState('all');
  const [monthlyCurrency, setMonthlyCurrency] = useState('all');
  const [taxYear, setTaxYear] = useState(`${new Date().getFullYear()-1}-${new Date().getFullYear()}`);
  const [taxOwnership, setTaxOwnership] = useState('all');
  const [taxJurisdiction, setTaxJurisdiction] = useState('all');
  const [taxCurrency, setTaxCurrency] = useState('all');
  const [capitalOwnership, setCapitalOwnership] = useState('all');
  const [capitalCurrency, setCapitalCurrency] = useState('all');
  const [companyReportYear, setCompanyReportYear] = useState(new Date().getFullYear().toString());
  const [companies, setCompanies] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState('unassigned');

  useEffect(() => {
    companiesAPI.getAll().then(rows => {
      setCompanies(rows);
      // Default to the first real company if any exist, since that's almost
      // always what someone actually wants to see first — falls back to the
      // "Unassigned" bucket only if no companies have been set up yet.
      if (rows.length > 0) setSelectedCompanyId(String(rows[0].id));
    }).catch(() => {});
  }, []);
  // New reports state
  const [rentRollOwnership, setRentRollOwnership] = useState('all');
  const [rentRollCurrency, setRentRollCurrency] = useState('all');
  const [rentRollMonth, setRentRollMonth] = useState(new Date().toISOString().substring(0,7));
  const [hcFromMonth, setHcFromMonth] = useState(() => {
    const d = new Date(); d.setMonth(d.getMonth() - 3);
    return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`;
  });
  const [hcToMonth, setHcToMonth] = useState(() => {
    const d = new Date(); d.setMonth(d.getMonth() - 1);
    return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`;
  });
  const [hcNarrative, setHcNarrative] = useState('');
  const [hcNarrativeLoading, setHcNarrativeLoading] = useState(false);
  const [hcNarrativeError, setHcNarrativeError] = useState('');
  const [hcIgnores, setHcIgnores] = useState([]);
  const [hcIgnoresLoaded, setHcIgnoresLoaded] = useState(false);
  const [hcShowIgnored, setHcShowIgnored] = useState(false);

  useEffect(() => {
    if (activeReport !== 'healthcheck' || hcIgnoresLoaded) return;
    const BASE  = API_URL;
    const token = localStorage.getItem('token');
    fetch(`${BASE}/api/reports/health-check-ignores`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(data => { setHcIgnores(Array.isArray(data) ? data : []); setHcIgnoresLoaded(true); })
      .catch(() => setHcIgnoresLoaded(true));
  }, [activeReport, hcIgnoresLoaded]);
  const [arrearsOwnership, setArrearsOwnership] = useState('all');
  const [arrearsCurrency, setArrearsCurrency] = useState('all');
  const [voidOwnership, setVoidOwnership] = useState('all');
  const [voidCurrency, setVoidCurrency] = useState('all');
  const [expCatYear, setExpCatYear] = useState(new Date().getFullYear().toString());
  const [expCatOwnership, setExpCatOwnership] = useState('all');
  const [expCatCurrency, setExpCatCurrency] = useState('all');
  const [yieldOwnership, setYieldOwnership] = useState('all');
  const [yieldCurrency, setYieldCurrency] = useState('all');

  const years = [];
  for (let y = 2022; y <= new Date().getFullYear() + 1; y++) years.push(y.toString());
  const taxYears = [];
  for (let i = 0; i < 5; i++) { const s = new Date().getFullYear() - 2 + i; taxYears.push(`${s}-${s+1}`); }

  // COMPANY-007: 'all' | 'personal' | '<companyId>'
  const [companyScope, setCompanyScope] = useState('all');
  const activeCompanies = (companies || []).filter(c => !(c.archived === true || c.archived === 'true'));
  const isMultiCompany = activeCompanies.length > 1;

  // The Company Property Report has its own company selector (COMPANY-002) and
  // filters by it internally. Applying the global scope there too would
  // double-filter -- pick Company A globally and Company B in the report and
  // you'd get an empty report with no explanation. Exempt it.
  // UX-003: the MTD page has its own fixed rule (UK, personally owned only), so it ignores the company scope too.
  const effectiveScope = (activeReport === 'company' || activeReport === 'mtd') ? 'all' : companyScope;

  const properties = useMemo(() => {
    if (!isMultiCompany || effectiveScope === 'all') return allProperties;
    if (effectiveScope === 'personal') {
      // Joint belongs here: joint ownership is two or more individuals, so S24
      // applies exactly as for sole personal ownership.
      return allProperties.filter(p => String(p.ownership||'').toLowerCase() !== 'company');
    }
    return allProperties.filter(p =>
      String(p.ownership||'').toLowerCase() === 'company' &&
      String(p.companyId || p.company_id || '') === String(effectiveScope)
    );
  }, [allProperties, effectiveScope, isMultiCompany]);

  const companyExpenses = useMemo(() => {
    if (!isMultiCompany || effectiveScope === 'all') return allCompanyExpenses;
    // Company overheads are not personal expenses -- scoping to Personal
    // excludes them entirely rather than lumping them in.
    if (effectiveScope === 'personal') return [];
    return (allCompanyExpenses || []).filter(e =>
      String(e.company_id || e.companyId || '') === String(effectiveScope)
    );
  }, [allCompanyExpenses, effectiveScope, isMultiCompany]);

  // COMPANY-007: one control, rendered on the report index and beside the back
  // button inside every report, so the scope is always visible. Declared here,
  // above every return in this component -- a `const` is not hoisted, so
  // declaring it lower down (next to BackBtn) made the index render throw
  // "Cannot access before initialization" and blank the screen.
  // Hidden entirely unless there are two or more companies, so a single-company
  // org sees no change at all.
  const CompanyScopeSelect = ({ compact = false }) => {
    if (!isMultiCompany) return null;
    if (activeReport === 'company') return null; // has its own selector
    if (activeReport === 'mtd') return null;     // UX-003: MTD is always UK personally owned only
    return (
      <div style={{ display:'flex', alignItems:'center', gap:'0.5rem', flexWrap:'wrap' }}>
        <label style={{ fontSize:'0.8rem', fontWeight:600, color:'var(--text-secondary)' }}>Company scope</label>
        <select
          value={companyScope}
          onChange={e => setCompanyScope(e.target.value)}
          style={{ padding: compact ? '0.35rem' : '0.5rem', borderRadius:'0.375rem', border:'1px solid var(--border)', fontSize:'0.85rem' }}
        >
          <option value="all">All companies &amp; personal</option>
          <option value="personal">Personal &amp; joint only</option>
          {activeCompanies.map(c => <option key={c.id} value={String(c.id)}>{c.name}</option>)}
        </select>
      </div>
    );
  };

  const activeProps = properties.filter(p => !p.archived);

  // BUG FIX: found during a sweep after removing the property wizard's
  // room/tenant capture step. Every rent-derived figure across this file
  // — Gross Yield, the Yield report, Rent Roll's Rent/mo column, the
  // Company Property Report — summed room.rentAmount directly. That's
  // correct for HMO, but a BTL/Single Let property now has zero rooms by
  // design (rooms are an HMO-only concept), so every one of those figures
  // was silently computing £0 / 0% for every non-HMO property. Monthly
  // rent for a non-HMO property now lives on the active tenant's own
  // rent_amount (set via the Tenant module), matching what the Arrears
  // Report already did correctly. One shared helper, used everywhere this
  // file needs "how much rent does this property bring in," rather than
  // reimplementing the same room-vs-tenant branch at each call site.
  const getPropertyMonthlyRent = (prop, roomsList) => {
    const isPropHMO = String(prop.property_type || prop.propertyType || 'hmo').toLowerCase() === 'hmo';
    if (isPropHMO) {
      return (roomsList || prop.rooms || []).filter(r => r.status === 'occupied').reduce((s,r) => s + parseFloat(r.rentAmount || 0), 0);
    }
    return (tenants || []).filter(t => String(t.property_id || t.propertyId) === String(prop.id) && t.status === 'active')
      .reduce((s,t) => s + parseFloat(t.rent_amount || 0), 0);
  };

  // BUG FIX: tenant date fields (lease_start, lease_end, move_out_date)
  // arrive from the API as full ISO timestamps whenever Postgres returns a
  // JS Date object for what is actually a DATE column — GET /api/tenants
  // uses a plain res.json(rows), which serialises a Date via .toJSON(),
  // producing "2026-08-28T00:00:00.000Z" rather than "2026-08-28". Every
  // earlier fix today worked despite this because .substring(0,7) or
  // .substring(0,10) truncation naturally extracts the correct leading
  // characters regardless of what follows — this is the first place a raw,
  // untruncated value is shown directly, which is why it surfaced here.
  const toShortDate = (raw) => {
    if (!raw) return '';
    const d = String(raw).substring(0,10); // "YYYY-MM-DD" regardless of a trailing time component
    const [y,m,day] = d.split('-');
    if (!y || !m || !day) return '';
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return `${day}-${months[parseInt(m,10)-1]}-${y.slice(-2)}`;
  };

  // Derive available currencies from active properties
  const availableCurrencies = useMemo(() => {
    return sortCurrencies(activeProps.map(p => p.currency || 'GBP'));
  }, [activeProps]);

  // BUG-005c: the Monthly Profit screen and its Excel both come from monthlyByCurrency (one block per currency)
  const monthlyGroups = useMemo(
    () => monthlyByCurrency({ properties, payments, expenses, companyExpenses, companies, year: monthlyYear, ownership: monthlyOwnership, currency: monthlyCurrency, monthStyle: 'long' }),
    [properties, payments, expenses, companyExpenses, companies, monthlyYear, monthlyOwnership, monthlyCurrency]);

  const capitalData = useMemo(() => {
    return activeProps.map(prop => {
      const cd = prop.capitalData || prop.capital_data || {};
      const purchasePrice = parseFloat(cd.purchasePrice || 0);
      const currentValue = parseFloat(cd.currentValue || 0);
      const mortgageBalance = parseFloat(cd.mortgageBalance || 0);
      const equity = currentValue - mortgageBalance;
      const ltv = currentValue > 0 ? (mortgageBalance / currentValue * 100) : 0;
      const capitalGrowth = currentValue - purchasePrice;
      const capitalGrowthPct = purchasePrice > 0 ? (capitalGrowth / purchasePrice * 100) : 0;
      const rooms = prop.rooms || [];
      const annualRent = getPropertyMonthlyRent(prop, rooms) * 12;
      const grossYield = purchasePrice > 0 ? (annualRent / purchasePrice * 100) : 0;
      return { ...prop, purchasePrice, currentValue, mortgageBalance, equity, ltv, capitalGrowth, capitalGrowthPct, grossYield, annualRent, mortgageProvider: cd.mortgageProvider, mortgageRate: cd.mortgageRate, rateExpiry: cd.rateExpiryDate };
    }).filter(p => p.purchasePrice > 0 || p.currentValue > 0);
  }, [activeProps]);

  const filteredCapitalData = useMemo(() => {
    return capitalData
      .filter(p => ownershipMatches(p, capitalOwnership))
      .filter(p => capitalCurrency === 'all' || (p.currency || 'GBP') === capitalCurrency);
  }, [capitalData, capitalOwnership, capitalCurrency]);

  // FEAT-006: capital costs per property (purchase price + purchase costs + capital improvements + sale costs)
  const capitalCostRows = useMemo(() => capitalCostsSummary(
    activeProps
      .filter(p => ownershipMatches(p, capitalOwnership))
      .filter(p => capitalCurrency === 'all' || (p.currency || 'GBP') === capitalCurrency)
      .map(p => ({ ...p, purchasePrice: parseFloat((p.capitalData || p.capital_data || {}).purchasePrice || 0) })),
    expenses), [activeProps, expenses, capitalOwnership, capitalCurrency]);

  // Company tax year report data — uses company tax year from settings (default Oct-Sep)
  // CT600-001: Computes a company's accounting year as an actual date range,
  // given its own tax_year_start ('MM-DD'). Generalizes the old hardcoded
  // Oct 1 – Sep 30 logic to work for any company's real year end — e.g. a
  // calendar-year company (01-01) correctly gets Jan–Dec of the SAME year,
  // while an Oct-start company gets Oct (yr-1) – Sep (yr), etc.
  const getCompanyYearRange = (taxYearStart, yr) => {
    const [m, d] = (taxYearStart || '10-01').split('-').map(Number);
    if (m === 1 && d === 1) {
      return { start: new Date(yr, 0, 1), end: new Date(yr, 11, 31) };
    }
    return { start: new Date(yr - 1, m - 1, d), end: new Date(yr, m - 1, d - 1) };
  };

  const companyReportData = useMemo(() => {
    const yr = parseInt(companyReportYear);
    const selectedCompany = companies.find(c => String(c.id) === selectedCompanyId);
    const taxYearStart = selectedCompany?.tax_year_start || '10-01';
    const { start, end } = getCompanyYearRange(taxYearStart, yr);
    const inRange = (d) => { const dt = new Date(d); return dt >= start && dt <= end; };
    const compProps = activeProps
      // BUG FIX: same casing mismatch — this filter is why the standalone
      // Company Property Report was showing completely empty for every user
      // with company-owned properties, not just miscalculating figures.
      .filter(p => String(p.ownership||'').toLowerCase() === 'company')
      .filter(p => selectedCompanyId === 'unassigned' ? !p.companyId : String(p.companyId) === selectedCompanyId);
    return compProps.map(prop => {
      const propIncome = payments.filter(p => {
        if ((p.propertyId||p.property_id) != prop.id) return false;
        if (!p.month) return false;
        const [y, m] = p.month.split('-').map(Number);
        const dt = new Date(y, m-1, 15);
        return dt >= start && dt <= end;
      }).reduce((s,p) => s+parseFloat(p.amount||0), 0);
      const propExp = expenses.filter(e =>
        (e.propertyId||e.property_id)==prop.id && (e.paid===true||e.paid==='true') && e.date && inRange(e.date)
      ).reduce((s,e) => s+parseFloat(e.amount||0), 0);
      const rooms = prop.rooms || [];
      const occupiedRooms = rooms.filter(r => r.status === 'occupied');
      return { ...prop, income: propIncome, expenses: propExp, profit: propIncome - propExp, rooms, occupiedRooms, yearStart: start, yearEnd: end };
    });
  }, [activeProps, payments, expenses, companyReportYear, companies, selectedCompanyId]);

  // TAX-001: Get tax treatment for a property
  const getTaxTreatment = (prop) => {
    // BUG FIX: properties.ownership is stored capitalised — 'Company',
    // 'Personal', 'Joint' (see the OWNERSHIP constant in Properties.js) —
    // but this compared against lowercase 'company', which can never match.
    // Every company-owned property was silently falling through to the
    // jurisdiction-based S24 branch below: mortgage interest wrongly
    // excluded and a 20% credit shown, instead of being fully deductible.
    // Understates a company's real deductible costs and overstates its
    // taxable profit — the exact wrong direction. Same pattern already
    // fixed correctly elsewhere in this file (see the COMPANY-007 scope
    // filter above) — never brought in line with it here until now.
    const ownership   = String(prop.ownership || 'personal').toLowerCase();
    // BUG-005a: effectiveJurisdiction treats a euro property as Irish whatever jurisdiction was saved
    const jurisdiction = effectiveJurisdiction(prop);
    if (ownership === 'company') {
      return { type: 'fully_deductible', label: 'Company — fully deductible', shortLabel: 'Company', color: '#1e40af' };
    }
    switch (jurisdiction) {
      case 'ireland':
        return { type: 'fully_deductible', label: 'Ireland — fully deductible (subject to RTB)', shortLabel: 'IE Personal', color: '#166534' };
      case 'scotland':
        return { type: 's24', label: 'Scotland — S24 restricted (20% tax credit)', shortLabel: 'Scotland', color: '#7c3aed', note: 'Placeholder — Scottish rules may differ in future' };
      case 'wales':
        return { type: 's24', label: 'Wales — S24 restricted (20% tax credit)', shortLabel: 'Wales', color: '#b45309', note: 'Placeholder — Welsh rules may differ in future' };
      case 'northern_ireland':
        return { type: 's24', label: 'N. Ireland — S24 restricted (20% tax credit)', shortLabel: 'N.Ireland', color: '#0369a1', note: 'Placeholder — NI rules may differ in future' };
      case 'england':
      default:
        return { type: 's24', label: 'England — S24 restricted (20% tax credit only)', shortLabel: 'England', color: '#dc2626' };
    }
  };

  const taxReportData = useMemo(() => {
    const [startYear, endYear] = taxYear.split('-').map(Number);
    // UK tax year: 6 April to 5 April
    const ukStart  = new Date(startYear, 3, 6);
    const ukEnd    = new Date(endYear, 3, 5);
    // BUG FIX: Irish tax year is the calendar year (1 Jan – 31 Dec), not the
    // UK's April–April period. This previously borrowed the UK date range
    // for Irish properties too — not just a mislabelled disclaimer, but an
    // actual data error, since payments/expenses falling in Jan–Mar or
    // Apr–Dec (depending on the year) were being included/excluded
    // incorrectly. Now computed per-property based on its own jurisdiction.
    // Uses startYear as the reference Irish calendar tax year, matching the
    // existing "ieYear = sy" convention already used for display.
    const ieStart  = new Date(startYear, 0, 1);
    const ieEnd    = new Date(startYear, 11, 31);
    const filteredProps = activeProps
      // BUG FIX: same casing mismatch as getTaxTreatment — p.ownership is
      // stored capitalised, taxOwnership's dropdown values are lowercase.
      // Selecting "Personal Only" or "Company Only" previously matched
      // NOTHING, returning a silently empty report rather than an error.
      .filter(p => ownershipMatches(p, taxOwnership))
      .filter(p => taxCurrency === 'all' || (p.currency || 'GBP') === taxCurrency)
      .filter(p => taxJurisdiction === 'all' || effectiveJurisdiction(p) === taxJurisdiction);
    return filteredProps.map(prop => {
      const isIrish = isIrishProperty(prop);
      const start = isIrish ? ieStart : ukStart;
      const end   = isIrish ? ieEnd   : ukEnd;
      const inRange  = (d) => { const dt = new Date(d); return dt >= start && dt <= end; };
      const propPayments = payments.filter(p => {
        if ((p.propertyId||p.property_id) != prop.id) return false;
        if (!p.month) return false;
        const [y, m] = p.month.split('-').map(Number);
        const dt = new Date(y, m-1, 15);
        return dt >= start && dt <= end;
      }).reduce((s,p) => s+parseFloat(p.amount||0), 0);

      const propExpensesAll = expenses.filter(e =>
        (e.propertyId||e.property_id)==prop.id &&
        (e.paid===true||e.paid==='true') &&
        e.date && inRange(e.date)
      );

      // Split mortgage interest from other allowable expenses
      // FEAT-006: capital costs are not allowable; splitTaxExpenses leaves them out of both figures
      const { financeCosts: mortgageInterest, allowable: allowableExpenses } = splitTaxExpenses(propExpensesAll);
      const treatment = getTaxTreatment(prop);

      let taxableProfit, s24Credit, totalExpenses;
      if (treatment.type === 'fully_deductible') {
        totalExpenses = allowableExpenses + mortgageInterest;
        taxableProfit = propPayments - totalExpenses;
        s24Credit     = 0;
      } else {
        // S24 — mortgage interest NOT deductible, replaced by 20% basic rate tax credit
        totalExpenses = allowableExpenses;
        taxableProfit = propPayments - allowableExpenses;
        s24Credit     = mortgageInterest * 0.20;
      }

      // FEAT-002: joint properties keep their full 100% figures above, exactly like any other property. casavello
      // never reduces them: you enter the 100% figures and your ownership share in your filing software, which
      // calculates your share. The "your share" values below are for INFORMATION only, to the penny, and are null
      // while the ownership share is not set (a share is only meaningful for a joint property).
      const isJoint = isJointProperty(prop);
      const sharePercent = isJoint ? propertyShare(prop) : null;
      const yourTaxableProfit    = isJoint ? shareOf(taxableProfit, sharePercent)    : taxableProfit;
      const yourS24Credit        = isJoint ? shareOf(s24Credit, sharePercent)        : s24Credit;
      const yourMortgageInterest = isJoint ? shareOf(mortgageInterest, sharePercent) : mortgageInterest;

      // TAX-001 / Ireland RTB: interest deductibility depends on the
      // tenancy being RTB-registered. Kept deliberately simple per Sam's
      // decision — one flag per property per period, not apportioned by
      // day: if ANY tenancy covering this property during the period was
      // not RTB-registered, flag the whole period rather than attempting a
      // partial-registration calculation.
      // RTB conditionality applies to individual (personal/joint) Irish
      // landlords specifically — not to a company, whose Irish deductibility
      // question is the separate 25%/close-company-surcharge matter covered
      // in the design doc, not RTB registration.
      const isIrishPersonal = String(prop.ownership || '').toLowerCase() !== 'company' && isIrishProperty(prop);
      let rtbFlag = null;
      if (isIrishPersonal) {
        const propertyTenants = (tenants || []).filter(t => String(t.propertyId || t.property_id) === String(prop.id));
        const anyUnregistered = propertyTenants.some(t => !t.rtb_registered && !t.rtbRegistered);
        if (propertyTenants.length === 0) {
          rtbFlag = 'no-tenancy-on-record';
        } else if (anyUnregistered) {
          rtbFlag = 'unregistered';
        } else {
          rtbFlag = 'registered';
        }
      }

      return {
        ...prop, income: propPayments, mortgageInterest, allowableExpenses, totalExpenses, taxableProfit, s24Credit, treatment,
        expenses: totalExpenses, profit: taxableProfit,
        isJoint, sharePercent, yourTaxableProfit, yourS24Credit, yourMortgageInterest,
        rtbFlag,
      };
    });
  }, [activeProps, payments, expenses, taxYear, taxOwnership, taxCurrency, taxJurisdiction, tenants]);

  // UX-003: the filtering and the sums live in utils/mtdScope.js (tested); the page always uses
  // UK, personally owned, non-archived properties (no company, joint or Irish).
  const mtdReportData = useMemo(
    () => mtdQuarterData(properties, payments, expenses, mtdTaxYear, mtdQuarter),
    [properties, payments, expenses, mtdTaxYear, mtdQuarter]);
  // FEAT-002: jointly owned UK properties, at 100%, listed separately and never added into the personal figures
  const mtdJointData = useMemo(
    () => mtdJointQuarterData(properties, payments, expenses, mtdTaxYear, mtdQuarter),
    [properties, payments, expenses, mtdTaxYear, mtdQuarter]);

  // ─── Excel export helpers ────────────────────────────────────────────────
  const xlsDate = () => new Date().toISOString().split('T')[0];

  // Apply column widths to a sheet
  const setCols = (ws, widths) => { ws['!cols'] = widths.map(w => ({ wch: w })); };

  // Style helpers — SheetJS CE doesn't support styles, but we can use aoa_to_sheet
  // with deliberate blank rows for visual grouping
  const addSheet = (wb, name, rows, colWidths) => {
    const ws = XLSX.utils.aoa_to_sheet(rows);
    if (colWidths) setCols(ws, colWidths);
    XLSX.utils.book_append_sheet(wb, ws, name);
  };

  // Format a number to 2dp string for cells
  const fmt = (v) => parseFloat(v||0).toFixed(2);
  const fmtPct = (v) => parseFloat(v||0).toFixed(1) + '%';

  // ── PORTFOLIO-WIDE EXPORT (from index page) ───────────────────────────────
  // ── EXPORT-001: date-range export ─────────────────────────────────
  const EXPORT_RANGES = [
    { key: 'lastmonth', label: 'Last full month', desc: 'The most recently completed calendar month' },
    { key: '3months',   label: 'Last 3 months',   desc: 'Rolling three months up to today' },
    { key: '12months',  label: 'Last 12 months',  desc: 'Rolling twelve months up to today' },
    { key: 'all',       label: 'All data',        desc: 'Everything recorded since you started' },
  ];

  const getRangeBounds = (range) => {
    const now = new Date();
    if (range === 'lastmonth') return { start: new Date(now.getFullYear(), now.getMonth()-1, 1), end: new Date(now.getFullYear(), now.getMonth(), 0, 23,59,59) };
    if (range === '3months')   { const s = new Date(now); s.setMonth(s.getMonth()-3);  return { start: s, end: now }; }
    if (range === '12months')  { const s = new Date(now); s.setMonth(s.getMonth()-12); return { start: s, end: now }; }
    return { start: null, end: null };
  };

  const inRange = (dateVal, bounds) => {
    if (!bounds.start) return true;
    if (!dateVal) return false;
    const d = new Date(dateVal);
    return d >= bounds.start && d <= bounds.end;
  };

  const rangeLabel = (range) => (EXPORT_RANGES.find(r => r.key === range) || {}).label || 'All data';

  // Applies the selected date range, then builds the workbook.
  // The filtered arrays are passed in as parameters so they shadow the
  // full datasets inside the builder without affecting the rest of the page.
  const handleExportExcel = async () => {
    const b = getRangeBounds(exportRange);
    return buildExportWorkbook(
      (payments        || []).filter(p => inRange(p.date, b)),
      (expenses        || []).filter(e => inRange(e.date, b)),
      (companyExpenses || []).filter(e => inRange(e.date, b)),
      rangeLabel(exportRange)
    );
  };

  const buildExportWorkbook = async (payments, expenses, companyExpenses, rangeName) => {
    const wb = XLSX.utils.book_new();
    const today = xlsDate();

    // Sheet 1: Portfolio Summary
    const propRows = [
      ['HMO PORTFOLIO SUMMARY', '', '', '', '', '', '', ''],
      [`Generated: ${today}`, '', '', '', '', '', '', ''],
      [],
      ['Address', 'Type', 'Ownership', 'Currency', 'Rooms', 'Occupied', 'Vacant', 'Monthly Rent'],
      ...activeProps.map(p => {
        const rooms = p.rooms || [];
        const occ = rooms.filter(r => r.status === 'occupied');
        const rent = getPropertyMonthlyRent(p, rooms);
        return [p.address, p.property_type||p.propertyType||'', p.ownership||'', p.currency||'GBP', rooms.length, occ.length, rooms.length-occ.length, fmt(rent)];
      }),
      [],
      ['TOTALS', '', '', '', activeProps.reduce((s,p)=>s+(p.rooms||[]).length,0), activeProps.reduce((s,p)=>s+(p.rooms||[]).filter(r=>r.status==='occupied').length,0), '', fmt(activeProps.reduce((s,p)=>s+getPropertyMonthlyRent(p, p.rooms||[]),0))],
    ];
    addSheet(wb, 'Portfolio Summary', propRows, [40,15,12,10,8,10,8,14]);

    // Sheet 2: All Rental Income (detailed)
    const incRows = [
      ['RENTAL INCOME — ALL PROPERTIES', '', '', '', '', ''],
      [`Generated: ${today}`, '', '', '', '', ''],
      [],
      ['Property', 'Ownership', 'Currency', 'Room / Tenant', 'Month', 'Amount', 'Paid Date'],
      ...payments
        .sort((a,b) => (b.month||'').localeCompare(a.month||''))
        .map(p => {
          const prop = properties.find(pr => pr.id == (p.propertyId||p.property_id));
          const room = (prop?.rooms||[]).find(r => String(r.id) === String(p.roomId||p.room_id));
          return [
            prop?.address||'Unknown',
            prop?.ownership||'',
            prop?.currency||'GBP',
            room ? `${room.roomName}${room.tenantName?' – '+room.tenantName:''}` : '',
            p.month||'',
            fmt(p.amount),
            p.paidDate||p.paid_date||''
          ];
        }),
      [],
      ['TOTAL', '', '', '', '', fmt(payments.reduce((s,p)=>s+parseFloat(p.amount||0),0)), ''],
    ];
    addSheet(wb, 'All Income', incRows, [40,12,10,25,12,12,14]);

    // Sheet 3: Income by Property (monthly breakdown)
    const incByPropRows = [
      ['INCOME BY PROPERTY — MONTHLY BREAKDOWN', '', '', '', '', '', '', '', '', '', '', '', '', ''],
      [`Generated: ${today}`, '', '', '', '', '', '', '', '', '', '', '', '', ''],
      [],
    ];
    const months = [];
    for (let m = 1; m <= 12; m++) months.push(String(m).padStart(2,'0'));
    const curYear = new Date().getFullYear();
    const prevYear = curYear - 1;
    [prevYear, curYear].forEach(yr => {
      incByPropRows.push([`Year ${yr}`, '', '', '', '', '', '', '', '', '', '', '', '', '']);
      incByPropRows.push(['Property', 'Ownership', 'Currency', ...months.map(m => new Date(yr, parseInt(m)-1, 1).toLocaleDateString('en-GB',{month:'short'})+' '+yr), 'TOTAL']);
      activeProps.forEach(prop => {
        const row = [prop.address, prop.ownership||'', prop.currency||'GBP'];
        let total = 0;
        months.forEach(m => {
          const monthStr = `${yr}-${m}`;
          const amt = payments.filter(p => (p.propertyId||p.property_id)==prop.id && p.month===monthStr).reduce((s,p)=>s+parseFloat(p.amount||0),0);
          row.push(amt > 0 ? fmt(amt) : '');
          total += amt;
        });
        row.push(fmt(total));
        incByPropRows.push(row);
      });
      // Year totals row
      const totRow = ['TOTAL', '', ''];
      let grandTotal = 0;
      months.forEach(m => {
        const monthStr = `${yr}-${m}`;
        const amt = payments.filter(p => activeProps.some(pr=>pr.id==(p.propertyId||p.property_id)) && p.month===monthStr).reduce((s,p)=>s+parseFloat(p.amount||0),0);
        totRow.push(amt > 0 ? fmt(amt) : '');
        grandTotal += amt;
      });
      totRow.push(fmt(grandTotal));
      incByPropRows.push(totRow);
      incByPropRows.push([]);
    });
    addSheet(wb, 'Income by Property', incByPropRows, [40,12,10,...Array(12).fill(10),'TOTAL',14]);

    // Sheet 4: All Property Expenses (detailed)
    const expRows = [
      ['PROPERTY EXPENSES — DETAILED', '', '', '', '', '', ''],
      [`Generated: ${today}`, '', '', '', '', '', ''],
      [],
      ['Property', 'Ownership', 'Currency', 'Category', 'Date', 'Amount', 'Paid', 'Description'],
      ...expenses
        .sort((a,b) => (b.date||'').localeCompare(a.date||''))
        .map(e => {
          const prop = properties.find(p => p.id == (e.propertyId||e.property_id));
          return [prop?.address||'Unknown', prop?.ownership||'', prop?.currency||'GBP', e.category||'', e.date||'', fmt(e.amount), (e.paid===true||e.paid==='true')?'Yes':'No', e.description||''];
        }),
      [],
      ['TOTAL', '', '', '', '', fmt(expenses.reduce((s,e)=>s+parseFloat(e.amount||0),0)), '', ''],
      ['TOTAL PAID', '', '', '', '', fmt(expenses.filter(e=>e.paid===true||e.paid==='true').reduce((s,e)=>s+parseFloat(e.amount||0),0)), '', ''],
    ];
    addSheet(wb, 'Property Expenses', expRows, [40,12,10,18,12,12,8,30]);

    // Sheet 5: Expenses by Category
    const catMap = {};
    expenses.forEach(e => {
      const cat = e.category || 'Other';
      if (!catMap[cat]) catMap[cat] = { total: 0, paid: 0, count: 0 };
      catMap[cat].total += parseFloat(e.amount||0);
      if (e.paid===true||e.paid==='true') catMap[cat].paid += parseFloat(e.amount||0);
      catMap[cat].count++;
    });
    const catRows = [
      ['EXPENSES BY CATEGORY', '', '', ''],
      [`Generated: ${today}`, '', '', ''],
      [],
      ['Category', 'Transactions', 'Total Amount', 'Paid Amount', 'Outstanding'],
      ...Object.entries(catMap).sort((a,b) => b[1].total-a[1].total).map(([cat,d]) => [cat, d.count, fmt(d.total), fmt(d.paid), fmt(d.total-d.paid)]),
      [],
      ['TOTAL', expenses.length, fmt(expenses.reduce((s,e)=>s+parseFloat(e.amount||0),0)), fmt(expenses.filter(e=>e.paid===true||e.paid==='true').reduce((s,e)=>s+parseFloat(e.amount||0),0)), ''],
    ];
    addSheet(wb, 'Expenses by Category', catRows, [25,14,16,16,16]);

    // Sheet 6: Company Expenses
    const corpRows = [
      ['COMPANY EXPENSES — DETAILED', '', '', '', ''],
      [`Generated: ${today}`, '', '', '', ''],
      [],
      ['Category', 'Date', 'Amount', 'Paid', 'Description'],
      ...companyExpenses
        .sort((a,b) => (b.date||'').localeCompare(a.date||''))
        .map(e => [e.category||'', e.date||'', fmt(e.amount), (e.paid===true||e.paid==='true')?'Yes':'No', e.description||'']),
      [],
      ['TOTAL', '', fmt(companyExpenses.reduce((s,e)=>s+parseFloat(e.amount||0),0)), '', ''],
      ['TOTAL PAID', '', fmt(companyExpenses.filter(e=>e.paid===true||e.paid==='true').reduce((s,e)=>s+parseFloat(e.amount||0),0)), '', ''],
    ];
    addSheet(wb, 'Company Expenses', corpRows, [25,12,14,8,40]);

    // Sheet 7: Capital & Mortgage
    const capRows = [
      ['PORTFOLIO CAPITAL & MORTGAGE', '', '', '', '', '', '', '', ''],
      [`Generated: ${today}`, '', '', '', '', '', '', '', ''],
      [],
      ['Property', 'Ownership', 'Currency', 'Purchase Price', 'Current Value', 'Capital Growth', 'Growth %', 'Mortgage Balance', 'Equity', 'LTV %', 'Gross Yield %', 'Provider', 'Rate %', 'Rate Expiry'],
      ...activeProps.map(prop => {
        const cd = prop.capitalData||prop.capital_data||{};
        const purchase = parseFloat(cd.purchasePrice||0);
        const value = parseFloat(cd.currentValue||0);
        const mort = parseFloat(cd.mortgageBalance||0);
        const rooms = prop.rooms||[];
        const annualRent = getPropertyMonthlyRent(prop, rooms)*12;
        const equity = value - mort;
        const ltv = value > 0 ? (mort/value*100) : 0;
        const growth = value - purchase;
        const growthPct = purchase > 0 ? (growth/purchase*100) : 0;
        const yield_ = purchase > 0 ? (annualRent/purchase*100) : 0;
        return [prop.address, prop.ownership||'', prop.currency||'GBP', purchase>0?fmt(purchase):'', value>0?fmt(value):'', growth!==0?fmt(growth):'', growthPct!==0?fmtPct(growthPct):'', mort>0?fmt(mort):'', value>0?fmt(equity):'', ltv>0?fmtPct(ltv):'', yield_>0?fmtPct(yield_):'', cd.mortgageProvider||'', cd.mortgageRate||'', cd.rateExpiryDate||''];
      }),
    ];
    addSheet(wb, 'Capital & Mortgage', capRows, [40,12,10,16,14,14,10,16,14,8,12,20,8,14]);

    // Sheet 8: Tenants
    const tenantRows = [
      ['TENANT REGISTER', '', '', '', '', '', '', '', '', '', ''],
      [`Generated: ${today}`, '', '', '', '', '', '', '', '', '', ''],
      [],
      ['Property', 'Type', 'Tenant Name', 'Email', 'Phone', 'Room', 'Tenancy Type', 'Lease Start', 'Lease End', 'Rent/Month', 'Status', 'RTB Registered', 'Deposit Amount'],
      ...(tenants || []).map(t => {
        const prop = properties.find(p => p.id == t.property_id);
        const room = prop?.rooms?.find(r => String(r.id) === String(t.room_id));
        const sym  = t.currency === 'EUR' ? '€' : '£';
        return [
          t.property_address || prop?.address || '',
          prop?.property_type || '',
          `${t.first_name||''} ${t.last_name||''}`.trim(),
          t.email || '',
          t.phone || '',
          room?.roomName || (t.room_id ? t.room_id : 'N/A'),
          t.tenancy_type || '',
          t.lease_start ? t.lease_start.substring(0,10) : '',
          t.lease_end ? t.lease_end.substring(0,10) : 'Periodic',
          t.rent_amount ? `${sym}${parseFloat(t.rent_amount).toFixed(2)}` : '',
          t.status || '',
          t.rtb_registered ? 'Yes' : 'No',
          t.deposit_amount ? `${sym}${parseFloat(t.deposit_amount).toFixed(2)}` : '',
        ];
      }),
      [],
      ['TOTAL ACTIVE', '', `${(tenants||[]).filter(t=>t.status==='active').length} tenants`, '', '', '', '', '', '', '', '', '', ''],
    ];
    addSheet(wb, 'Tenants', tenantRows, [40,12,25,30,16,16,20,14,14,14,10,14,14]);

    // Sheet 9: Service Requests
    const srData = await serviceRequestsAPI.getAll().catch(() => []);
    const srRows = [
      ['SERVICE REQUESTS', '', '', '', '', '', '', '', ''],
      [`Generated: ${today}`, '', '', '', '', '', '', '', ''],
      [],
      ['Property', 'Title', 'Category', 'Priority', 'Status', 'Contractor', 'Reported Date', 'Completed Date', 'Quote Amount', 'Actual Cost'],
      ...srData.map(r => [
        r.property_address || '',
        r.title || '',
        r.category || '',
        r.priority || '',
        r.status || '',
        r.contractor_name || '',
        r.reported_date ? r.reported_date.substring(0,10) : '',
        r.completed_date ? r.completed_date.substring(0,10) : '',
        r.quote_amount ? formatMoney(r.quote_amount, r.currency) : '',
        r.actual_cost ? formatMoney(r.actual_cost, r.currency) : '',
      ]),
      [],
      // BUG-005e: each currency totalled on its own, with its own symbol (never added together)
      ...sortCurrencies(srData.filter(r=>r.actual_cost).map(r=>r.currency)).map(cur => ['TOTAL COST (' + cur + ')', '', '', '', '', '', '', '', '',
        formatMoney(srData.filter(r=>r.actual_cost && normCurrency(r.currency)===cur).reduce((s,r)=>s+parseFloat(r.actual_cost||0),0), cur)]),
    ];
    addSheet(wb, 'Service Requests', srRows, [40,30,18,12,16,25,14,14,14,14]);

    const rangeSlug = (rangeName || 'All data').replace(/\s+/g, '-');
    XLSX.writeFile(wb, `casavello-Export-${rangeSlug}-${today}.xlsx`);
  };

  // ── PER-REPORT EXCEL EXPORTS ──────────────────────────────────────────────
  const exportPerformanceExcel = (groups, year, ownership, currency) => {
    const wb = XLSX.utils.book_new();
    const today = xlsDate();
    // BUG-005c: the same per-currency groups the screen shows, and detail sheets that follow the same filters
    addSheet(wb, 'Monthly Summary', monthlySummaryRows({ title: 'PERFORMANCE REPORT', year, today, groups }), [10,16,14,18,18,14,10]);
    const d = yearDetail({ properties, payments, expenses, companyExpenses, companies, year, ownership, currency });
    const propOf = (id) => properties.find(pr => pr.id == id);

    // Income detail
    const incDetailRows = [
      ['INCOME DETAIL BY PROPERTY', year, '', '', '', ''],
      [],
      ['Month', 'Property', 'Ownership', 'Currency', 'Room / Tenant', 'Amount'],
      ...[...d.payments]
        .sort((a,b)=>(b.month||'').localeCompare(a.month||''))
        .map(p => {
          const prop = propOf(p.propertyId||p.property_id);
          const room = (prop?.rooms||[]).find(r=>String(r.id)===String(p.roomId||p.room_id));
          return [p.month, prop?.address||'Unknown', prop?.ownership||'', normCurrency(prop?.currency), room?`${room.roomName}${room.tenantName?' – '+room.tenantName:''}` : '', fmt(p.amount)];
        }),
      [],
      ...totalRowsByCurrency(d.payments.map(p => ({ currency: propOf(p.propertyId||p.property_id)?.currency, amount: p.amount })), 6, 5),
    ];
    addSheet(wb, 'Income Detail', incDetailRows, [12,40,12,10,25,12]);

    // Expense detail
    const expDetailRows = [
      ['PROPERTY EXPENSE DETAIL', year, '', '', '', '', '', ''],
      [],
      ['Date', 'Property', 'Ownership', 'Currency', 'Category', 'Amount', 'Paid', 'Description'],
      ...[...d.expenses]
        .sort((a,b)=>(b.date||'').localeCompare(a.date||''))
        .map(e => {
          const prop = propOf(e.propertyId||e.property_id);
          return [e.date, prop?.address||'Unknown', prop?.ownership||'', normCurrency(prop?.currency), e.category||'', fmt(e.amount), (e.paid===true||e.paid==='true')?'Yes':'No', e.description||''];
        }),
      [],
      ...totalRowsByCurrency(d.expenses.filter(e=>e.paid===true||e.paid==='true').map(e => ({ currency: propOf(e.propertyId||e.property_id)?.currency, amount: e.amount })), 8, 5, 'TOTAL PAID'),
    ];
    addSheet(wb, 'Expense Detail', expDetailRows, [12,40,12,10,18,12,8,30]);

    // Company expense detail
    const corpDetailRows = [
      ['COMPANY EXPENSE DETAIL', year, '', '', '', ''],
      [],
      ['Date', 'Category', 'Currency', 'Amount', 'Paid', 'Description'],
      ...[...d.corp]
        .sort((a,b)=>(b.date||'').localeCompare(a.date||''))
        .map(e => [e.date, e.category||'', companyExpenseCurrency(e, companies), fmt(e.amount), (e.paid===true||e.paid==='true')?'Yes':'No', e.description||'']),
      [],
      ...totalRowsByCurrency(d.corp.filter(e=>e.paid===true||e.paid==='true').map(e => ({ currency: companyExpenseCurrency(e, companies), amount: e.amount })), 6, 3, 'TOTAL PAID'),
    ];
    addSheet(wb, 'Company Expenses', corpDetailRows, [12,25,10,12,8,40]);

    XLSX.writeFile(wb, `Performance-Report-${year}.xlsx`);
  };

  const exportUKTaxExcel = (taxYearStr, taxData, corpExpTotal) => {
    const wb = XLSX.utils.book_new();
    const [sy, ey] = taxYearStr.split('-').map(Number);
    const today = xlsDate();
    const fmtGBP = n => isNaN(n) ? '£0.00' : `£${Math.abs(n).toFixed(2)}`;

    // FEAT-002: joint properties are NOT mixed into the personal / company totals; they get their own block at 100%
    const { main: ukMain, joint: ukJointLines } = splitTaxRows(taxData);
    const ukPersonal = ukMain.filter(p => String(p.ownership||'').toLowerCase() !== 'company');
    const ukCompany  = ukMain.filter(p => String(p.ownership||'').toLowerCase() === 'company');

    const summaryRows = [
      [`casavello UK TAX REPORT (HMRC) — TAX YEAR ${sy}/${ey}`, '', '', '', '', '', '', ''],
      [`Tax period: 6 April ${sy} to 5 April ${ey}`, '', '', '', '', '', '', ''],
      [`Generated: ${today}`, '', '', '', '', '', '', ''],
      ['IMPORTANT: For organisational purposes only. Not tax advice. Consult a qualified UK accountant before submitting to HMRC.', '', '', '', '', '', '', ''],
      ['Hibernian Technologies Limited accepts no liability for errors or omissions in these figures.', '', '', '', '', '', '', ''],
      [CAPITAL_COSTS_NOTE, '', '', '', '', '', '', ''],
      [],
      ['KEY ASSUMPTION — S24 FINANCE COST RESTRICTION:', '', '', '', '', '', '', ''],
      ['Mortgage interest and other finance costs CANNOT be deducted from rental income for personal ownership.', '', '', '', '', '', '', ''],
      ['Instead, a 20% basic rate tax credit applies. This applies in England, Scotland, Wales and Northern Ireland.', '', '', '', '', '', '', ''],
      ['Company-owned properties: all finance costs are fully deductible (CT600).', '', '', '', '', '', '', ''],
      [],
      ukPersonal.length ? ['PERSONAL PROPERTIES — S24 APPLIES (SA105)', '', '', '', '', '', '', ''] : null,
      ukPersonal.length ? ['Property', 'Jurisdiction', 'Currency', 'Gross Income', 'Allowable Expenses', 'Mortgage Interest (S24)', 'Taxable Profit', 'S24 Tax Credit (est.)'] : null,
      ...ukPersonal.map(p => [p.address, effectiveJurisdiction(p).replace('_',' ').replace(/\b\w/g,c=>c.toUpperCase()), p.currency||'GBP', fmtGBP(p.income), fmtGBP(p.allowableExpenses), p.mortgageInterest>0?fmtGBP(p.mortgageInterest):'—', fmtGBP(p.taxableProfit), p.s24Credit>0?fmtGBP(p.s24Credit):'—']),
      ukPersonal.length ? ['Personal Total', '', '', fmtGBP(ukPersonal.reduce((s,p)=>s+p.income,0)), fmtGBP(ukPersonal.reduce((s,p)=>s+p.allowableExpenses,0)), fmtGBP(ukPersonal.reduce((s,p)=>s+p.mortgageInterest,0)), fmtGBP(ukPersonal.reduce((s,p)=>s+p.taxableProfit,0)), fmtGBP(ukPersonal.reduce((s,p)=>s+p.s24Credit,0))] : null,
      [],
      ukCompany.length ? ['COMPANY PROPERTIES — FULLY DEDUCTIBLE (CT600)', '', '', '', '', '', '', ''] : null,
      ukCompany.length ? ['Property', 'Jurisdiction', 'Currency', 'Gross Income', 'Allowable Expenses', 'Mortgage Interest', 'Taxable Profit', ''] : null,
      ...ukCompany.map(p => [p.address, effectiveJurisdiction(p).replace('_',' ').replace(/\b\w/g,c=>c.toUpperCase()), p.currency||'GBP', fmtGBP(p.income), fmtGBP(p.allowableExpenses), p.mortgageInterest>0?fmtGBP(p.mortgageInterest):'—', fmtGBP(p.taxableProfit), '']),
      ukCompany.length ? ['Company Overhead Expenses', '', '', '', '', '', fmtGBP(-corpExpTotal), ''] : null,
      ukCompany.length ? ['Company Net (after overheads)', '', '', '', '', '', fmtGBP(ukCompany.reduce((s,p)=>s+p.taxableProfit,0)-corpExpTotal), ''] : null,
      [],
      [ukJointLines.length ? 'COMBINED UK TOTAL (personal + company; excludes jointly owned properties)' : 'COMBINED UK TOTAL', '', '', fmtGBP(ukMain.reduce((s,p)=>s+p.income,0)), fmtGBP(ukMain.reduce((s,p)=>s+p.allowableExpenses,0)), fmtGBP(ukMain.reduce((s,p)=>s+p.mortgageInterest,0)), fmtGBP(ukMain.reduce((s,p)=>s+p.taxableProfit,0)-corpExpTotal), fmtGBP(ukMain.reduce((s,p)=>s+p.s24Credit,0))],
      ...(ukJointLines.length ? [[], ...jointTaxExcelRows(ukJointLines)] : []),
    ].filter(Boolean);
    addSheet(wb, 'UK Tax Summary (HMRC)', summaryRows, [50,16,10,16,20,22,16,18,16,22,26,26,24]);
    XLSX.writeFile(wb, `casavello-UK-Tax-Report-HMRC-${taxYearStr}.xlsx`);
  };

  const exportIETaxExcel = (taxYearStr, taxData, corpExpTotal = 0) => {
    const wb = XLSX.utils.book_new();
    const [sy, ey] = taxYearStr.split('-').map(Number);
    const today = xlsDate();
    const fmtEUR = n => isNaN(n) ? '€0.00' : `€${Math.abs(n).toFixed(2)}`;

    const iePersonal = taxData.filter(p => String(p.ownership||'').toLowerCase() !== 'company');
    const ieCompany  = taxData.filter(p => String(p.ownership||'').toLowerCase() === 'company');

    const summaryRows = [
      [`casavello IRISH TAX REPORT (REVENUE COMMISSIONERS) — ${sy}`, '', '', '', '', ''],
      [`Tax period: 1 January ${sy} to 31 December ${sy}`, '', '', '', '', ''],
      [`Generated: ${today}`, '', '', '', '', ''],
      ['IMPORTANT: For organisational purposes only. Not tax advice for Revenue Commissioners purposes.', '', '', '', '', ''],
      ['Consult a qualified Irish accountant (ROS registered) before submitting Form 11 or CT1 to Revenue.', '', '', '', '', ''],
      ['Hibernian Technologies Limited accepts no liability for errors or omissions in these figures.', '', '', '', '', ''],
      [CAPITAL_COSTS_NOTE, '', '', '', '', ''],
      ['All figures shown in Euro (€).', '', '', '', '', ''],
      [],
      ['KEY ASSUMPTIONS:', '', '', '', '', ''],
      ['Personal ownership: Mortgage interest is fully deductible subject to RTB registration compliance.', '', '', '', '', ''],
      ['Company ownership: All finance costs including mortgage interest are fully deductible (CT1).', '', '', '', '', ''],
      ['USC and PRSI are not calculated by casavello — your accountant will apply these based on full income position.', '', '', '', '', ''],
      [],
      ['⚠️ RTB REGISTRATION WARNING:', '', '', '', '', ''],
      ['Mortgage interest is ONLY deductible where the tenancy is registered with the RTB.', '', '', '', '', ''],
      ['Unregistered tenancies may have the deduction disallowed in full. Verify RTB status before filing.', '', '', '', '', ''],
      [],
      iePersonal.length ? ['PERSONAL PROPERTIES — FORM 11', '', '', '', '', ''] : null,
      iePersonal.length ? ['Property', 'Currency', 'Gross Income (€)', 'Allowable Expenses (€)', 'Mortgage Interest (€) — Deductible', 'Taxable Profit (€)'] : null,
      ...iePersonal.map(p => [p.address, p.currency||'EUR', fmtEUR(p.income), fmtEUR(p.allowableExpenses), p.mortgageInterest>0?fmtEUR(p.mortgageInterest):'—', fmtEUR(p.taxableProfit)]),
      iePersonal.length ? ['Personal Total', '', fmtEUR(iePersonal.reduce((s,p)=>s+p.income,0)), fmtEUR(iePersonal.reduce((s,p)=>s+p.allowableExpenses,0)), fmtEUR(iePersonal.reduce((s,p)=>s+p.mortgageInterest,0)), fmtEUR(iePersonal.reduce((s,p)=>s+p.taxableProfit,0))] : null,
      [],
      ieCompany.length ? ['COMPANY PROPERTIES — CT1', '', '', '', '', ''] : null,
      ieCompany.length ? ['Property', 'Currency', 'Gross Income (€)', 'Allowable Expenses (€)', 'Mortgage Interest (€) — Deductible', 'Taxable Profit (€)'] : null,
      ...ieCompany.map(p => [p.address, p.currency||'EUR', fmtEUR(p.income), fmtEUR(p.allowableExpenses), p.mortgageInterest>0?fmtEUR(p.mortgageInterest):'—', fmtEUR(p.taxableProfit)]),
      ieCompany.length ? ['Company Overhead Expenses (Irish companies)', '', '', '', '', fmtEUR(-corpExpTotal)] : null,
      ieCompany.length ? ['Company Net (after overheads)', '', '', '', '', fmtEUR(ieCompany.reduce((s,p)=>s+p.taxableProfit,0)-corpExpTotal)] : null,
      [],
      ['COMBINED IRISH TOTAL', '', fmtEUR(taxData.reduce((s,p)=>s+p.income,0)), fmtEUR(taxData.reduce((s,p)=>s+p.allowableExpenses,0)), fmtEUR(taxData.reduce((s,p)=>s+p.mortgageInterest,0)), fmtEUR(taxData.reduce((s,p)=>s+p.taxableProfit,0)-corpExpTotal)],
    ].filter(Boolean);
    addSheet(wb, 'Irish Tax Summary (Revenue)', summaryRows, [55,10,18,22,28,16]);
    XLSX.writeFile(wb, `casavello-Irish-Tax-Report-Revenue-${sy}.xlsx`);
  };

  const exportTaxExcel = (taxYearStr, taxData, corpExpTotal) => {
    const wb = XLSX.utils.book_new();
    const [sy, ey] = taxYearStr.split('-').map(Number);
    const today = xlsDate();
    const taxLabel = `${sy}-${ey}`;

    // TAX-001: Jurisdiction-aware summary sheet
    const s24Props      = taxData.filter(p => p.treatment?.type === 's24');
    const fullyDedProps = taxData.filter(p => p.treatment?.type === 'fully_deductible');
    const companyRows   = taxData.filter(p => String(p.ownership||'').toLowerCase() === 'company');
    const personalUKRows = taxData.filter(p => String(p.ownership||'').toLowerCase() !== 'company' && !isIrishProperty(p));
    const personalIERows = taxData.filter(p => String(p.ownership||'').toLowerCase() !== 'company' && isIrishProperty(p));

    const summaryRows = [
      [`casavello TAX REPORT — TAX YEAR ${sy}/${ey}`, '', '', '', '', '', '', ''],
      [`Tax period: 6 April ${sy} to 5 April ${ey}`, '', '', '', '', '', '', ''],
      [`Generated: ${today}`, '', '', '', '', '', '', ''],
      ['IMPORTANT: These figures are for organisational purposes only and do not constitute tax advice.', '', '', '', '', '', '', ''],
      ['casavello is not a tax adviser. Consult a qualified accountant before submitting any tax return.', '', '', '', '', '', '', ''],
      ['Hibernian Technologies Limited accepts no liability for errors or omissions in these figures.', '', '', '', '', '', '', ''],
      [],
      ['KEY ASSUMPTIONS:', '', '', '', '', '', '', ''],
      ['UK Personal (S24): Mortgage interest is NOT deductible. A 20% basic rate tax credit applies instead.', '', '', '', '', '', '', ''],
      ['Irish Personal: Mortgage interest is fully deductible subject to RTB registration.', '', '', '', '', '', '', ''],
      ['Company (all jurisdictions): All finance costs including mortgage interest are fully deductible.', '', '', '', '', '', '', ''],
      [],
      // UK Personal (S24)
      personalUKRows.length ? ['UK PERSONAL PROPERTIES — S24 APPLIES (SA105)', '', '', '', '', '', '', ''] : null,
      personalUKRows.length ? ['Property', 'Jurisdiction', 'Currency', 'Gross Income', 'Allowable Expenses', 'Mortgage Interest', 'Taxable Profit', 'S24 Tax Credit (est.)'] : null,
      ...personalUKRows.map(p => [
        p.address,
        effectiveJurisdiction(p).replace('_',' ').replace(/\b\w/g,c=>c.toUpperCase()),
        p.currency||'GBP',
        fmt(p.income), fmt(p.allowableExpenses),
        p.mortgageInterest>0?fmt(p.mortgageInterest):'—',
        fmt(p.taxableProfit),
        p.s24Credit>0?fmt(p.s24Credit):'—'
      ]),
      personalUKRows.length ? ['UK Personal Total', '', '', fmt(personalUKRows.reduce((s,p)=>s+p.income,0)), fmt(personalUKRows.reduce((s,p)=>s+p.allowableExpenses,0)), fmt(personalUKRows.reduce((s,p)=>s+p.mortgageInterest,0)), fmt(personalUKRows.reduce((s,p)=>s+p.taxableProfit,0)), fmt(personalUKRows.reduce((s,p)=>s+p.s24Credit,0))] : null,
      [],
      // Irish Personal
      personalIERows.length ? ['IRISH PERSONAL PROPERTIES — MORTGAGE INTEREST FULLY DEDUCTIBLE', '', '', '', '', '', '', ''] : null,
      personalIERows.length ? ['Property', 'Jurisdiction', 'Currency', 'Gross Income', 'Allowable Expenses', 'Mortgage Interest', 'Taxable Profit', ''] : null,
      ...personalIERows.map(p => [
        p.address, 'Ireland', p.currency||'EUR',
        fmt(p.income), fmt(p.allowableExpenses),
        p.mortgageInterest>0?fmt(p.mortgageInterest):'—',
        fmt(p.taxableProfit), ''
      ]),
      personalIERows.length ? ['Irish Personal Total', '', '', fmt(personalIERows.reduce((s,p)=>s+p.income,0)), fmt(personalIERows.reduce((s,p)=>s+p.allowableExpenses,0)), fmt(personalIERows.reduce((s,p)=>s+p.mortgageInterest,0)), fmt(personalIERows.reduce((s,p)=>s+p.taxableProfit,0)), ''] : null,
      [],
      // Company
      companyRows.length ? ['COMPANY PROPERTIES — FULLY DEDUCTIBLE (CT600)', '', '', '', '', '', '', ''] : null,
      companyRows.length ? ['Property', 'Jurisdiction', 'Currency', 'Gross Income', 'Allowable Expenses', 'Mortgage Interest', 'Taxable Profit', ''] : null,
      ...companyRows.map(p => [
        p.address,
        effectiveJurisdiction(p).replace('_',' ').replace(/\b\w/g,c=>c.toUpperCase()),
        p.currency||'GBP',
        fmt(p.income), fmt(p.allowableExpenses),
        p.mortgageInterest>0?fmt(p.mortgageInterest):'—',
        fmt(p.taxableProfit), ''
      ]),
      companyRows.length ? ['Company Overhead Expenses', '', '', '', '', '', fmt(-corpExpTotal), ''] : null,
      companyRows.length ? ['Company Net (after overheads)', '', '', '', '', '', fmt(companyRows.reduce((s,p)=>s+p.taxableProfit,0)-corpExpTotal), ''] : null,
      [],
      ['COMBINED TOTAL', '', '', fmt(taxData.reduce((s,p)=>s+p.income,0)), fmt(taxData.reduce((s,p)=>s+p.allowableExpenses,0)), fmt(taxData.reduce((s,p)=>s+p.mortgageInterest,0)), fmt(taxData.reduce((s,p)=>s+p.taxableProfit,0)-corpExpTotal), fmt(taxData.reduce((s,p)=>s+p.s24Credit,0))],
    ].filter(Boolean);
    addSheet(wb, 'Tax Summary', summaryRows, [50,16,10,16,20,18,16,18]);

    // Income detail — every payment in the tax year
    const start = new Date(sy,3,6), end = new Date(ey,3,5);
    const taxPayments = payments.filter(p => {
      if (!p.month) return false;
      const [y,m] = p.month.split('-').map(Number);
      const dt = new Date(y,m-1,15);
      return dt>=start && dt<=end;
    });
    const incRows = [
      [`INCOME DETAIL — TAX YEAR ${sy}/${ey}`, '', '', '', '', ''],
      [],
      ['Month', 'Property', 'Ownership', 'Currency', 'Room / Tenant', 'Amount'],
      ...taxPayments
        .sort((a,b)=>(b.month||'').localeCompare(a.month||''))
        .map(p => {
          const prop = properties.find(pr=>pr.id==(p.propertyId||p.property_id));
          const room = (prop?.rooms||[]).find(r=>String(r.id)===String(p.roomId||p.room_id));
          return [p.month, prop?.address||'Unknown', prop?.ownership||'', prop?.currency||'GBP', room?`${room.roomName}${room.tenantName?' – '+room.tenantName:''}` : '', fmt(p.amount)];
        }),
      [],
      ['TOTAL', '', '', '', '', fmt(taxPayments.reduce((s,p)=>s+parseFloat(p.amount||0),0))],
    ];
    addSheet(wb, 'Income Detail', incRows, [12,40,12,10,25,12]);

    // Expense detail — every property expense in the tax year
    const taxExpenses = expenses.filter(e => e.date && new Date(e.date)>=start && new Date(e.date)<=end);
    const expRows = [
      [`EXPENSE DETAIL — TAX YEAR ${sy}/${ey}`, '', '', '', '', '', ''],
      [],
      ['Date', 'Property', 'Ownership', 'Currency', 'Category', 'Amount', 'Paid', 'Description'],
      ...taxExpenses
        .sort((a,b)=>(b.date||'').localeCompare(a.date||''))
        .map(e => {
          const prop = properties.find(p=>p.id==(e.propertyId||e.property_id));
          return [e.date, prop?.address||'Unknown', prop?.ownership||'', prop?.currency||'GBP', e.category||'', fmt(e.amount), (e.paid===true||e.paid==='true')?'Yes':'No', e.description||''];
        }),
      [],
      ['TOTAL', '', '', '', '', fmt(taxExpenses.reduce((s,e)=>s+parseFloat(e.amount||0),0)), '', ''],
      ['TOTAL PAID', '', '', '', '', fmt(taxExpenses.filter(e=>e.paid===true||e.paid==='true').reduce((s,e)=>s+parseFloat(e.amount||0),0)), '', ''],
    ];
    addSheet(wb, 'Expense Detail', expRows, [12,40,12,10,18,12,8,30]);

    // Company overhead expenses in tax year
    const taxCorpExp = companyExpenses.filter(e => e.date && new Date(e.date)>=start && new Date(e.date)<=end);
    const corpRows = [
      [`COMPANY OVERHEAD EXPENSES — TAX YEAR ${sy}/${ey}`, '', '', '', ''],
      [],
      ['Date', 'Category', 'Amount', 'Paid', 'Description'],
      ...taxCorpExp
        .sort((a,b)=>(b.date||'').localeCompare(a.date||''))
        .map(e => [e.date, e.category||'', fmt(e.amount), (e.paid===true||e.paid==='true')?'Yes':'No', e.description||'']),
      [],
      ['TOTAL', '', fmt(taxCorpExp.reduce((s,e)=>s+parseFloat(e.amount||0),0)), '', ''],
    ];
    addSheet(wb, 'Company Expenses', corpRows, [12,25,12,8,40]);

    // Expense breakdown by category
    const catTotals = {};
    taxExpenses.forEach(e => {
      const c = e.category||'Other';
      if (!catTotals[c]) catTotals[c] = 0;
      catTotals[c] += parseFloat(e.amount||0);
    });
    const catRows = [
      [`ALLOWABLE EXPENSES BY CATEGORY — TAX YEAR ${sy}/${ey}`, ''],
      [],
      ['Category', 'Total Amount'],
      ...Object.entries(catTotals).sort((a,b)=>b[1]-a[1]).map(([c,t])=>[c, fmt(t)]),
      [],
      ['GRAND TOTAL', fmt(taxExpenses.reduce((s,e)=>s+parseFloat(e.amount||0),0))],
    ];
    addSheet(wb, 'Expenses by Category', catRows, [30,14]);

    XLSX.writeFile(wb, `HMRC-Tax-Report-${taxLabel}.xlsx`);
  };

  const exportMTDExcel = (quarterData, taxYearStr, quarter) => {
    const wb = XLSX.utils.book_new();
    const [sy, ey] = taxYearStr.split('-').map(Number);
    const today = xlsDate();

    // Summary
    const sumRows = [
      [`casavello MTD QUARTERLY REPORT (HMRC) — TAX YEAR ${sy}/${ey}`, '', '', '', ''],
      [`Generated: ${today}`, '', '', '', ''],
      ['IMPORTANT: For organisational purposes only. Not tax advice. Consult a qualified UK accountant before submitting to HMRC MTD.', '', '', '', ''],
      ['Hibernian Technologies Limited accepts no liability for errors or omissions in these figures.', '', '', '', ''],
      [CAPITAL_COSTS_NOTE, '', '', '', ''],
      [],
      ['S24 NOTE: Mortgage interest is included in quarterly expense figures as HMRC expects it reported at MTD stage.', '', '', '', ''],
      ['The S24 restriction (20% basic rate credit) is applied by HMRC at annual Self Assessment — not at MTD quarterly submission.', '', '', '', ''],
      ['Your accountant will handle the S24 adjustment on your SA100/SA105.', '', '', '', ''],
      [MTD_SCOPE_TEXT, '', '', '', ''],
      [],
      ['Quarter', 'Income', 'Property Expenses (incl. Mortgage Interest)', 'Net Profit', 'Margin %'],
      ...quarterData.map(q => [q.quarter, fmt(q.income), fmt(q.expenses), fmt(q.profit), q.income>0?fmtPct((q.profit/q.income)*100):'—']),
      [],
      ['ANNUAL TOTAL', fmt(quarterData.reduce((s,q)=>s+q.income,0)), fmt(quarterData.reduce((s,q)=>s+q.expenses,0)), fmt(quarterData.reduce((s,q)=>s+q.profit,0)), ''],
    ];
    // FEAT-002: the joint block comes from the same entries the screen shows (utils/mtdScope.js)
    const jointEntries = mtdJointQuarterData(properties, payments, expenses, taxYearStr, quarter);
    if (jointEntries.length) { sumRows.push([], ...mtdJointExcelRows(jointEntries, fmt)); }
    addSheet(wb, 'MTD Summary', sumRows, [28,18,18,18,16,16,24,24,24]);

    // UX-003: same property filter and date range as the on-screen table (utils/mtdScope.js)
    const detail = mtdDetailItems(properties, payments, expenses, taxYearStr, quarter);

    // Income detail
    const incRows = [
      [`INCOME DETAIL — MTD ${sy}/${ey}`, '', '', '', '', ''],
      [],
      ['Month', 'Property', 'Ownership', 'Currency', 'Room / Tenant', 'Amount'],
      ...detail.income
        .slice()
        .sort((a,b)=>(b.month||'').localeCompare(a.month||''))
        .map(p => {
          const prop = properties.find(pr=>pr.id==(p.propertyId||p.property_id));
          const room = (prop?.rooms||[]).find(r=>String(r.id)===String(p.roomId||p.room_id));
          return [p.month, prop?.address||'Unknown', prop?.ownership||'', prop?.currency||'GBP', room?`${room.roomName}${room.tenantName?' – '+room.tenantName:''}` : '', fmt(p.amount)];
        }),
    ];
    addSheet(wb, 'Income Detail', incRows, [12,40,12,10,25,12]);

    // Expense detail
    const expRows = [
      [`EXPENSE DETAIL — MTD ${sy}/${ey}`, '', '', '', '', '', ''],
      [],
      ['Date', 'Property', 'Ownership', 'Currency', 'Category', 'Amount', 'Paid', 'Description'],
      ...detail.expenses
        .slice()
        .sort((a,b)=>(b.date||'').localeCompare(a.date||''))
        .map(e => {
          const prop = properties.find(p=>p.id==(e.propertyId||e.property_id));
          return [e.date, prop?.address||'Unknown', prop?.ownership||'', prop?.currency||'GBP', e.category||'', fmt(e.amount), 'Yes', e.description||''];
        }),
    ];
    addSheet(wb, 'Expense Detail', expRows, [12,40,12,10,18,12,8,30]);

    // FEAT-002: jointly owned properties' detail at 100%, on their own sheets so the personal sheets stay clean
    if (jointEntries.length) {
      const jd = mtdJointDetailItems(properties, payments, expenses, taxYearStr, quarter);
      const shareFor = (id) => { const pr = properties.find(x => x.id == id); const sh = propertyShare(pr); return sh === null ? 'NOT SET' : shareLabel(sh); };
      addSheet(wb, 'Joint Income Detail', [
        [`JOINT PROPERTIES — INCOME DETAIL (100%) — MTD ${sy}/${ey}`, '', '', '', '', ''],
        [JOINT_FILING_NOTE, '', '', '', '', ''],
        [],
        ['Month', 'Property', 'Ownership share %', 'Currency', 'Room / Tenant', 'Amount (100%)'],
        ...jd.income.slice().sort((a,b)=>(b.month||'').localeCompare(a.month||'')).map(p => {
          const pid = p.propertyId||p.property_id;
          const prop = properties.find(pr=>pr.id==pid);
          const room = (prop?.rooms||[]).find(r=>String(r.id)===String(p.roomId||p.room_id));
          return [p.month, prop?.address||'Unknown', shareFor(pid), prop?.currency||'GBP', room?`${room.roomName}${room.tenantName?' – '+room.tenantName:''}` : '', fmt(p.amount)];
        }),
      ], [12,40,18,10,25,14]);
      addSheet(wb, 'Joint Expense Detail', [
        [`JOINT PROPERTIES — EXPENSE DETAIL (100%) — MTD ${sy}/${ey}`, '', '', '', '', '', '', ''],
        [JOINT_FILING_NOTE, '', '', '', '', '', '', ''],
        [],
        ['Date', 'Property', 'Ownership share %', 'Currency', 'Category', 'Amount (100%)', 'Paid', 'Description'],
        ...jd.expenses.slice().sort((a,b)=>(b.date||'').localeCompare(a.date||'')).map(e => {
          const pid = e.propertyId||e.property_id;
          const prop = properties.find(pr=>pr.id==pid);
          return [e.date, prop?.address||'Unknown', shareFor(pid), prop?.currency||'GBP', e.category||'', fmt(e.amount), 'Yes', e.description||''];
        }),
      ], [12,40,18,10,18,14,8,30]);
    }

    XLSX.writeFile(wb, `casavello-MTD-Report-${taxYearStr}-${quarter}.xlsx`);
  };

  const exportMonthlyExcel = (year) => {
    const wb = XLSX.utils.book_new();
    const today = xlsDate();
    // BUG-005c: follows the on-screen Ownership and Currency filters, and totals each currency on its own
    const groups = monthlyByCurrency({ properties, payments, expenses, companyExpenses, companies, year, ownership: monthlyOwnership, currency: monthlyCurrency, monthStyle: 'long' });
    const d = yearDetail({ properties, payments, expenses, companyExpenses, companies, year, ownership: monthlyOwnership, currency: monthlyCurrency });
    const propOf = (id) => properties.find(pr => pr.id == id);
    const monthLabels = groups[0].months.map(m => m.label);

    addSheet(wb, 'Monthly Summary', monthlySummaryRows({ title: `MONTHLY PROFIT REPORT — ${year}`, year, today, groups }), [10,16,14,18,18,14,10]);

    // Per-property monthly breakdown
    const propRows = [
      [`INCOME BY PROPERTY — ${year}`, '', '', '', '', '', '', '', '', '', '', '', '', '', ''],
      [],
      ['Property', 'Ownership', 'Currency', ...monthLabels, 'TOTAL'],
      ...d.props.map(prop => {
        const row = [prop.address, prop.ownership||'', normCurrency(prop.currency)];
        let tot = 0;
        groups[0].months.forEach(m => {
          const amt = payments.filter(p=>(p.propertyId||p.property_id)==prop.id&&p.month===m.key).reduce((s,p)=>s+parseFloat(p.amount||0),0);
          row.push(amt>0?fmt(amt):'');
          tot+=amt;
        });
        row.push(fmt(tot));
        return row;
      }),
      [],
      ...groups.map(g => [`TOTAL (${g.currency})`, '', g.currency, ...g.months.map(m => fmt(m.inc)), fmt(g.totals.inc)]),
    ];
    addSheet(wb, 'Income by Property', propRows, [40,12,10,...Array(12).fill(9),12]);

    // Expenses by property monthly
    const expPropRows = [
      [`EXPENSES BY PROPERTY — ${year}`, '', '', '', '', '', '', '', '', '', '', '', '', '', ''],
      [],
      ['Property', 'Ownership', 'Currency', ...monthLabels, 'TOTAL'],
      ...d.props.map(prop => {
        const row = [prop.address, prop.ownership||'', normCurrency(prop.currency)];
        let tot = 0;
        groups[0].months.forEach(m => {
          const amt = expenses.filter(e=>(e.propertyId||e.property_id)==prop.id&&(e.paid===true||e.paid==='true')&&e.date?.startsWith(m.key)).reduce((s,e)=>s+parseFloat(e.amount||0),0);
          row.push(amt>0?fmt(amt):'');
          tot+=amt;
        });
        row.push(fmt(tot));
        return row;
      }),
      [],
      ...groups.map(g => [`TOTAL (${g.currency})`, '', g.currency, ...g.months.map(m => fmt(m.propExp)), fmt(g.totals.propExp)]),
    ];
    addSheet(wb, 'Expenses by Property', expPropRows, [40,12,10,...Array(12).fill(9),12]);

    // Full income detail
    const incDetailRows = [
      [`INCOME DETAIL — ${year}`, '', '', '', '', '', ''],
      [],
      ['Month', 'Property', 'Ownership', 'Currency', 'Room / Tenant', 'Amount', 'Paid Date'],
      ...[...d.payments]
        .sort((a,b)=>(b.month||'').localeCompare(a.month||''))
        .map(p=>{
          const prop=propOf(p.propertyId||p.property_id);
          const room=(prop?.rooms||[]).find(r=>String(r.id)===String(p.roomId||p.room_id));
          return [p.month, prop?.address||'Unknown', prop?.ownership||'', normCurrency(prop?.currency), room?`${room.roomName}${room.tenantName?' – '+room.tenantName:''}` : '', fmt(p.amount), p.paidDate||p.paid_date||''];
        }),
      [],
      ...totalRowsByCurrency(d.payments.map(p => ({ currency: propOf(p.propertyId||p.property_id)?.currency, amount: p.amount })), 7, 5),
    ];
    addSheet(wb, 'Income Detail', incDetailRows, [12,40,12,10,25,12,12]);

    // Full expense detail
    const expDetailRows = [
      [`EXPENSE DETAIL — ${year}`, '', '', '', '', '', ''],
      [],
      ['Date', 'Property', 'Ownership', 'Currency', 'Category', 'Amount', 'Paid', 'Description'],
      ...[...d.expenses]
        .sort((a,b)=>(b.date||'').localeCompare(a.date||''))
        .map(e=>{
          const prop=propOf(e.propertyId||e.property_id);
          return [e.date, prop?.address||'Unknown', prop?.ownership||'', normCurrency(prop?.currency), e.category||'', fmt(e.amount), (e.paid===true||e.paid==='true')?'Yes':'No', e.description||''];
        }),
      [],
      ...totalRowsByCurrency(d.expenses.filter(e=>e.paid===true||e.paid==='true').map(e => ({ currency: propOf(e.propertyId||e.property_id)?.currency, amount: e.amount })), 8, 5, 'TOTAL PAID'),
    ];
    addSheet(wb, 'Expense Detail', expDetailRows, [12,40,12,10,18,12,8,30]);

    XLSX.writeFile(wb, `Monthly-Report-${year}.xlsx`);
  };

  const exportCapitalExcel = (capData) => {
    const wb = XLSX.utils.book_new();
    // BUG-005c: rows come from the same helper the tests check; each currency is totalled on its own
    addSheet(wb, 'Capital Report', capitalRows({ capData, today: xlsDate() }), [40,15,12,10,16,14,14,10,16,14,8,12,14,20,8,14]);
    // FEAT-006: capital costs per property, each currency totalled on its own
    if (capitalCostRows.length) {
      const m2 = n => (Math.round((n || 0) * 100) / 100).toFixed(2);
      const rows = [
        ['CAPITAL COSTS SUMMARY (for Capital Gains Tax)', '', '', '', '', '', '', ''],
        ['Total = purchase price + purchase costs + capital improvements + sale costs. Initial furnishing is shown but is not part of the total. Paid expenses only.', '', '', '', '', '', '', ''],
        [],
        ['Property', 'Currency', 'Purchase Price', 'Purchase Costs', 'Capital Improvements', 'Sale Costs', 'Total', 'Initial Furnishing (not in total)'],
        ...capitalCostRows.map(r => [r.address, normCurrency(r.currency), m2(r.purchasePrice), m2(r.purchaseCosts), m2(r.capitalImprovements), m2(r.saleCosts), m2(r.total), m2(r.initialFurnishing)]),
      ];
      addSheet(wb, 'Capital Costs', rows, [40,10,16,16,20,14,16,24]);
    }
    XLSX.writeFile(wb, `Capital-Report-${xlsDate()}.xlsx`);
  };

  const ReportCard = ({ icon, title, desc, tags, onClick, locked }) => (
    <div
      onClick={locked ? () => setUpgradeModal(true) : onClick}
      title={locked ? 'Upgrade to unlock this report' : title}
      style={{
        cursor:'pointer', border:'1px solid #e2e8f0', padding:'1.5rem', borderRadius:'0.75rem',
        background: locked ? '#fafafa' : 'white', transition:'all 0.2s',
        boxShadow:'0 1px 3px rgba(0,0,0,0.05)', position:'relative',
      }}
      onMouseOver={e=>{e.currentTarget.style.boxShadow='0 4px 12px rgba(0,0,0,0.1)';e.currentTarget.style.borderColor= locked ? '#a78bfa' : '#3b82f6';}}
      onMouseOut={e=>{e.currentTarget.style.boxShadow='0 1px 3px rgba(0,0,0,0.05)';e.currentTarget.style.borderColor='#e2e8f0';}}>
      {locked && (
        <span style={{ position:'absolute', top:'0.875rem', right:'0.875rem', background:'#ede9fe', color:'#6d28d9',
          padding:'0.2rem 0.55rem', borderRadius:'1rem', fontSize:'0.7rem', fontWeight:700, letterSpacing:'0.02em' }}>
          🔒 STANDARD
        </span>
      )}
      <div style={{ fontSize:'2.5rem', marginBottom:'1rem', opacity: locked ? 0.35 : 1, filter: locked ? 'grayscale(1)' : 'none' }}>{icon}</div>
      <h3 style={{ margin:'0 0 0.5rem', color: locked ? '#94a3b8' : '#1e293b' }}>{title}</h3>
      <p style={{ color: locked ? '#a8b3c2' : '#64748b', fontSize:'0.875rem', margin:'0 0 1rem' }}>{desc}</p>
      <div style={{ display:'flex', flexWrap:'wrap', gap:'0.5rem', opacity: locked ? 0.45 : 1 }}>
        {tags.map((t,i) => <span key={i} style={{ background:t.bg, color:t.color, padding:'0.25rem 0.625rem', borderRadius:'1rem', fontSize:'0.75rem', fontWeight:600 }}>{t.label}</span>)}
      </div>
    </div>
  );

  if (!activeReport) return (
    <div>
      <h2 style={{ fontSize:'1.5rem', fontWeight:700, marginBottom:'0.5rem' }}>📊 Reports Dashboard</h2>
      <p style={{ color:'var(--text-secondary)', marginBottom: reportsUnlocked ? '2rem' : '1.25rem' }}>
        Select a report to view detailed analytics and data exports
      </p>

      {!reportsUnlocked && (
        <div style={{
          background:'linear-gradient(90deg,#f5f3ff 0%,#ede9fe 100%)', border:'1px solid #ddd6fe',
          borderRadius:'0.75rem', padding:'1rem 1.25rem', marginBottom:'2rem',
          display:'flex', alignItems:'center', justifyContent:'space-between', gap:'1rem', flexWrap:'wrap',
        }}>
          <div>
            <div style={{ fontWeight:700, fontSize:'0.9375rem', color:'#5b21b6', marginBottom:'0.15rem' }}>
              🔒 Unlock 11 more reports
            </div>
            <div style={{ fontSize:'0.8125rem', color:'#6d28d9' }}>
              Data Export is included on every plan. Upgrade to Standard for tax reports, yield analysis,
              arrears tracking, performance charts and more.
            </div>
          </div>
          <button onClick={() => setUpgradeModal(true)} style={{
            background:'#7c3aed', color:'#fff', border:'none', borderRadius:'0.5rem',
            padding:'0.55rem 1.1rem', fontWeight:700, fontSize:'0.875rem', cursor:'pointer',
            whiteSpace:'nowrap', flexShrink:0,
          }}>View Plans →</button>
        </div>
      )}

      {upgradeModal && <UpgradeModal feature="reports" onClose={() => setUpgradeModal(false)} />}

      {/* COMPANY-007: set the scope before opening a report, rather than
          generating a full-portfolio report and only then realising you
          wanted one entity. Persists as you move between reports. */}
      {isMultiCompany && (
        <div className="card" style={{ marginBottom:'1.5rem', display:'flex', alignItems:'center', justifyContent:'space-between', gap:'1rem', flexWrap:'wrap' }}>
          <CompanyScopeSelect />
          <span style={{ fontSize:'0.78rem', color:'var(--text-secondary)' }}>
            Applies to every report below except the Company Property Report, which has its own company selector.
          </span>
        </div>
      )}

      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(280px, 1fr))', gap:'1.5rem' }}>
        <ReportCard icon="📥" title="Data Export" desc="Download your full portfolio data as an Excel workbook — choose last month, 3 months, 12 months or everything" tags={[{label:'Included free',bg:'#dcfce7',color:'#15803d'},{label:'Excel',bg:'#eff6ff',color:'#1e40af'}]} onClick={() => setActiveReport('export')} />
        <ReportCard icon="📈" title="Performance Charts" desc="Visual analysis of income vs expenses with trend charts and profit tracking" tags={[{label:'Charts',bg:'#eff6ff',color:'#1e40af'},{label:'Trends',bg:'#f0fdf4',color:'#15803d'}]} onClick={() => setActiveReport('performance')} locked={!reportsUnlocked} />
        <ReportCard icon="💰" title="Monthly Profit Report" desc="Cash flow report showing actual income vs all expenses paid each month including mortgage interest. Management tool — not your HMRC taxable profit." tags={[{label:'Cash Flow',bg:'#f0fdf4',color:'#15803d'},{label:'Management',bg:'#eff6ff',color:'#1e40af'},{label:'Monthly',bg:'#fef3c7',color:'#92400e'}]} onClick={() => setActiveReport('monthly')} locked={!reportsUnlocked} />
        <ReportCard icon="💎" title="Portfolio Capital Report" desc="Capital performance, equity, LTV ratios, and portfolio valuation — filter by ownership and currency" tags={[{label:'Capital',bg:'#f3e8ff',color:'#6b21a8'},{label:'Equity',bg:'#fef3c7',color:'#92400e'}]} onClick={() => setActiveReport('capital')} locked={!reportsUnlocked} />
        <ReportCard icon="🇬🇧" title="UK Tax Report (HMRC)" desc="Tax year summary for HMRC self-assessment — England, Scotland, Wales and Northern Ireland properties. S24 finance cost restriction applies to personal ownership." tags={[{label:'HMRC',bg:'#fee2e2',color:'#991b1b'},{label:'SA105/CT600',bg:'#f0fdf4',color:'#15803d'},{label:'UK Only',bg:'#eff6ff',color:'#1e40af'}]} onClick={() => setActiveReport('tax_uk')} locked={!reportsUnlocked} />
        <ReportCard icon="🇮🇪" title="Irish Tax Report (Revenue)" desc="Tax year summary for Revenue Commissioners — Irish properties only. Mortgage interest fully deductible for personal ownership subject to RTB registration." tags={[{label:'Revenue',bg:'#fef3c7',color:'#92400e'},{label:'Form 11',bg:'#f0fdf4',color:'#15803d'},{label:'Ireland Only',bg:'#dcfce7',color:'#166534'}]} onClick={() => setActiveReport('tax_ie')} locked={!reportsUnlocked} />
        <ReportCard icon="🏢" title="Company Property Report" desc="Company tax year report for company-owned properties, using each company\u2019s own accounting year end. All finance costs fully deductible. CT600 reference — consult your accountant before filing." tags={[{label:'Company',bg:'#f3e8ff',color:'#6b21a8'},{label:'CT600',bg:'#fef3c7',color:'#92400e'},{label:'Annual',bg:'#f0fdf4',color:'#15803d'}]} onClick={() => setActiveReport('company')} locked={!reportsUnlocked} />
        <ReportCard icon="📊" title="MTD Quarterly Reports" desc="Making Tax Digital quarterly income and expense reports for HMRC. UK personal properties only. Mortgage interest included at MTD stage — S24 applied by HMRC at annual Self Assessment." tags={[{label:'MTD',bg:'#dbeafe',color:'#1e40af'},{label:'Quarterly',bg:'#fef3c7',color:'#92400e'},{label:'UK Only',bg:'#f0fdf4',color:'#15803d'}]} onClick={() => setActiveReport('mtd')} locked={!reportsUnlocked} />
        <ReportCard icon="📋" title="Rent Roll" desc="Snapshot of all rooms for any month — tenant, rent, lease dates and payment status. Select a previous month to check historical unpaid rent." tags={[{label:'Any Month',bg:'#f0fdf4',color:'#15803d'},{label:'Tenants',bg:'#eff6ff',color:'#1e40af'}]} onClick={() => setActiveReport('rentroll')} locked={!reportsUnlocked} />
        <ReportCard icon="🩺" title="Portfolio Health Check" desc="Spot missing recurring costs, unusual amounts, and rent gaps across any month range — ideal before quarterly or annual returns. Includes an AI summary." tags={[{label:'AI Summary',bg:'#f3e8ff',color:'#6b21a8'},{label:'Multi-Month',bg:'#eff6ff',color:'#1e40af'}]} onClick={() => setActiveReport('healthcheck')} locked={!reportsUnlocked} />
        <ReportCard icon="⚠️" title="Arrears Report" desc="Rooms with no payment recorded this month — outstanding amounts and days overdue" tags={[{label:'Arrears',bg:'#fee2e2',color:'#991b1b'},{label:'Cashflow',bg:'#fef3c7',color:'#92400e'}]} onClick={() => setActiveReport('arrears')} locked={!reportsUnlocked} />
        <ReportCard icon="🔑" title="Void Period Report" desc="Vacant rooms, days empty and monthly income lost across the portfolio" tags={[{label:'Voids',bg:'#fef3c7',color:'#92400e'},{label:'Occupancy',bg:'#f0fdf4',color:'#15803d'}]} onClick={() => setActiveReport('voids')} locked={!reportsUnlocked} />
        <ReportCard icon="🗂️" title="Expense Category Analysis" desc="All spending broken down by category — paid vs unpaid, trends by year" tags={[{label:'Expenses',bg:'#fee2e2',color:'#991b1b'},{label:'Categories',bg:'#f3e8ff',color:'#6b21a8'}]} onClick={() => setActiveReport('expcategory')} locked={!reportsUnlocked} />
        <ReportCard icon="📐" title="Yield Analysis" desc="Gross yield, net yield and ROI per property ranked best to worst" tags={[{label:'Yield',bg:'#f0fdf4',color:'#15803d'},{label:'ROI',bg:'#eff6ff',color:'#1e40af'},{label:'Ranked',bg:'#fef3c7',color:'#92400e'}]} onClick={() => setActiveReport('yield')} locked={!reportsUnlocked} />
      </div>
    </div>
  );

  const BackBtn = () => (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:'1rem', flexWrap:'wrap', marginBottom:'1.5rem' }}>
      <button className="btn btn-secondary" onClick={() => setActiveReport(null)}>← Back to Reports</button>
      <CompanyScopeSelect compact />
    </div>
  );

  // ── EXPORT-002: Data Export view (available on every plan) ──────────
  if (activeReport === 'export') {
    return (
      <div>
        <BackBtn />
        <h2 style={{ fontSize:'1.5rem', fontWeight:700, marginBottom:'0.5rem' }}>📥 Data Export</h2>
        <p style={{ color:'var(--text-secondary)', marginBottom:'1.75rem' }}>
          Download your portfolio data as a multi-sheet Excel workbook. Choose how far back you want to go.
        </p>

        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(220px, 1fr))', gap:'1rem', marginBottom:'1.75rem' }}>
          {EXPORT_RANGES.map(r => {
            const selected = exportRange === r.key;
            return (
              <div key={r.key} onClick={() => setExportRange(r.key)} style={{
                cursor:'pointer', padding:'1.15rem', borderRadius:'0.75rem',
                border: selected ? '2px solid #166534' : '1px solid #e2e8f0',
                background: selected ? '#f0fdf4' : '#fff',
                boxShadow: selected ? '0 2px 10px rgba(22,101,52,0.12)' : '0 1px 3px rgba(0,0,0,0.05)',
                transition:'all 0.15s',
              }}>
                <div style={{ display:'flex', alignItems:'center', gap:'0.5rem', marginBottom:'0.35rem' }}>
                  <span style={{
                    width:'1.05rem', height:'1.05rem', borderRadius:'50%', flexShrink:0,
                    border: selected ? '5px solid #166534' : '2px solid #cbd5e1', background:'#fff',
                  }} />
                  <span style={{ fontWeight:700, color: selected ? '#166534' : '#1e293b', fontSize:'0.9375rem' }}>{r.label}</span>
                </div>
                <div style={{ fontSize:'0.8125rem', color:'#64748b', paddingLeft:'1.55rem' }}>{r.desc}</div>
              </div>
            );
          })}
        </div>

        <div style={{ background:'#f8fafc', border:'1px solid #e2e8f0', borderRadius:'0.75rem', padding:'1.25rem', marginBottom:'1.5rem' }}>
          <div style={{ fontWeight:700, fontSize:'0.9375rem', marginBottom:'0.5rem', color:'#1e293b' }}>What's included</div>
          <div style={{ fontSize:'0.8125rem', color:'#64748b', lineHeight:1.7 }}>
            Portfolio summary · Rental income · Income by property · Property expenses · Expense categories ·
            Company expenses · Capital &amp; equity · Tenants
            <br />
            <span style={{ color:'#94a3b8' }}>
              Properties, tenants and capital data are always exported in full. Payments and expenses follow your selected date range.
            </span>
          </div>
        </div>

        <button
          onClick={handleExportExcel}
          style={{
            background:'#166534', color:'#fff', border:'none', borderRadius:'0.5rem',
            padding:'0.85rem 1.6rem', fontWeight:700, fontSize:'0.9375rem', cursor:'pointer',
          }}
        >
          📥 Download Excel — {rangeLabel(exportRange)}
        </button>
      </div>
    );
  }

  const YearSelect = ({ value, onChange }) => <select value={value} onChange={e=>onChange(e.target.value)} style={{ padding:'0.5rem', borderRadius:'0.5rem', border:'1px solid var(--border-light)' }}>{years.map(y=><option key={y}>{y}</option>)}</select>;
  const TaxYearSelect = ({ value, onChange }) => <select value={value} onChange={e=>onChange(e.target.value)} style={{ padding:'0.5rem', borderRadius:'0.5rem', border:'1px solid var(--border-light)' }}>{taxYears.map(y=><option key={y}>{y}</option>)}</select>;
  // BUG FIX: the Irish report shared the UK selector, so it offered "2025-2026"
  // -- a straddling April-to-April label. The underlying calculation was always
  // correct (it uses only startYear, giving 1 Jan - 31 Dec 2025), but the
  // dropdown implied a UK-style year and made a correct report look wrong.
  // Same underlying value, honest label.
  const IrishYearSelect = ({ value, onChange }) => (
    <select value={value} onChange={e=>onChange(e.target.value)} style={{ padding:'0.5rem', borderRadius:'0.5rem', border:'1px solid var(--border-light)' }}>
      {taxYears.map(y => <option key={y} value={y}>{y.split('-')[0]}</option>)}
    </select>
  );

  if (activeReport === 'performance') {
    // BUG-005c: one block per currency; euro and pound amounts are never added together and nothing is converted.
    const perfGroups = monthlyByCurrency({ properties, payments, expenses, companyExpenses, companies, year: selectedYear, ownership: selectedOwnership, currency: selectedCurrency, monthStyle: 'short' });
    const multiCur = perfGroups.length > 1;
    return (<div><BackBtn /><div className="card">
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'1rem',flexWrap:'wrap',gap:'0.75rem'}}>
        <h2 style={{margin:0}}>📈 Performance Charts</h2>
        <div style={{display:'flex',gap:'0.75rem',alignItems:'center',flexWrap:'wrap'}}>
          <YearSelect value={selectedYear} onChange={setSelectedYear} />
          <select value={selectedOwnership} onChange={e=>setSelectedOwnership(e.target.value)} style={{padding:'0.5rem',borderRadius:'0.5rem',border:'1px solid var(--border-light)'}}>
            <option value="all">All Ownership</option>
            <option value="personal">Personal &amp; joint</option>
            <option value="company">Company Only</option>
          </select>
          {availableCurrencies.length > 1 && (
            <select value={selectedCurrency} onChange={e=>setSelectedCurrency(e.target.value)} style={{padding:'0.5rem',borderRadius:'0.5rem',border:'1px solid var(--border-light)'}}>
              <option value="all">All Currencies (shown separately)</option>
              {availableCurrencies.map(c=><option key={c} value={c}>{c}</option>)}
            </select>
          )}
        </div>
      </div>
      {multiCur && <div style={{background:'#eff6ff',border:'1px solid #bfdbfe',borderRadius:'0.5rem',padding:'0.6rem 0.9rem',marginBottom:'1rem',fontSize:'0.8rem',color:'#1e40af'}}>Each currency is shown on its own. casavello never adds euro and pound amounts together and does not convert between currencies.</div>}
      {perfGroups.map(g => {
        const sym = g.symbol;
        const months = g.months;
        const maxVal = Math.max(...months.map(m=>Math.max(m.inc,m.exp)),1);
        const yLabels = [maxVal, maxVal*0.75, maxVal*0.5, maxVal*0.25, 0];
        return (<div key={g.currency} style={{marginBottom: multiCur ? '2.5rem' : 0}}>
      {multiCur && <h3 style={{margin:'0 0 0.75rem'}}>{g.currency} ({sym})</h3>}
      {/* Bar chart with Y-axis */}
      <div style={{display:'flex',gap:'0.5rem',marginBottom:'1rem'}}>
        {/* Y-axis labels */}
        <div style={{display:'flex',flexDirection:'column',justifyContent:'space-between',alignItems:'flex-end',height:'180px',paddingBottom:'18px',minWidth:'56px',flexShrink:0}}>
          {yLabels.map((v,i) => (
            <span key={i} style={{fontSize:'0.6rem',color:'var(--text-secondary)',lineHeight:1}}>
              {sym}{v>=1000?`${(v/1000).toFixed(0)}k`:v.toFixed(0)}
            </span>
          ))}
        </div>
        {/* Bars */}
        <div style={{flex:1,display:'flex',alignItems:'flex-end',gap:'0.25rem',height:'180px',borderLeft:'1px solid var(--border-light)',borderBottom:'1px solid var(--border-light)',paddingBottom:'18px',position:'relative'}}>
          {/* Gridlines */}
          {[0.25,0.5,0.75].map(pct => (
            <div key={pct} style={{position:'absolute',left:0,right:0,bottom:`calc(${pct*100}% + 18px)`,borderTop:'1px dashed var(--border-light)',pointerEvents:'none'}} />
          ))}
          {months.map((m,i)=>(<div key={i} style={{flex:1,display:'flex',flexDirection:'column',alignItems:'center',gap:'1px',height:'100%',justifyContent:'flex-end'}}>
            <div style={{width:'100%',display:'flex',gap:'1px',alignItems:'flex-end',flex:1}}>
              <div title={`Income: ${sym}${m.inc.toFixed(0)}`} style={{flex:1,background:'#22c55e',height:`${(m.inc/maxVal)*100}%`,borderRadius:'2px 2px 0 0',minHeight:m.inc>0?'3px':'0'}} />
              <div title={`Expenses: ${sym}${m.exp.toFixed(0)}`} style={{flex:1,background:'#ef4444',height:`${(m.exp/maxVal)*100}%`,borderRadius:'2px 2px 0 0',minHeight:m.exp>0?'3px':'0'}} />
            </div>
            <div style={{fontSize:'0.6rem',color:'var(--text-secondary)',textAlign:'center',lineHeight:1,marginTop:'2px'}}>{m.label}</div>
          </div>))}
        </div>
      </div>
      <div style={{display:'flex',gap:'1rem',marginBottom:'1.5rem',fontSize:'0.75rem'}}>
        <span><span style={{display:'inline-block',width:'12px',height:'12px',background:'#22c55e',borderRadius:'2px',marginRight:'4px'}}></span>Income</span>
        <span><span style={{display:'inline-block',width:'12px',height:'12px',background:'#ef4444',borderRadius:'2px',marginRight:'4px'}}></span>Expenses</span>
      </div>
      <div className="table-container"><table className="table">
        <thead><tr><th>Month</th><th>Income{multiCur ? ` (${g.currency})` : ''}</th><th>Expenses{multiCur ? ` (${g.currency})` : ''}</th><th>Net Profit{multiCur ? ` (${g.currency})` : ''}</th><th>Margin</th></tr></thead>
        <tbody>{months.map((m,i)=>(<tr key={i}><td>{m.label} {selectedYear}</td><td style={{color:'var(--success)',fontWeight:600}}>{formatMoney(m.inc, g.currency)}</td><td style={{color:'var(--danger)',fontWeight:600}}>{formatMoney(m.exp, g.currency)}</td><td style={{color:m.net>=0?'var(--success)':'var(--danger)',fontWeight:700}}>{formatMoney(m.net, g.currency)}</td><td>{m.inc>0?`${((m.net/m.inc)*100).toFixed(1)}%`:'-'}</td></tr>))}</tbody>
        <tfoot><tr style={{fontWeight:700,background:'var(--bg-light)'}}><td>Total{multiCur ? ` (${g.currency})` : ''}</td><td style={{color:'var(--success)'}}>{formatMoney(g.totals.inc, g.currency)}</td><td style={{color:'var(--danger)'}}>{formatMoney(g.totals.exp, g.currency)}</td><td style={{color:g.totals.net>=0?'var(--success)':'var(--danger)'}}>{formatMoney(g.totals.net, g.currency)}</td><td>-</td></tr></tfoot>
      </table></div>
        </div>);
      })}
      <button className="btn btn-success" style={{marginTop:'1rem'}} onClick={()=>exportPerformanceExcel(perfGroups,selectedYear,selectedOwnership,selectedCurrency)}>
        <i className="ti ti-file-spreadsheet" /> Export to Excel
      </button>
    </div></div>);
  }

  if (activeReport === 'monthly') {
    // BUG-005c: one block per currency; euro and pound amounts are never added together and nothing is converted.
    const multiCur = monthlyGroups.length > 1;
    return (<div><BackBtn /><div className="card">
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'1rem',flexWrap:'wrap',gap:'0.75rem'}}>
        <h2 style={{margin:0}}>💰 Monthly Profit Report</h2>
        <div style={{display:'flex',gap:'0.75rem',alignItems:'center',flexWrap:'wrap'}}>
          <YearSelect value={monthlyYear} onChange={setMonthlyYear} />
          <select value={monthlyOwnership} onChange={e=>setMonthlyOwnership(e.target.value)} style={{padding:'0.5rem',borderRadius:'0.5rem',border:'1px solid var(--border-light)'}}>
            <option value="all">All Ownership</option>
            <option value="personal">Personal &amp; joint</option>
            <option value="company">Company Only</option>
          </select>
          {availableCurrencies.length > 1 && (
            <select value={monthlyCurrency} onChange={e=>setMonthlyCurrency(e.target.value)} style={{padding:'0.5rem',borderRadius:'0.5rem',border:'1px solid var(--border-light)'}}>
              <option value="all">All Currencies (shown separately)</option>
              {availableCurrencies.map(c=><option key={c} value={c}>{c}</option>)}
            </select>
          )}
        </div>
      </div>
      <div style={{background:'#f0fdf4',border:'1px solid #bbf7d0',borderRadius:'0.5rem',padding:'1rem',marginBottom:'1rem',fontSize:'0.8rem',color:'#166534'}}>
        <strong>📊 Cash Flow Report — Not a Tax Report</strong><br/>
        This report shows your actual cash position: all income received minus all expenses paid, including mortgage interest.
        This is your real-world profit and loss — useful for managing your business and tracking cash flow.<br/><br/>
        <strong>Important:</strong> For UK personal properties, mortgage interest is shown here as a real cost but is <strong>not deductible for HMRC purposes</strong> (S24 restriction applies).
        Your HMRC taxable profit will differ — use the <strong>🇬🇧 UK Tax Report</strong> for your tax return figures.
        For Irish properties, mortgage interest is deductible subject to RTB registration — use the <strong>🇮🇪 Irish Tax Report</strong> for Revenue figures.
      </div>
      {multiCur && <div style={{background:'#eff6ff',border:'1px solid #bfdbfe',borderRadius:'0.5rem',padding:'0.6rem 0.9rem',marginBottom:'1rem',fontSize:'0.8rem',color:'#1e40af'}}>Each currency is shown on its own. casavello never adds euro and pound amounts together and does not convert between currencies.</div>}
      {monthlyGroups.map(g => (<div key={g.currency} style={{marginBottom: multiCur ? '2.5rem' : 0}}>
      {multiCur && <h3 style={{margin:'0 0 0.75rem'}}>{g.currency} ({g.symbol})</h3>}
      <div className="stats-grid" style={{marginBottom:'1.5rem'}}>
        <div className="stat-card"><div className="stat-label">Total Income{multiCur ? ` (${g.currency})` : ''}</div><div className="stat-value positive">{formatMoney(g.totals.inc, g.currency)}</div></div>
        <div className="stat-card"><div className="stat-label">Total Expenses{multiCur ? ` (${g.currency})` : ''}</div><div className="stat-value negative">{formatMoney(g.totals.exp, g.currency)}</div></div>
        <div className="stat-card"><div className="stat-label">Net Profit{multiCur ? ` (${g.currency})` : ''}</div><div className={`stat-value ${g.totals.net>=0?'positive':'negative'}`}>{formatMoney(g.totals.net, g.currency)}</div></div>
      </div>
      <div className="table-container"><table className="table">
        <thead><tr><th>Month</th><th>Income</th><th>Expenses</th><th>Net Profit</th></tr></thead>
        <tbody>{g.months.map((m,i)=>(<tr key={i}><td>{m.label} {monthlyYear}</td><td style={{color:'var(--success)',fontWeight:600}}>{formatMoney(m.inc, g.currency)}</td><td style={{color:'var(--danger)',fontWeight:600}}>{formatMoney(m.exp, g.currency)}</td><td style={{color:m.net>=0?'var(--success)':'var(--danger)',fontWeight:700}}>{formatMoney(m.net, g.currency)}</td></tr>))}</tbody>
      </table></div>
      </div>))}
      <button className="btn btn-success" style={{marginTop:'1rem'}} onClick={()=>exportMonthlyExcel(monthlyYear)}>
        <i className="ti ti-file-spreadsheet" /> Export to Excel
      </button>
    </div></div>);
  }

  if (activeReport === 'capital') {
    // BUG-005c: totals are worked out one currency at a time; euro and pound amounts are never added together
    const capGroups = capitalByCurrency(filteredCapitalData);
    const multiCapCur = capGroups.length > 1;
    return (<div><BackBtn /><div className="card">
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'1rem',flexWrap:'wrap',gap:'0.75rem'}}>
        <h2 style={{margin:0}}>💎 Portfolio Capital Report</h2>
        <div style={{display:'flex',gap:'0.75rem',alignItems:'center',flexWrap:'wrap'}}>
          <select value={capitalOwnership} onChange={e=>setCapitalOwnership(e.target.value)} style={{padding:'0.5rem',borderRadius:'0.5rem',border:'1px solid var(--border-light)'}}>
            <option value="all">All Ownership</option>
            <option value="personal">Personal &amp; joint</option>
            <option value="company">Company Only</option>
          </select>
          {availableCurrencies.length > 1 && (
            <select value={capitalCurrency} onChange={e=>setCapitalCurrency(e.target.value)} style={{padding:'0.5rem',borderRadius:'0.5rem',border:'1px solid var(--border-light)'}}>
              <option value="all">All Currencies (shown separately)</option>
              {availableCurrencies.map(c=><option key={c} value={c}>{c}</option>)}
            </select>
          )}
        </div>
      </div>
      {filteredCapitalData.length===0 ? (<div className="empty-state"><span>💎</span><h3>No Capital Data</h3><p>Add purchase prices and valuations to your properties</p></div>) : (<>
        {multiCapCur && <div style={{background:'#eff6ff',border:'1px solid #bfdbfe',borderRadius:'0.5rem',padding:'0.6rem 0.9rem',marginBottom:'1rem',fontSize:'0.8rem',color:'#1e40af'}}>Each currency is totalled on its own. casavello never adds euro and pound amounts together and does not convert between currencies.</div>}
        {capGroups.map(g => (<div key={g.currency}>
        {multiCapCur && <h3 style={{margin:'0 0 0.75rem'}}>{g.currency} ({g.symbol})</h3>}
        <div className="stats-grid" style={{marginBottom:'1.5rem'}}>
          <div className="stat-card"><div className="stat-label">Total Portfolio Value{multiCapCur ? ` (${g.currency})` : ''}</div><div className="stat-value positive">{formatMoney(g.val, g.currency)}</div></div>
          <div className="stat-card"><div className="stat-label">Total Mortgage Balance{multiCapCur ? ` (${g.currency})` : ''}</div><div className="stat-value negative">{formatMoney(g.mort, g.currency)}</div></div>
          <div className="stat-card"><div className="stat-label">Total Equity{multiCapCur ? ` (${g.currency})` : ''}</div><div className="stat-value positive">{formatMoney(g.equity, g.currency)}</div></div>
          <div className="stat-card"><div className="stat-label">Average LTV{multiCapCur ? ` (${g.currency})` : ''}</div><div className={`stat-value ${g.ltv>75?'negative':g.ltv>60?'':'positive'}`}>{g.ltv.toFixed(1)}%</div></div>
        </div>
        </div>))}
        <div className="table-container"><table className="table">
          <thead><tr><th>Property</th><th>Ownership</th><th>Currency</th><th>Purchase Price</th><th>Current Value</th><th>Mortgage</th><th>Equity</th><th>LTV</th><th>Gross Yield</th><th>Capital Growth</th></tr></thead>
          <tbody>{filteredCapitalData.map(p=>(<tr key={p.id}><td><strong>{p.address}</strong><div style={{fontSize:'0.75rem',color:'var(--text-secondary)'}}>{p.mortgageProvider||''} {p.mortgageRate?`@ ${p.mortgageRate}%`:''}</div></td><td><span className="badge badge-info">{p.ownership}</span></td><td>{p.currency||'GBP'}</td><td>{formatMoney(p.purchasePrice, p.currency)}</td><td>{formatMoney(p.currentValue, p.currency)}</td><td style={{color:'var(--danger)'}}>{formatMoney(p.mortgageBalance, p.currency)}</td><td style={{color:p.equity>=0?'var(--success)':'var(--danger)',fontWeight:600}}>{formatMoney(p.equity, p.currency)}</td><td><span className={`badge ${p.ltv>75?'badge-danger':p.ltv>60?'badge-warning':'badge-success'}`}>{p.ltv.toFixed(1)}%</span></td><td>{p.grossYield.toFixed(2)}%</td><td style={{color:p.capitalGrowth>=0?'var(--success)':'var(--danger)'}}>{formatMoney(p.capitalGrowth, p.currency)} ({p.capitalGrowthPct.toFixed(1)}%)</td></tr>))}</tbody>
        </table></div>
        {capitalCostRows.length > 0 && (<div style={{marginTop:'1.5rem'}}>
          <h3 style={{margin:'0 0 0.25rem'}}>Capital costs summary</h3>
          <p style={{fontSize:'0.8rem',color:'var(--text-secondary)',margin:'0 0 0.75rem'}}>For Capital Gains Tax: total = purchase price + purchase costs + capital improvements + sale costs. Initial furnishing is recorded but not part of the total. Paid expenses only. Each currency is shown on its own.</p>
          <div className="table-container"><table className="table">
            <thead><tr><th>Property</th><th>Currency</th><th>Purchase Price</th><th>Purchase Costs</th><th>Capital Improvements</th><th>Sale Costs</th><th>Total</th><th>Initial Furnishing</th></tr></thead>
            <tbody>{capitalCostRows.map(r=>(<tr key={r.id}><td><strong>{r.address}</strong></td><td>{normCurrency(r.currency)}</td><td>{formatMoney(r.purchasePrice, r.currency)}</td><td>{formatMoney(r.purchaseCosts, r.currency)}</td><td>{formatMoney(r.capitalImprovements, r.currency)}</td><td>{formatMoney(r.saleCosts, r.currency)}</td><td><strong>{formatMoney(r.total, r.currency)}</strong></td><td>{formatMoney(r.initialFurnishing, r.currency)}</td></tr>))}</tbody>
          </table></div>
        </div>)}
      <button className="btn btn-success" style={{marginTop:'1rem'}} onClick={()=>exportCapitalExcel(filteredCapitalData)}>
          <i className="ti ti-file-spreadsheet" /> Export to Excel
        </button>
      </>)}
    </div></div>);
  }

  // ===== UK TAX REPORT (HMRC) =====
  if (activeReport === 'tax_uk') {
    // UK only — filter out Irish properties
    const ukAllData = taxReportData.filter(p => !isIrishProperty(p));
    // FEAT-002: joint properties are listed separately at 100% and never added into these totals
    const { main: ukTaxData, joint: ukJointLines } = splitTaxRows(ukAllData);
    const ukJointYourShare = sumJointYourShare(ukJointLines);
    const totals = sumTaxRows(ukTaxData);
    const [sy, ey] = taxYear.split('-').map(Number);
    // BUG FIX: the "Mortgage Interest" stat card and per-row caption below
    // previously showed "S24 — credit only" whenever mortgageInterest > 0,
    // with no check on WHICH treatment actually applied to that money. Once
    // the ownership-casing fix corrected the underlying maths for company
    // properties (interest genuinely now deducted in full), these labels
    // were left pointing the wrong way — a company property's fully-
    // deducted interest was captioned "20% credit only, not deducted",
    // directly contradicting its own correctly-calculated Taxable Profit
    // figure sitting right next to it. Derived here once, from the real
    // treatment on each row, rather than inferred from whether a number
    // happens to be non-zero.
    const hasS24Interest             = ukTaxData.some(p => p.treatment.type === 's24' && p.mortgageInterest > 0);
    const hasFullyDeductibleInterest = ukTaxData.some(p => p.treatment.type === 'fully_deductible' && p.mortgageInterest > 0);
    const showCorpExp = taxOwnership === 'all' || taxOwnership === 'company';
    // BUG-005d: only the overheads of UK companies (paid, in this UK tax year, in pounds) reduce UK figures.
    // An Irish company's overhead never does; it belongs to the Irish report.
    const ukOverheads = showCorpExp ? taxReportOverheads({ companyExpenses, companies, side: 'uk', start: new Date(sy,3,6), end: new Date(ey,3,5) }) : null;
    const corpExpForYear = ukOverheads ? ukOverheads.total : 0;
    const netProfit = totals.taxableProfit - corpExpForYear;

    return (<div><BackBtn /><div className="card">
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'1rem',flexWrap:'wrap',gap:'0.75rem'}}>
        <h2 style={{margin:0}}>🇬🇧 UK Tax Report (HMRC)</h2>
        <div style={{display:'flex',gap:'0.75rem',alignItems:'center',flexWrap:'wrap'}}>
          {/* The UK tax year straddles two calendar years (6 Apr - 5 Apr), so
              the "2025-2026" label is correct here and must stay. */}
          <div style={{display:'flex',flexDirection:'column',gap:'0.15rem'}}>
            <TaxYearSelect value={taxYear} onChange={setTaxYear} />
            <span style={{fontSize:'0.68rem',color:'var(--text-secondary)'}}>UK tax year: 6 Apr &ndash; 5 Apr</span>
          </div>
          <select value={taxOwnership} onChange={e=>setTaxOwnership(e.target.value)} style={{padding:'0.5rem',borderRadius:'0.5rem',border:'1px solid var(--border-light)'}}>
            <option value="all">All Ownership</option>
            <option value="personal">Personal &amp; joint (SA105)</option>
            <option value="company">Company Only (CT600)</option>
          </select>
          <select value={taxCurrency} onChange={e=>setTaxCurrency(e.target.value)} style={{padding:'0.5rem',borderRadius:'0.5rem',border:'1px solid var(--border-light)'}}>
            <option value="all">All Currencies</option>
            <option value="GBP">GBP £</option>
          </select>
          <select value={taxJurisdiction} onChange={e=>setTaxJurisdiction(e.target.value)} style={{padding:'0.5rem',borderRadius:'0.5rem',border:'1px solid var(--border-light)'}}>
            <option value="all">All UK Jurisdictions</option>
            <option value="england">England</option>
            <option value="scotland">Scotland</option>
            <option value="wales">Wales</option>
            <option value="northern_ireland">Northern Ireland</option>
          </select>
        </div>
      </div>

      {/* FEAT-006: capital costs are not allowable */}
      <div style={{background:'#fffbeb',border:'1px solid #fcd34d',borderRadius:'0.5rem',padding:'0.75rem 1rem',marginBottom:'1rem',fontSize:'0.875rem',color:'#92400e'}}>
        <strong>Note:</strong> {CAPITAL_COSTS_NOTE}
      </div>

      {/* UK Disclaimer */}
      <div style={{background:'#fef2f2',border:'1px solid #fca5a5',borderRadius:'0.5rem',padding:'1rem',marginBottom:'1rem',fontSize:'0.875rem',color:'#991b1b'}}>
        <strong>⚠️ Important Disclaimer — UK Properties</strong><br/>
        These figures are produced by casavello for organisational purposes only and do not constitute tax advice for HMRC purposes.
        casavello is not a tax adviser. You must consult a qualified UK accountant or tax adviser before submitting any HMRC return (SA105, SA100 or CT600).
        Hibernian Technologies Limited accepts no liability for errors or omissions in these figures.
      </div>

      {/* UK Assumptions */}
      <div style={{background:'#eff6ff',border:'1px solid #bfdbfe',borderRadius:'0.5rem',padding:'1rem',marginBottom:'1.5rem',fontSize:'0.8rem',color:'#1e40af'}}>
        <strong>📋 UK Tax Assumptions — 6 April {sy} to 5 April {ey}</strong>
        <ul style={{margin:'0.5rem 0 0',paddingLeft:'1.25rem'}}>
          <li><strong>S24 Finance Cost Restriction (personal ownership):</strong> Mortgage Interest and Other Finance Costs cannot be deducted from rental income. Instead a 20% basic rate tax credit is applied. This currently applies in England, Scotland, Wales and Northern Ireland.</li>
          <li><strong>Company ownership:</strong> All finance costs including mortgage interest are fully deductible business expenses (CT600).</li>
          <li><strong>Scotland / Wales / Northern Ireland:</strong> Currently treated the same as England for finance costs. These jurisdictions may introduce their own rules in future — consult your accountant.</li>
          <li>Only expenses marked as <em>paid</em> within the tax year are included.</li>
        </ul>
      </div>

      {/* Stats */}
      <div className="stats-grid" style={{marginBottom:'1.5rem'}}>
        <div className="stat-card"><div className="stat-label">Total Rental Income</div><div className="stat-value positive">{formatCurrency(totals.inc)}</div></div>
        <div className="stat-card"><div className="stat-label">Allowable Expenses</div><div className="stat-value negative">{formatCurrency(totals.allowable)}</div></div>
        {totals.mortgageInterest > 0 && (
          <div className="stat-card">
            <div className="stat-label">Mortgage Interest{hasS24Interest && !hasFullyDeductibleInterest ? ' (S24)' : ''}</div>
            <div className="stat-value negative">{formatCurrency(totals.mortgageInterest)}</div>
            <div style={{fontSize:'0.7rem',color:'var(--text-secondary)',marginTop:'0.25rem'}}>
              {hasS24Interest && hasFullyDeductibleInterest
                ? 'Mixed: 20% credit only for personal properties, fully deductible for company properties'
                : hasS24Interest
                  ? '20% credit only — not deducted'
                  : 'Fully deductible business expense'}
            </div>
          </div>
        )}
        {totals.s24Credit > 0 && <div className="stat-card"><div className="stat-label">S24 Tax Credit (est.)</div><div className="stat-value positive">{formatCurrency(totals.s24Credit)}</div><div style={{fontSize:'0.7rem',color:'var(--text-secondary)',marginTop:'0.25rem'}}>Reduces your tax bill</div></div>}
        {showCorpExp && corpExpForYear > 0 && <div className="stat-card"><div className="stat-label">Company Overheads</div><div className="stat-value negative">{formatCurrency(corpExpForYear)}</div></div>}
        <div className="stat-card"><div className="stat-label">Taxable Profit (est.)</div><div className={`stat-value ${netProfit>=0?'positive':'negative'}`}>{formatCurrency(netProfit)}</div></div>
      </div>
      {ukOverheads && ukOverheads.skipped.length > 0 && <div style={{background:'#fef3c7',border:'1px solid #fde68a',borderRadius:'0.5rem',padding:'0.6rem 0.9rem',marginBottom:'1rem',fontSize:'0.8rem',color:'#92400e'}}>⚠️ {ukOverheads.skipped.length} company overhead{ukOverheads.skipped.length===1?'':'s'} ({ukOverheads.skippedTotal.toFixed(2)}) {ukOverheads.skipped.length===1?'was':'were'} left out because {ukOverheads.skipped.length===1?'its':'their'} company's currency is not pounds. Check the Companies page.</div>}

      {ukAllData.length === 0 ? (
        <div className="empty-state"><span>🇬🇧</span><h3>No UK properties found</h3><p>No properties with England, Scotland, Wales or Northern Ireland jurisdiction for this tax year.</p></div>
      ) : ukTaxData.length === 0 ? (
        <p style={{color:'var(--text-secondary)'}}>No personally or company-owned UK properties for this tax year. Jointly owned properties are listed below.</p>
      ) : (
        <div className="table-container"><table className="table">
          <thead><tr>
            <th>Property</th><th>Jurisdiction</th><th>Ownership</th><th>Treatment</th>
            <th>Income</th><th>Allowable Expenses</th><th>Mortgage Interest</th><th>Taxable Profit</th><th>S24 Credit (est.)</th>
          </tr></thead>
          <tbody>{ukTaxData.map(p=>(
            <tr key={p.id}>
              <td><strong>{p.address}</strong></td>
              <td style={{fontSize:'0.8rem'}}>{effectiveJurisdiction(p).replace('_',' ').replace(/\b\w/g,c=>c.toUpperCase())}</td>
              <td><span className="badge badge-info" style={{textTransform:'capitalize'}}>{p.ownership||'personal'}</span></td>
              <td><span style={{fontSize:'0.75rem',background:p.treatment?.color+'20',color:p.treatment?.color,padding:'0.2rem 0.5rem',borderRadius:'0.25rem',fontWeight:600}}>{p.treatment?.shortLabel}</span></td>
              <td style={{color:'var(--success)',fontWeight:600}}>{formatCurrency(p.income)}</td>
              <td>{p.allowableExpenses>0?formatCurrency(p.allowableExpenses):'—'}</td>
              <td>{p.mortgageInterest>0?<span>{formatCurrency(p.mortgageInterest)}<div style={{fontSize:'0.7rem',color: p.treatment.type==='s24' ? '#dc2626' : '#166534',fontWeight:600}}>{p.treatment.type==='s24' ? 'S24 — credit only' : 'Fully deductible'}</div></span>:'—'}</td>
              <td style={{color:p.taxableProfit>=0?'var(--success)':'var(--danger)',fontWeight:700}}>{formatCurrency(p.taxableProfit)}</td>
              <td style={{color:'#166534',fontWeight:600}}>{p.s24Credit>0?formatCurrency(p.s24Credit):'—'}</td>
            </tr>
          ))}</tbody>
          <tfoot><tr style={{fontWeight:700,background:'var(--bg-light)'}}>
            <td colSpan="4">Total</td>
            <td style={{color:'var(--success)'}}>{formatCurrency(totals.inc)}</td>
            <td>{formatCurrency(totals.allowable)}</td>
            <td>{totals.mortgageInterest>0?formatCurrency(totals.mortgageInterest):'—'}</td>
            <td style={{color:netProfit>=0?'var(--success)':'var(--danger)'}}>{formatCurrency(netProfit)}</td>
            <td style={{color:'#166534'}}>{totals.s24Credit>0?formatCurrency(totals.s24Credit):'—'}</td>
          </tr></tfoot>
        </table></div>
      )}

      {ukJointLines.length > 0 && (
        <div style={{marginTop:'1.5rem'}}>
          <h3 style={{margin:'0 0 0.5rem'}}>Jointly owned properties — 100% figures</h3>
          <div style={{background:'#fef3c7',border:'1px solid #fde68a',borderRadius:'0.5rem',padding:'0.75rem 1rem',marginBottom:'0.75rem',fontSize:'0.85rem',color:'#92400e'}}>
            <strong>ℹ️ {JOINT_FILING_NOTE}</strong> These properties are not included in the totals above, so nothing is counted twice.
          </div>
          <div className="table-container"><table className="table">
            <thead><tr>
              <th>Property</th><th>Income (100%)</th><th>Allowable Expenses (100%)</th><th>Mortgage Interest (100%)</th><th>Taxable Profit (100%)</th>
              <th>Ownership share</th><th>Your share (info only)</th>
            </tr></thead>
            <tbody>{ukJointLines.map(l=>(
              <tr key={l.id}>
                <td><strong>{l.address}</strong><div style={{fontSize:'0.7rem',color:'var(--text-secondary)'}}>Joint</div></td>
                <td style={{color:'var(--success)',fontWeight:600}}>{signedMoney(l.income, '£')}</td>
                <td>{l.allowableExpenses>0?signedMoney(l.allowableExpenses, '£'):'—'}</td>
                <td>{l.mortgageInterest>0?signedMoney(l.mortgageInterest, '£'):'—'}</td>
                <td style={{fontWeight:700}}>{signedMoney(l.taxableProfit, '£')}</td>
                <td>{l.shareMissing
                  ? <span style={{color:'#dc2626',fontWeight:600}} title={SHARE_PROMPT}>Not set — set it on the property page</span>
                  : <strong>{shareLabel(l.sharePercent)}</strong>}</td>
                <td style={{fontSize:'0.8rem',color:'var(--text-secondary)'}}>{l.shareMissing ? '—' : (
                  <div>Income {signedMoney(l.yourIncome, '£')}<br/>Expenses {signedMoney(l.yourAllowableExpenses, '£')}{l.mortgageInterest>0 && <><br/>Interest {signedMoney(l.yourMortgageInterest, '£')}</>}<br/>Profit {signedMoney(l.yourTaxableProfit, '£')}</div>
                )}</td>
              </tr>
            ))}</tbody>
            {ukJointYourShare.count > 0 && <tfoot><tr style={{fontWeight:600,background:'var(--bg-light)'}}>
              <td colSpan="6">Your share total (information only; properties with a share set)</td>
              <td style={{fontSize:'0.8rem'}}>Income {signedMoney(ukJointYourShare.income, '£')}<br/>Expenses {signedMoney(ukJointYourShare.allowableExpenses, '£')}<br/>Profit {signedMoney(ukJointYourShare.taxableProfit, '£')}</td>
            </tr></tfoot>}
          </table></div>
        </div>
      )}

      {totals.s24Credit > 0 && (
        <div style={{background:'#eff6ff',border:'1px solid #bfdbfe',borderRadius:'0.5rem',padding:'1rem',marginTop:'1rem',fontSize:'0.8rem',color:'#1e40af'}}>
          <strong>ℹ️ How to use S24 on your SA105:</strong> Your mortgage interest of {formatCurrency(totals.mortgageInterest)} cannot be deducted from rental income.
          Enter this amount in Box 26 "Residential finance costs" on your SA105. HMRC will then calculate your 20% tax credit ({formatCurrency(totals.s24Credit)}) against your final tax bill.
          Your taxable profit is higher than it would be without S24 — your accountant can confirm the full impact on your income tax liability.
        </div>
      )}

      {/* TAX-001 Open Question 1, confirmed with Sam: HMRC calculates one
          person's S24 credit across their WHOLE UK property business — one
          combined figure, not one per property. The per-property table
          above is kept exactly as before (core to "which properties are
          underperforming"); this card adds the portfolio-level approximate
          alongside it, rather than replacing the per-property detail. Only
          shown for personal/joint properties, since S24 aggregation is an
          individual-level concept — a company's Corporation Tax return
          doesn't work this way. */}
      {(() => {
        const personalUkTaxData = ukTaxData.filter(p => String(p.ownership||'').toLowerCase() !== 'company'); // FEAT-002: joint properties are not in ukTaxData
        if (personalUkTaxData.length < 2) return null; // only meaningful once there's something to combine
        const portfolioIncome         = personalUkTaxData.reduce((s,p)=>s + p.income, 0);
        const portfolioExpenses       = personalUkTaxData.reduce((s,p)=>s + p.allowableExpenses, 0);
        const portfolioMortgage       = personalUkTaxData.reduce((s,p)=>s + p.mortgageInterest, 0);
        const portfolioTaxableProfit  = personalUkTaxData.reduce((s,p)=>s + p.taxableProfit, 0);
        const portfolioS24Credit      = personalUkTaxData.reduce((s,p)=>s + p.s24Credit, 0);
        return (
          <div className="card" style={{ marginTop: '1rem', border: '1px solid #c4b5fd' }}>
            <div style={{ fontWeight: 700, marginBottom: '0.5rem' }}>📊 Your combined UK property business (estimate)</div>
            <p style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', marginBottom: '0.75rem' }}>
              HMRC treats all of your UK personal rental properties as a single property business — one
              combined profit figure and one S24 credit, not one per property. This approximates that combined
              view for your personally owned properties only. Jointly owned properties are listed separately
              above at 100%: your filing software combines them using your ownership share. Your accountant
              applies the final calculation on your actual SA105/SA100.
            </p>
            <div className="stats-grid">
              <div className="stat-card"><div className="stat-label">Combined Income</div><div className="stat-value positive">{formatCurrency(portfolioIncome)}</div></div>
              <div className="stat-card"><div className="stat-label">Combined Expenses</div><div className="stat-value negative">{formatCurrency(portfolioExpenses)}</div></div>
              {portfolioMortgage > 0 && <div className="stat-card"><div className="stat-label">Combined Mortgage Interest</div><div className="stat-value negative">{formatCurrency(portfolioMortgage)}</div></div>}
              {portfolioS24Credit > 0 && <div className="stat-card"><div className="stat-label">Combined S24 Credit (est.)</div><div className="stat-value positive">{formatCurrency(portfolioS24Credit)}</div></div>}
              <div className="stat-card"><div className="stat-label">Combined Taxable Profit (est.)</div><div className={`stat-value ${portfolioTaxableProfit>=0?'positive':'negative'}`}>{formatCurrency(portfolioTaxableProfit)}</div></div>
            </div>
          </div>
        );
      })()}

      <button className="btn btn-success" style={{marginTop:'1rem'}} onClick={()=>exportUKTaxExcel(taxYear, ukAllData, corpExpForYear)}>
        <i className="ti ti-file-spreadsheet" /> Export to Excel (HMRC)
      </button>
    </div></div>);
  }

  // ===== IRISH TAX REPORT (REVENUE) =====
  if (activeReport === 'tax_ie') {
    // Irish properties only
    const ieTaxData = taxReportData.filter(p => isIrishProperty(p));
    const fmt = (n) => isNaN(n) ? '€0.00' : `€${Math.abs(n).toFixed(2)}`;
    const totals = {
      inc:              ieTaxData.reduce((s,p)=>s+p.income,0),
      mortgageInterest: ieTaxData.reduce((s,p)=>s+p.mortgageInterest,0),
      allowable:        ieTaxData.reduce((s,p)=>s+p.allowableExpenses,0),
      taxableProfit:    ieTaxData.reduce((s,p)=>s+p.taxableProfit,0),
    };
    const [sy, ey] = taxYear.split('-').map(Number);
    // Irish tax year is calendar year — note this for user
    const ieYear = sy; // Use the start year as the reference Irish tax year
    // BUG-005d: only the overheads of Irish companies (paid, in this Irish calendar year, in euro) reduce Irish figures.
    // A UK company's overhead never does; it belongs to the UK report.
    const showIeCorp = taxOwnership === 'all' || taxOwnership === 'company';
    const ieOverheads = showIeCorp ? taxReportOverheads({ companyExpenses, companies, side: 'ie', start: new Date(sy,0,1), end: new Date(sy,11,31) }) : null;
    const ieCorpExp = ieOverheads ? ieOverheads.total : 0;
    const ieNetProfit = totals.taxableProfit - ieCorpExp;

    return (<div><BackBtn /><div className="card">
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'1rem',flexWrap:'wrap',gap:'0.75rem'}}>
        <h2 style={{margin:0}}>🇮🇪 Irish Tax Report (Revenue)</h2>
        <div style={{display:'flex',gap:'0.75rem',alignItems:'center',flexWrap:'wrap'}}>
          <div style={{display:'flex',flexDirection:'column',gap:'0.15rem'}}>
            <IrishYearSelect value={taxYear} onChange={setTaxYear} />
            <span style={{fontSize:'0.68rem',color:'var(--text-secondary)'}}>Irish tax year: 1 Jan &ndash; 31 Dec</span>
          </div>
          <select value={taxOwnership} onChange={e=>setTaxOwnership(e.target.value)} style={{padding:'0.5rem',borderRadius:'0.5rem',border:'1px solid var(--border-light)'}}>
            <option value="all">All Ownership</option>
            <option value="personal">Personal &amp; joint (Form 11)</option>
            <option value="company">Company Only (CT1)</option>
          </select>
        </div>
      </div>

      {/* FEAT-006: capital costs are not allowable */}
      <div style={{background:'#fffbeb',border:'1px solid #fcd34d',borderRadius:'0.5rem',padding:'0.75rem 1rem',marginBottom:'1rem',fontSize:'0.875rem',color:'#92400e'}}>
        <strong>Note:</strong> {CAPITAL_COSTS_NOTE}
      </div>

      {/* Irish Disclaimer */}
      <div style={{background:'#fef2f2',border:'1px solid #fca5a5',borderRadius:'0.5rem',padding:'1rem',marginBottom:'1rem',fontSize:'0.875rem',color:'#991b1b'}}>
        <strong>⚠️ Important Disclaimer — Irish Properties</strong><br/>
        <strong>Reporting period: 1 January {ieYear} to 31 December {ieYear}</strong> (the Irish calendar tax year).<br/>
        These figures are produced by casavello for organisational purposes only and do not constitute tax advice for Revenue Commissioners purposes.
        casavello is not a tax adviser. You must consult a qualified Irish accountant or tax adviser (ROS registered) before submitting any Revenue return (Form 11 or CT1).
        Hibernian Technologies Limited accepts no liability for errors or omissions in these figures.
        All figures are shown in Euro (€).
      </div>

      {/* Irish Assumptions */}
      <div style={{background:'#f0fdf4',border:'1px solid #bbf7d0',borderRadius:'0.5rem',padding:'1rem',marginBottom:'1.5rem',fontSize:'0.8rem',color:'#166534'}}>
        <strong>📋 Irish Tax Assumptions — Tax Year {sy}/{ey}</strong>
        <ul style={{margin:'0.5rem 0 0',paddingLeft:'1.25rem'}}>
          <li><strong>Personal ownership:</strong> Mortgage interest is fully deductible against rental income, subject to the property being registered with the RTB (Residential Tenancies Board). Failure to register with RTB may disallow the deduction — verify RTB compliance in the Tenants section.</li>
          <li><strong>Company ownership:</strong> All finance costs including mortgage interest are fully deductible business expenses (CT1).</li>
          <li><strong>Tax year:</strong> This report correctly uses the Irish calendar tax year (1 January – 31 December), not the UK's April–April period — all figures below are calculated on that basis.</li>
          <li><strong>USC and PRSI:</strong> These charges are not calculated by casavello — your accountant will apply these based on your full income position.</li>
          <li>Only expenses marked as <em>paid</em> within the period are included. All figures shown in Euro (€).</li>
        </ul>
      </div>

      {/* Stats */}
      <div className="stats-grid" style={{marginBottom:'1.5rem'}}>
        <div className="stat-card"><div className="stat-label">Total Rental Income</div><div className="stat-value positive">{fmt(totals.inc)}</div></div>
        <div className="stat-card"><div className="stat-label">Allowable Expenses</div><div className="stat-value negative">{fmt(totals.allowable)}</div></div>
        {totals.mortgageInterest > 0 && <div className="stat-card"><div className="stat-label">Mortgage Interest</div><div className="stat-value negative">{fmt(totals.mortgageInterest)}</div><div style={{fontSize:'0.7rem',color:'var(--text-secondary)',marginTop:'0.25rem'}}>Fully deductible (subject to RTB)</div></div>}
        {ieCorpExp > 0 && <div className="stat-card"><div className="stat-label">Company Overheads</div><div className="stat-value negative">{fmt(ieCorpExp)}</div></div>}
        <div className="stat-card"><div className="stat-label">Taxable Profit (est.)</div><div className={`stat-value ${ieNetProfit>=0?'positive':'negative'}`}>{fmt(ieNetProfit)}</div></div>
      </div>
      {ieOverheads && ieOverheads.skipped.length > 0 && <div style={{background:'#fef3c7',border:'1px solid #fde68a',borderRadius:'0.5rem',padding:'0.6rem 0.9rem',marginBottom:'1rem',fontSize:'0.8rem',color:'#92400e'}}>⚠️ {ieOverheads.skipped.length} company overhead{ieOverheads.skipped.length===1?'':'s'} ({ieOverheads.skippedTotal.toFixed(2)}) {ieOverheads.skipped.length===1?'was':'were'} left out because {ieOverheads.skipped.length===1?'its':'their'} company's currency is not euro. Check the Companies page.</div>}

      {/* TAX-001: summary-level warning so an unregistered tenancy isn't
          only visible by scrolling to that one row in a long table. */}
      {ieTaxData.some(p => p.rtbFlag === 'unregistered') && (
        <div style={{background:'#fef2f2',border:'1px solid #fca5a5',borderRadius:'0.5rem',padding:'0.85rem 1rem',marginBottom:'1rem',fontSize:'0.8rem',color:'#991b1b'}}>
          ⚠️ One or more properties below has a tenancy that was not RTB-registered during this period. Interest deductibility for those properties is at risk — register at <a href="https://www.rtb.ie" style={{color:'#991b1b',fontWeight:600}}>rtb.ie</a> and confirm with your accountant.
        </div>
      )}

      {ieTaxData.length === 0 ? (
        <div className="empty-state"><span>🇮🇪</span><h3>No Irish properties found</h3><p>No properties with Ireland jurisdiction found. Set the jurisdiction to Ireland on each property in the Management tab.</p></div>
      ) : (
        <div className="table-container"><table className="table">
          <thead><tr>
            <th>Property</th><th>Ownership</th><th>RTB Registered</th>
            <th>Income (€)</th><th>Allowable Expenses (€)</th><th>Mortgage Interest (€)</th><th>Taxable Profit (€)</th>
          </tr></thead>
          <tbody>{ieTaxData.map(p=>{
            // TAX-001: was a hardcoded "Check tenants tab" placeholder — now
            // reads the actual rtb_registered flag already tracked on each
            // tenancy (and reminded on via the existing IC-001 cron), kept
            // deliberately simple per Sam's decision: one flag per property
            // per period if ANY tenancy in that period wasn't registered,
            // rather than a per-day apportioned calculation.
            const rtbCell = p.rtbFlag === 'registered'
              ? <span style={{fontSize:'0.75rem',color:'#166534',fontWeight:600}}>✓ Registered</span>
              : p.rtbFlag === 'unregistered'
                ? <span style={{fontSize:'0.75rem',color:'#dc2626',fontWeight:700}}>⚠ Not registered — deduction at risk</span>
                : p.rtbFlag === 'no-tenancy-on-record'
                  ? <span style={{fontSize:'0.75rem',color:'var(--text-secondary)'}}>No tenancy on record</span>
                  : <span style={{fontSize:'0.75rem',color:'var(--text-secondary)'}}>—</span>;
            return (
              <tr key={p.id}>
                <td>
                  <strong>{p.address}</strong>
                  {p.isJoint && <div style={{fontSize:'0.7rem',color:'var(--text-secondary)'}}>{p.sharePercent === null ? 'Joint — ownership share not set' : `Joint — your share ${p.sharePercent}%`}</div>}
                </td>
                <td><span className="badge badge-info" style={{textTransform:'capitalize'}}>{p.ownership||'personal'}</span></td>
                <td>{rtbCell}</td>
                <td style={{color:'var(--success)',fontWeight:600}}>{fmt(p.income)}</td>
                <td>{p.allowableExpenses>0?fmt(p.allowableExpenses):'—'}</td>
                <td>{p.mortgageInterest>0?<span>{fmt(p.mortgageInterest)}<div style={{fontSize:'0.7rem',color:'#166534',fontWeight:600}}>Deducted ✓</div></span>:'—'}</td>
                <td style={{color:p.taxableProfit>=0?'var(--success)':'var(--danger)',fontWeight:700}}>
                  {fmt(p.taxableProfit)}
                  {p.isJoint && p.yourTaxableProfit !== null && <div style={{fontSize:'0.7rem',fontWeight:500,color:'var(--text-secondary)'}}>Your share: {fmt(p.yourTaxableProfit)}</div>}
                </td>
              </tr>
            );
          })}</tbody>
          <tfoot><tr style={{fontWeight:700,background:'var(--bg-light)'}}>
            <td colSpan="3">Total</td>
            <td style={{color:'var(--success)'}}>{fmt(totals.inc)}</td>
            <td>{fmt(totals.allowable)}</td>
            <td>{totals.mortgageInterest>0?fmt(totals.mortgageInterest):'—'}</td>
            <td style={{color:ieNetProfit>=0?'var(--success)':'var(--danger)'}}>{fmt(ieNetProfit)}</td>
          </tr></tfoot>
        </table></div>
      )}

      <div style={{background:'#fef3c7',border:'1px solid #fde68a',borderRadius:'0.5rem',padding:'1rem',marginTop:'1rem',fontSize:'0.8rem',color:'#92400e'}}>
        <strong>ℹ️ RTB Registration Reminder:</strong> Mortgage interest is only deductible for Irish rental properties where the tenancy is registered with the Residential Tenancies Board (RTB).
        Unregistered tenancies may have the mortgage interest deduction disallowed in full. Check the compliance status of each tenancy in the Tenants section before filing.
      </div>

      <button className="btn btn-success" style={{marginTop:'1rem'}} onClick={()=>exportIETaxExcel(taxYear, ieTaxData, ieCorpExp)}>
        <i className="ti ti-file-spreadsheet" /> Export to Excel (Revenue)
      </button>
    </div></div>);
  }

  if (activeReport === 'mtd') {
    return (<div><BackBtn /><div className="card">
      <h2 style={{marginBottom:'1rem'}}>📊 MTD Quarterly Reports</h2>

      {/* FEAT-006: capital costs are not allowable */}
      <div style={{background:'#fffbeb',border:'1px solid #fcd34d',borderRadius:'0.5rem',padding:'0.75rem 1rem',marginBottom:'1rem',fontSize:'0.875rem',color:'#92400e'}}>
        <strong>Note:</strong> {CAPITAL_COSTS_NOTE}
      </div>

      {/* Disclaimer */}
      <div style={{background:'#fef2f2',border:'1px solid #fca5a5',borderRadius:'0.5rem',padding:'1rem',marginBottom:'1rem',fontSize:'0.875rem',color:'#991b1b'}}>
        <strong>⚠️ {MTD_SCOPE_TEXT}</strong><br/>
        These figures are produced by casavello for organisational purposes only and do not constitute tax advice for HMRC MTD purposes.
        casavello is not a tax adviser. You must consult a qualified UK accountant before submitting any MTD quarterly update to HMRC.
        Hibernian Technologies Limited accepts no liability for errors or omissions in these figures.
      </div>

      {/* MTD assumptions including S24 explanation */}
      <div style={{background:'#eff6ff',border:'1px solid #bfdbfe',borderRadius:'0.5rem',padding:'1rem',marginBottom:'1.5rem',fontSize:'0.8rem',color:'#1e40af'}}>
        <strong>📋 MTD Assumptions & S24 Note</strong>
        <ul style={{margin:'0.5rem 0 0',paddingLeft:'1.25rem'}}>
          <li><strong>Making Tax Digital:</strong> Quarterly income and expense updates required from April 2026 for personal property income over £50,000/year. These quarterly figures feed your annual Self Assessment.</li>
          <li><strong>What MTD submits:</strong> Gross rental income and allowable expenses per quarter — HMRC receives raw figures, not your final tax liability.</li>
          <li><strong>S24 and mortgage interest:</strong> Mortgage interest IS included in these quarterly expense figures as HMRC expects it reported here. However S24 restriction (limiting relief to 20% basic rate credit) is applied by HMRC at annual Self Assessment stage — not at the quarterly MTD submission stage. Your accountant will handle the S24 adjustment on your SA100/SA105.</li>
          <li>Only expenses marked as <em>paid</em> within each quarter are included.</li>
        </ul>
      </div>
      <div className="form-grid" style={{marginBottom:'1.5rem'}}>
        <div className="form-group"><label>Tax Year</label><TaxYearSelect value={mtdTaxYear} onChange={setMtdTaxYear} /></div>
        <div className="form-group"><label>Quarter</label>
          <select value={mtdQuarter} onChange={e=>setMtdQuarter(e.target.value)} style={{width:'100%',padding:'0.625rem',border:'1px solid var(--border-light)',borderRadius:'0.5rem'}}>
            <option value="all">All Quarters (Annual View)</option>
            <option value="q1">Q1 (Apr 6 - Jul 5)</option>
            <option value="q2">Q2 (Jul 6 - Oct 5)</option>
            <option value="q3">Q3 (Oct 6 - Jan 5)</option>
            <option value="q4">Q4 (Jan 6 - Apr 5)</option>
          </select>
        </div>
      </div>
      <div className="table-container"><table className="table">
        <thead><tr><th>Quarter</th><th>Income</th><th>Expenses</th><th>Net Profit</th></tr></thead>
        <tbody>{mtdReportData.map((q,i)=>(<tr key={i}><td><strong>{q.quarter}</strong></td><td style={{color:'var(--success)',fontWeight:600}}>{formatCurrency(q.income)}</td><td style={{color:'var(--danger)',fontWeight:600}}>{formatCurrency(q.expenses)}</td><td style={{color:q.profit>=0?'var(--success)':'var(--danger)',fontWeight:700}}>{formatCurrency(q.profit)}</td></tr>))}</tbody>
        {mtdReportData.length>1 && <tfoot><tr style={{fontWeight:700,background:'var(--bg-light)'}}><td>Annual Total</td><td style={{color:'var(--success)'}}>{formatCurrency(mtdReportData.reduce((s,q)=>s+q.income,0))}</td><td style={{color:'var(--danger)'}}>{formatCurrency(mtdReportData.reduce((s,q)=>s+q.expenses,0))}</td><td>{formatCurrency(mtdReportData.reduce((s,q)=>s+q.profit,0))}</td></tr></tfoot>}
      </table></div>
      {mtdJointData.length > 0 && (
        <div style={{marginTop:'1.75rem'}}>
          <h3 style={{margin:'0 0 0.5rem'}}>Jointly owned properties — 100% figures</h3>
          <div style={{background:'#fef3c7',border:'1px solid #fde68a',borderRadius:'0.5rem',padding:'0.75rem 1rem',marginBottom:'0.75rem',fontSize:'0.85rem',color:'#92400e'}}>
            <strong>ℹ️ {JOINT_FILING_NOTE}</strong> These figures are not part of the personal totals above, so nothing is counted twice.
          </div>
          {mtdJointData.map(e => (
            <div key={e.id} style={{marginBottom:'1.25rem'}}>
              <div style={{display:'flex',justifyContent:'space-between',flexWrap:'wrap',gap:'0.5rem',marginBottom:'0.35rem'}}>
                <strong>{e.address}</strong>
                {e.shareMissing
                  ? <span style={{color:'#dc2626',fontWeight:600}} title={SHARE_PROMPT}>Ownership share not set — set it on the property page</span>
                  : <span>Ownership share: <strong>{shareLabel(e.sharePercent)}</strong></span>}
              </div>
              <div className="table-container"><table className="table">
                <thead><tr><th>Quarter</th><th>Income (100%)</th><th>Expenses (100%)</th><th>Net Profit (100%)</th>
                  <th>Your income (info)</th><th>Your expenses (info)</th><th>Your profit (info)</th></tr></thead>
                <tbody>{e.rows.map((r,i)=>(<tr key={i}>
                  <td>{r.quarter}</td><td>{signedMoney(r.income, '£')}</td><td>{signedMoney(r.expenses, '£')}</td><td><strong>{signedMoney(r.profit, '£')}</strong></td>
                  <td style={{color:'var(--text-secondary)'}}>{e.shareMissing ? '—' : signedMoney(r.yourIncome, '£')}</td>
                  <td style={{color:'var(--text-secondary)'}}>{e.shareMissing ? '—' : signedMoney(r.yourExpenses, '£')}</td>
                  <td style={{color:'var(--text-secondary)'}}>{e.shareMissing ? '—' : signedMoney(r.yourProfit, '£')}</td>
                </tr>))}</tbody>
                {e.rows.length>1 && <tfoot><tr style={{fontWeight:700,background:'var(--bg-light)'}}>
                  <td>Total (100%)</td><td>{signedMoney(e.totals.income, '£')}</td><td>{signedMoney(e.totals.expenses, '£')}</td><td>{signedMoney(e.totals.profit, '£')}</td>
                  <td>{e.shareMissing ? '—' : signedMoney(e.totals.yourIncome, '£')}</td><td>{e.shareMissing ? '—' : signedMoney(e.totals.yourExpenses, '£')}</td><td>{e.shareMissing ? '—' : signedMoney(e.totals.yourProfit, '£')}</td>
                </tr></tfoot>}
              </table></div>
            </div>
          ))}
        </div>
      )}
      <button className="btn btn-success" style={{marginTop:'1rem'}} onClick={()=>exportMTDExcel(mtdReportData, mtdTaxYear, mtdQuarter)}>
        <i className="ti ti-file-spreadsheet" /> Export to Excel
      </button>
    </div></div>);
  }

  if (activeReport === 'company') {
    const yr = parseInt(companyReportYear);
    const selectedCompany = companies.find(c => String(c.id) === selectedCompanyId);
    const taxYearStart = selectedCompany?.tax_year_start || '10-01';
    const { start: yearStart, end: yearEnd } = getCompanyYearRange(taxYearStart, yr);
    const periodLabel = `${yearStart.toLocaleDateString('en-GB',{day:'numeric',month:'long'})} ${yearStart.getFullYear()} – ${yearEnd.toLocaleDateString('en-GB',{day:'numeric',month:'long'})} ${yearEnd.getFullYear()}`;
    const compPropIds = new Set(companyReportData.map(p => p.id));
    const compProps = activeProps.filter(p => compPropIds.has(p.id));
    // BUG-005c: the paid overheads of this company and period, and totals worked out one currency at a time
    // (euro and pound amounts are never added together; nothing is converted)
    const periodOverheads = companyExpenses
      .filter(e => (e.paid===true||e.paid==='true') && e.date && new Date(e.date)>=yearStart && new Date(e.date)<=yearEnd)
      .filter(e => selectedCompanyId === 'unassigned' ? !(e.company_id||e.companyId) : String(e.company_id||e.companyId) === selectedCompanyId);
    const compGroups = companyTotalsByCurrency({ rows: companyReportData, overheads: periodOverheads, companies });
    const multiCompCur = compGroups.length > 1;
    const companyLabel = selectedCompany ? selectedCompany.name : 'Unassigned Company Properties';

    const exportCompanyExcel = () => {
      const wb = XLSX.utils.book_new();
      const today = xlsDate();
      const summaryRows = [
        [`casavello COMPANY PROPERTY REPORT (CT600 Reference) — ${companyLabel}`, '', '', '', ''],
        [`Period: ${periodLabel}`, '', '', '', ''],
        [`Generated: ${today}`, '', '', '', ''],
        ['IMPORTANT: For organisational purposes only. Not tax advice. Consult a qualified accountant before submitting CT600.', '', '', '', ''],
        ['Hibernian Technologies Limited accepts no liability for errors or omissions in these figures.', '', '', '', ''],
        ['All finance costs including mortgage interest are treated as fully deductible for company ownership.', '', '', '', ''],
        [],
        ['Each currency is totalled on its own. casavello does not convert between currencies.', '', '', '', ''],
        [],
        ['Property', 'Currency', 'Rooms', 'Occupied', 'Income', 'Expenses', 'Net Profit'],
        ...companyReportData.map(p => [p.address, normCurrency(p.currency), p.rooms.length, p.occupiedRooms.length, fmt(p.income), fmt(p.expenses), fmt(p.profit)]),
        [],
        ...compGroups.flatMap(g => [
          [`PROPERTY TOTALS (${g.currency})`, g.currency, '', '', fmt(g.inc), fmt(g.exp), fmt(g.profit)],
          [`Company Overhead Expenses (${g.currency})`, g.currency, '', '', '', fmt(g.overhead), fmt(-g.overhead)],
          [`NET PROFIT after overheads (${g.currency})`, g.currency, '', '', '', '', fmt(g.net)],
          [],
        ]),
      ];
      addSheet(wb, 'Company Summary', summaryRows, [40,10,8,10,14,14,14]);
      // Income detail
      const incRows = [
        [`INCOME DETAIL — ${companyLabel} — ${periodLabel}`, '', '', '', '', ''],
        [],
        ['Month', 'Property', 'Currency', 'Room / Tenant', 'Amount'],
        ...payments
          .filter(p => {
            if (!compProps.some(fp=>fp.id==(p.propertyId||p.property_id))) return false;
            if (!p.month) return false;
            const [y,m] = p.month.split('-').map(Number);
            const dt = new Date(y,m-1,15);
            return dt>=yearStart && dt<=yearEnd;
          })
          .sort((a,b)=>(b.month||'').localeCompare(a.month||''))
          .map(p => {
            const prop = properties.find(pr=>pr.id==(p.propertyId||p.property_id));
            const room = (prop?.rooms||[]).find(r=>String(r.id)===String(p.roomId||p.room_id));
            return [p.month, prop?.address||'Unknown', normCurrency(prop?.currency), room?`${room.roomName}${room.tenantName?' – '+room.tenantName:''}` : '', fmt(p.amount)];
          }),
        [],
        ...compGroups.map(g => [`TOTAL (${g.currency})`, '', g.currency, '', fmt(g.inc)]),
      ];
      addSheet(wb, 'Income Detail', incRows, [12,40,10,25,12]);
      // Expense detail
      const expRows = [
        [`EXPENSE DETAIL — ${companyLabel} — ${periodLabel}`, '', '', '', '', ''],
        [],
        ['Date', 'Property', 'Currency', 'Category', 'Amount', 'Paid', 'Description'],
        ...expenses
          .filter(e => compProps.some(fp=>fp.id==(e.propertyId||e.property_id)) && e.date && new Date(e.date)>=yearStart && new Date(e.date)<=yearEnd)
          .sort((a,b)=>(b.date||'').localeCompare(a.date||''))
          .map(e => {
            const prop = properties.find(p=>p.id==(e.propertyId||e.property_id));
            return [e.date, prop?.address||'Unknown', normCurrency(prop?.currency), e.category||'', fmt(e.amount), (e.paid===true||e.paid==='true')?'Yes':'No', e.description||''];
          }),
        [],
        ...compGroups.map(g => [`TOTAL PAID (${g.currency})`, '', g.currency, '', fmt(g.exp), '', '']),
      ];
      addSheet(wb, 'Expense Detail', expRows, [12,40,10,18,12,8,30]);
      // Company overhead expenses
      const overheadRows = [
        [`COMPANY OVERHEAD EXPENSES — ${companyLabel} — ${periodLabel}`, '', '', ''],
        [],
        ['Date', 'Category', 'Currency', 'Amount', 'Paid', 'Description'],
        ...companyExpenses
          .filter(e => e.date && new Date(e.date)>=yearStart && new Date(e.date)<=yearEnd)
          .filter(e => selectedCompanyId === 'unassigned' ? !(e.company_id||e.companyId) : String(e.company_id||e.companyId) === selectedCompanyId)
          .sort((a,b)=>(b.date||'').localeCompare(a.date||''))
          .map(e => [e.date, e.category||'', companyExpenseCurrency(e, companies), fmt(e.amount), (e.paid===true||e.paid==='true')?'Yes':'No', e.description||'']),
        [],
        ...totalRowsByCurrency(periodOverheads.map(e => ({ currency: companyExpenseCurrency(e, companies), amount: e.amount })), 6, 3, 'TOTAL PAID'),
      ];
      addSheet(wb, 'Overhead Expenses', overheadRows, [12,25,10,12,8,40]);
      XLSX.writeFile(wb, `casavello-Company-Report-${companyLabel.replace(/[^a-z0-9]+/gi,'-')}-${yr}.xlsx`);
    };

    return (<div><BackBtn /><div className="card">
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'1rem',flexWrap:'wrap',gap:'0.75rem'}}>
        <h2 style={{margin:0}}>🏢 Company Property Report</h2>
        <div style={{display:'flex',gap:'0.75rem',alignItems:'center',flexWrap:'wrap'}}>
          <select value={selectedCompanyId} onChange={e=>setSelectedCompanyId(e.target.value)} style={{padding:'0.5rem',borderRadius:'0.5rem',border:'1px solid var(--border-light)',fontWeight:600}}>
            {companies.map(c => <option key={c.id} value={String(c.id)}>{c.name}</option>)}
            <option value="unassigned">Unassigned company properties</option>
          </select>
          <select value={companyReportYear} onChange={e=>setCompanyReportYear(e.target.value)} style={{padding:'0.5rem',borderRadius:'0.5rem',border:'1px solid var(--border-light)'}}>
            {years.map(y=><option key={y}>{y}</option>)}
          </select>
        </div>
      </div>
      {companies.length === 0 && (
        <div style={{background:'#fef3c7',border:'1px solid #fde047',borderRadius:'0.5rem',padding:'0.85rem 1rem',marginBottom:'1rem',fontSize:'0.85rem',color:'#92400e'}}>
          ⚠️ No companies set up yet — this report is showing company-owned properties with no specific company assigned, using a default {periodLabel} accounting year. <a href="/companies" style={{color:'#92400e',fontWeight:600}}>Set up a company</a> to use its own accounting year end and see it reflected here.
        </div>
      )}
      <div style={{background:'#fef2f2',border:'1px solid #fca5a5',borderRadius:'0.5rem',padding:'1rem',marginBottom:'1rem',fontSize:'0.875rem',color:'#991b1b'}}>
        <strong>⚠️ Important Disclaimer — Company Properties</strong><br/>
        These figures are produced by casavello for organisational purposes only and do not constitute tax advice for HMRC CT600 purposes.
        casavello is not a tax adviser. You must consult a qualified accountant before submitting any CT600 or corporation tax return.
        Hibernian Technologies Limited accepts no liability for errors or omissions in these figures.
      </div>
      <div style={{background:'#eff6ff',border:'1px solid #bfdbfe',borderRadius:'0.5rem',padding:'1rem',marginBottom:'1.5rem',fontSize:'0.8rem',color:'#1e40af'}}>
        <strong>📋 {companyLabel} — Accounting Year: {periodLabel}</strong> — Company-owned properties only (CT600 reference).<br/>
        All finance costs including mortgage interest are treated as fully deductible business expenses for company ownership.
        {selectedCompany ? ' This is your company\'s own accounting year end, set on the Companies page.' : ' Set up a company with its correct accounting year end on the Companies page for a fully accurate period.'}
      </div>
      {companyReportData.length===0 ? (
        <div className="empty-state"><span>🏢</span><h3>No Company Properties</h3><p>Mark properties as company-owned to see them here</p></div>
      ) : (<>
        {multiCompCur && <div style={{background:'#eff6ff',border:'1px solid #bfdbfe',borderRadius:'0.5rem',padding:'0.6rem 0.9rem',marginBottom:'1rem',fontSize:'0.8rem',color:'#1e40af'}}>Each currency is totalled on its own. casavello never adds euro and pound amounts together and does not convert between currencies.</div>}
        <div className="stats-grid" style={{marginBottom:'1.5rem'}}>
          <div className="stat-card"><div className="stat-label">Company Properties</div><div className="stat-value">{companyReportData.length}</div></div>
        </div>
        {compGroups.map(g => (<div key={g.currency}>
        {multiCompCur && <h3 style={{margin:'0 0 0.75rem'}}>{g.currency} ({g.symbol})</h3>}
        <div className="stats-grid" style={{marginBottom:'1.5rem'}}>
          <div className="stat-card"><div className="stat-label">Total Rental Income{multiCompCur ? ` (${g.currency})` : ''}</div><div className="stat-value positive">{formatMoney(g.inc, g.currency)}</div></div>
          <div className="stat-card"><div className="stat-label">Total Property Expenses{multiCompCur ? ` (${g.currency})` : ''}</div><div className="stat-value negative">{formatMoney(g.exp, g.currency)}</div></div>
          <div className="stat-card"><div className="stat-label">Company Overhead Expenses{multiCompCur ? ` (${g.currency})` : ''}</div><div className="stat-value negative">{formatMoney(g.overhead, g.currency)}</div></div>
          <div className="stat-card"><div className="stat-label">Gross Property Profit{multiCompCur ? ` (${g.currency})` : ''}</div><div className={`stat-value ${g.profit>=0?'positive':'negative'}`}>{formatMoney(g.profit, g.currency)}</div></div>
          <div className="stat-card"><div className="stat-label">Net Profit (after overheads){multiCompCur ? ` (${g.currency})` : ''}</div><div className={`stat-value ${g.net>=0?'positive':'negative'}`}>{formatMoney(g.net, g.currency)}</div></div>
        </div>
        </div>))}
        <div className="table-container"><table className="table">
          <thead><tr><th>Property</th><th>Currency</th><th>Rooms</th><th>Occupied</th><th>Income</th><th>Expenses</th><th>Net Profit</th></tr></thead>
          <tbody>{companyReportData.map(p=>(<tr key={p.id}>
            <td><strong>{p.address}</strong></td>
            <td>{p.currency||'GBP'}</td>
            <td>{p.rooms.length}</td>
            <td>{p.occupiedRooms.length}</td>
            <td style={{color:'var(--success)',fontWeight:600}}>{formatMoney(p.income, p.currency)}</td>
            <td style={{color:'var(--danger)',fontWeight:600}}>{formatMoney(p.expenses, p.currency)}</td>
            <td style={{color:p.profit>=0?'var(--success)':'var(--danger)',fontWeight:700}}>{formatMoney(p.profit, p.currency)}</td>
          </tr>))}</tbody>
          <tfoot>
            {compGroups.map(g => (<React.Fragment key={g.currency}>
            <tr style={{fontWeight:700,background:'var(--bg-light)'}}>
              <td colSpan="4">Property Totals{multiCompCur ? ` (${g.currency})` : ''}</td>
              <td style={{color:'var(--success)'}}>{formatMoney(g.inc, g.currency)}</td>
              <td style={{color:'var(--danger)'}}>{formatMoney(g.exp, g.currency)}</td>
              <td style={{color:g.profit>=0?'var(--success)':'var(--danger)'}}>{formatMoney(g.profit, g.currency)}</td>
            </tr>
            {g.overhead > 0 && (
              <tr style={{fontWeight:600,color:'var(--danger)'}}>
                <td colSpan="5">Company Overhead Expenses{multiCompCur ? ` (${g.currency})` : ''}</td>
                <td>{formatMoney(g.overhead, g.currency)}</td>
                <td>{formatMoney(-g.overhead, g.currency)}</td>
              </tr>
            )}
            <tr style={{fontWeight:700,background:'#f0fdf4'}}>
              <td colSpan="6">Net Profit (after all expenses &amp; overheads){multiCompCur ? ` (${g.currency})` : ''}</td>
              <td style={{color:g.net>=0?'var(--success)':'var(--danger)',fontSize:'1.1rem'}}>{formatMoney(g.net, g.currency)}</td>
            </tr>
            </React.Fragment>))}
          </tfoot>
        </table></div>
        <button className="btn btn-success" style={{marginTop:'1rem'}} onClick={exportCompanyExcel}>
          <i className="ti ti-file-spreadsheet" /> Export to Excel
        </button>
      </>)}
    </div></div>);
  }

  // ─── RENT ROLL ──────────────────────────────────────────────────────────────
  if (activeReport === 'rentroll') {
    const today = new Date();
    const todayStr = today.toISOString().substring(0,10);
    // RENTROLL-001: Was hardcoded to always show the current month, making it
    // impossible to check which tenants were unpaid in a PREVIOUS month —
    // exactly the scenario this report is most useful for. Now driven by
    // rentRollMonth, defaulting to the current month but selectable.
    const selectedMonth = rentRollMonth;
    const isCurrentMonth = selectedMonth === todayStr.substring(0,7);
    const [selYear, selMonthNum] = selectedMonth.split('-').map(Number);
    const selectedMonthLabel = new Date(selYear, selMonthNum-1, 1).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });

    // Build a list of selectable months — 12 months back through 1 month ahead
    const monthOptions = [];
    for (let i = -1; i <= 12; i++) {
      const d = new Date(today.getFullYear(), today.getMonth() - i, 1);
      const val = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`;
      monthOptions.push({ value: val, label: d.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' }) });
    }

    const rrProps = activeProps
      .filter(p => ownershipMatches(p, rentRollOwnership))
      .filter(p => rentRollCurrency === 'all' || (p.currency||'GBP') === rentRollCurrency);
    const rows = [];
    rrProps.forEach(prop => {
      // BUG FIX: this iterated prop.rooms directly — for a BTL/Single Let
      // property (zero rooms by design), the loop body never ran at all,
      // meaning the property was completely ABSENT from Rent Roll, not
      // just showing £0 rent. Constructs one synthetic "room" representing
      // the whole property for non-HMO, sourced from its active tenant —
      // same approach Arrears already uses correctly (see its fakeRoom).
      // id is set to '' deliberately: that's what a non-HMO payment's own
      // room_id actually holds (rooms were removed from those properties
      // entirely), so the existing payment-matching logic below still
      // works unmodified rather than needing its own non-HMO branch.
      const isPropHMO = String(prop.property_type||prop.propertyType||'hmo').toLowerCase() === 'hmo';
      const roomsToIterate = isPropHMO ? (prop.rooms||[]) : (() => {
        const activeTenant = (tenants||[]).find(t => String(t.property_id||t.propertyId)===String(prop.id) && t.status==='active');
        return [{
          id: '',
          roomName: 'Full Property',
          tenantName: activeTenant ? `${activeTenant.first_name||''} ${activeTenant.last_name||''}`.trim() : '',
          rentAmount: activeTenant ? parseFloat(activeTenant.rent_amount||0) : 0,
          status: activeTenant ? 'occupied' : 'vacant',
          leaseStart: activeTenant?.lease_start ? String(activeTenant.lease_start).substring(0,10) : null,
          leaseEnd: activeTenant?.lease_end ? String(activeTenant.lease_end).substring(0,10) : null,
        }];
      })();
      roomsToIterate.forEach(room => {
        // Only include rooms that were actually let out by the selected month
        // (skip rooms whose lease hadn't started yet, for historical accuracy)
        if (room.leaseStart && room.leaseStart.substring(0,7) > selectedMonth) return;
        // BUG FIX: this grabbed the most recent payment EVER made in the
        // room, sorted by month, with no regard for who actually made it —
        // exactly the same room-not-tenancy pattern found everywhere else
        // today. A new tenant with no payments of their own yet showed the
        // PREVIOUS tenant's last payment as if it were theirs, implying
        // they'd already paid when they hadn't. Now bounded to payments
        // dated on/after this room's current lease_start (just fixed above
        // to be kept reliably in sync) — a payment made before the current
        // tenancy began cannot belong to the current tenant, regardless of
        // which room it's tagged against.
        // BUG FIX: relied on a non-HMO payment's room_id happening to equal
        // the synthetic room's id ('') via loose equality. Found a
        // pre-existing backend comment confirming some BTL/single-let
        // payments actually store room_id as real SQL NULL, not empty
        // string — and null == '' is false in JS, so those payments never
        // matched at all, meaning "last paid" showed nothing. Explicit now:
        // match by property alone for non-HMO, ignoring room_id entirely
        // regardless of whether it's null, undefined, or empty string in
        // the actual record.
        const lastPayment = payments
          .filter(p => (p.propertyId||p.property_id)==prop.id && (isPropHMO ? (p.roomId||p.room_id)==room.id : true))
          .filter(p => !room.leaseStart || (p.paidDate||p.paid_date||'').substring(0,10) >= room.leaseStart.substring(0,10))
          .sort((a,b) => (b.month||'').localeCompare(a.month||''))[0];
        // Same fix as lastPayment above — a payment from the previous
        // tenant landing in the selected month by coincidence must not be
        // read as this tenant having already paid it.
        const paidSelectedMonth = payments.some(p =>
          (p.propertyId||p.property_id)==prop.id && (isPropHMO ? (p.roomId||p.room_id)==room.id : true) && p.month===selectedMonth
          && (!room.leaseStart || (p.paidDate||p.paid_date||'').substring(0,10) >= room.leaseStart.substring(0,10))
        );
        rows.push({ prop, room, lastPayment, paidSelectedMonth });
      });
    });
    const occupied = rows.filter(r => r.room.status==='occupied');
    const vacant = rows.filter(r => r.room.status!=='occupied');
    // BUG FIX: was summing rent across all currencies then showing one
    // (always GBP) symbol — meaningless once GBP and EUR properties are both
    // in the filtered view. Grouped by currency, same pattern used in
    // Transactions.js's currency fix.
    const rentByCurrency = {};
    occupied.forEach(r => {
      const cur = r.prop.currency || 'GBP';
      rentByCurrency[cur] = (rentByCurrency[cur] || 0) + parseFloat(r.room.rentAmount || 0);
    });
    const rentCurrencies = Object.keys(rentByCurrency);
    const FilterBar = () => (
      <div style={{display:'flex',gap:'0.75rem',alignItems:'center',flexWrap:'wrap'}}>
        <select value={rentRollMonth} onChange={e=>setRentRollMonth(e.target.value)} style={{padding:'0.5rem',borderRadius:'0.5rem',border:'1px solid var(--border-light)',fontWeight:600}}>
          {monthOptions.map(m => <option key={m.value} value={m.value}>{m.label}{m.value===todayStr.substring(0,7)?' (current)':''}</option>)}
        </select>
        <select value={rentRollOwnership} onChange={e=>setRentRollOwnership(e.target.value)} style={{padding:'0.5rem',borderRadius:'0.5rem',border:'1px solid var(--border-light)'}}>
          <option value="all">All Ownership</option><option value="personal">Personal &amp; joint</option><option value="company">Company</option>
        </select>
        {availableCurrencies.length > 1 && <select value={rentRollCurrency} onChange={e=>setRentRollCurrency(e.target.value)} style={{padding:'0.5rem',borderRadius:'0.5rem',border:'1px solid var(--border-light)'}}>
          <option value="all">All Currencies</option>{availableCurrencies.map(c=><option key={c}>{c}</option>)}
        </select>}
      </div>
    );
    const exportRentRoll = () => {
      const wb = XLSX.utils.book_new();
      const sheetRows = [
        ['RENT ROLL REPORT', '', '', '', '', '', '', ''],
        [`Period: ${selectedMonthLabel}`, '', '', '', '', '', '', ''],
        [`Generated: ${xlsDate()}`, '', '', '', '', '', '', ''],
        [],
        ['Property', 'Ownership', 'Currency', 'Room', 'Tenant', 'Rent/mo', 'Lease Start', 'Lease End', 'Status', `Paid ${selectedMonthLabel}`, 'Last Payment Month'],
        ...rows.map(r => [r.prop.address, r.prop.ownership||'', r.prop.currency||'GBP', r.room.roomName||'', r.room.tenantName||'—', fmt(r.room.rentAmount||0), toShortDate(r.room.leaseStart), toShortDate(r.room.leaseEnd), r.room.status||'', r.paidSelectedMonth?'Yes':'No', r.lastPayment?.month||'—']),
        [],
        ...rentCurrencies.map(cur => [`TOTAL OCCUPIED ROOMS (${cur})`, '', '', '', '', rentByCurrency[cur].toFixed(2), '', '', '', '', '']),
      ];
      addSheet(wb, 'Rent Roll', sheetRows, [40,12,10,15,20,10,12,12,10,16,16]);
      XLSX.writeFile(wb, `Rent-Roll-${selectedMonth}-${xlsDate()}.xlsx`);
    };
    return (<div><BackBtn /><div className="card">
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'1rem',flexWrap:'wrap',gap:'0.75rem'}}>
        <h2 style={{margin:0}}>📋 Rent Roll — {selectedMonthLabel}{!isCurrentMonth && <span style={{fontSize:'0.75rem',fontWeight:400,color:'var(--text-secondary)',marginLeft:'0.5rem'}}>(historical)</span>}</h2>
        <FilterBar />
      </div>
      <div className="stats-grid" style={{marginBottom:'1.5rem'}}>
        <div className="stat-card"><div className="stat-label">Total Rooms</div><div className="stat-value">{rows.length}</div></div>
        <div className="stat-card"><div className="stat-label">Occupied</div><div className="stat-value positive">{occupied.length}</div></div>
        <div className="stat-card"><div className="stat-label">Vacant</div><div className="stat-value" style={{color:vacant.length>0?'var(--danger)':'var(--success)'}}>{vacant.length}</div></div>
        {rentCurrencies.length <= 1 ? (
          <div className="stat-card"><div className="stat-label">Monthly Rent Roll</div><div className="stat-value positive">{formatCurrency(rentCurrencies[0] ? rentByCurrency[rentCurrencies[0]] : 0, rentCurrencies[0] || 'GBP')}</div></div>
        ) : rentCurrencies.map(cur => (
          <div className="stat-card" key={cur}><div className="stat-label">Monthly Rent Roll ({cur})</div><div className="stat-value positive">{formatCurrency(rentByCurrency[cur], cur)}</div></div>
        ))}
        <div className="stat-card"><div className="stat-label">Paid {isCurrentMonth ? 'This Month' : `in ${selectedMonthLabel}`}</div><div className="stat-value positive">{occupied.filter(r=>r.paidSelectedMonth).length}</div></div>
        <div className="stat-card"><div className="stat-label">{isCurrentMonth ? 'Not Yet Paid' : 'Unpaid'}</div><div className="stat-value" style={{color:'var(--danger)'}}>{occupied.filter(r=>!r.paidSelectedMonth).length}</div></div>
      </div>
      <div className="table-container"><table className="table">
        <thead><tr><th>Property</th><th>Room</th><th>Tenant</th><th>Rent/mo</th><th>Lease Start</th><th>Lease End</th><th>Status</th><th>Paid {isCurrentMonth ? 'This Month' : selectedMonthLabel}</th><th>Last Payment</th></tr></thead>
        <tbody>{rows.map((r,i)=>(
          <tr key={i} style={{background:r.room.status!=='occupied'?'#fafafa':''}}>
            <td><strong>{r.prop.address}</strong><div style={{fontSize:'0.7rem',color:'var(--text-secondary)'}}>{r.prop.ownership} · {r.prop.currency||'GBP'}</div></td>
            <td>{r.room.roomName||'—'}</td>
            <td>{r.room.tenantName||<span style={{color:'var(--text-secondary)'}}>Vacant</span>}</td>
            <td style={{fontWeight:600}}>{r.room.rentAmount?formatCurrency(r.room.rentAmount, r.prop.currency||'GBP'):'—'}</td>
            <td>{toShortDate(r.room.leaseStart)||'—'}</td>
            <td>{toShortDate(r.room.leaseEnd)||'—'}</td>
            <td><span className={`badge ${r.room.status==='occupied'?'badge-success':'badge-secondary'}`}>{r.room.status||'vacant'}</span></td>
            <td>{r.room.status==='occupied'?(<span className={`badge ${r.paidSelectedMonth?'badge-success':'badge-danger'}`}>{r.paidSelectedMonth?'✓ Paid':'✗ Unpaid'}</span>):'—'}</td>
            <td style={{color:'var(--text-secondary)',fontSize:'0.875rem'}}>{r.lastPayment?.month||'—'}</td>
          </tr>
        ))}</tbody>
      </table></div>
      <button className="btn btn-success" style={{marginTop:'1rem'}} onClick={exportRentRoll}><i className="ti ti-file-spreadsheet" /> Export to Excel</button>
    </div></div>);
  }

  // ─── PORTFOLIO HEALTH CHECK ─────────────────────────────────────────────────
  if (activeReport === 'healthcheck') {
    const today = new Date();
    const currentMonthStr = today.toISOString().substring(0,7);
    // Only fully-closed months can be reviewed — never the current month
    const monthOptions = [];
    for (let i = 1; i <= 18; i++) {
      const d = new Date(today.getFullYear(), today.getMonth() - i, 1);
      const val = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`;
      monthOptions.push({ value: val, label: d.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' }) });
    }
    // Ensure the selected range never includes the current (open) month
    const safeFrom = hcFromMonth >= currentMonthStr ? monthOptions[monthOptions.length-1].value : hcFromMonth;
    const safeTo   = hcToMonth   >= currentMonthStr ? monthOptions[0].value : hcToMonth;

    const EXCLUDED_CATEGORIES = ['Repairs & Maintenance', 'Void Period Costs', 'Other Allowable Expenses'];
    // FEAT-006: capital costs are one-offs, never recurring, so the health check does not look at them
    const isHealthCheckExcluded = (cat) => EXCLUDED_CATEGORIES.includes(cat) || isCapitalCategory(cat);

    const addMonths = (m, n) => { const [y,mo]=m.split('-').map(Number); const d=new Date(y,mo-1+n,1); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`; };
    const monthRange = (from, to) => { const out=[]; let m=from; let guard=0; while (m<=to && guard<60) { out.push(m); m=addMonths(m,1); guard++; } return out; };
    const reviewMonths = monthRange(safeFrom, safeTo);

    // Build per-bucket (property, or "Company Expenses") category histories
    const buckets = [
      ...activeProps.map(p => ({ key:`p${p.id}`, label:p.address, propertyId:p.id, currency:p.currency||'GBP', rows: expenses.filter(e => (e.propertyId||e.property_id)==p.id && (e.paid===true||e.paid==='true')) })),
      { key:'company', label:'Company Expenses', propertyId:null, currency:'GBP', rows: (companyExpenses||[]).filter(e => (e.paid===true||e.paid==='true')) },
    ].filter(b => b.rows.length > 0);

    const findings = [];
    buckets.forEach(bucket => {
      // category -> { [month]: { count, amount, descriptions: [] } }
      const catMonth = {};
      bucket.rows.forEach(e => {
        const cat = e.category || 'Uncategorised';
        if (isHealthCheckExcluded(cat)) return;
        const m = (e.date||'').substring(0,7);
        if (!m) return;
        catMonth[cat] = catMonth[cat] || {};
        catMonth[cat][m] = catMonth[cat][m] || { count:0, amount:0, descriptions:[] };
        catMonth[cat][m].count += 1;
        catMonth[cat][m].amount += parseFloat(e.amount||0);
        if (e.description) catMonth[cat][m].descriptions.push(e.description);
      });

      Object.keys(catMonth).forEach(cat => {
        reviewMonths.forEach(M => {
          const trailing6 = [1,2,3,4,5,6].map(n => addMonths(M,-n));
          const monthsPresentIn6 = trailing6.filter(m => catMonth[cat][m]?.count > 0);
          const twoBack = addMonths(M,-1), threeBack = addMonths(M,-2);
          const presentLast2 = catMonth[cat][twoBack]?.count > 0 && catMonth[cat][threeBack]?.count > 0;
          const established = monthsPresentIn6.length >= 3;
          const potentiallyRecurring = !established && presentLast2;
          if (!established && !potentiallyRecurring) return; // not enough history to judge this month

          const qualifyingMonths = established ? monthsPresentIn6 : [twoBack, threeBack];
          const typicalCount  = qualifyingMonths.reduce((s,m)=>s+(catMonth[cat][m]?.count||0),0) / qualifyingMonths.length;
          const typicalAmount = qualifyingMonths.reduce((s,m)=>s+(catMonth[cat][m]?.amount||0),0) / qualifyingMonths.length;
          const actual = catMonth[cat][M] || { count:0, amount:0, descriptions:[] };
          const expectedCount = Math.max(1, Math.round(typicalCount));

          // Sample description — most recent one found in the qualifying baseline months
          const sampleDescription = [...qualifyingMonths].reverse()
            .map(m => catMonth[cat][m]?.descriptions?.[0])
            .find(d => d) || null;

          if (actual.count < expectedCount) {
            findings.push({
              type: 'missing', bucketKey: bucket.key, bucket: bucket.label, propertyId: bucket.propertyId,
              category: cat, month: M,
              expected: expectedCount, actual: actual.count,
              typicalAmount: typicalAmount.toFixed(2), currency: bucket.currency,
              confidence: established ? 'established pattern' : 'recently recurring',
              sampleDescription,
            });
          } else if (actual.amount > 0 && typicalAmount > 0 && Math.abs(actual.amount - typicalAmount) / typicalAmount > 0.4) {
            findings.push({
              type: 'unusual_amount', bucketKey: bucket.key, bucket: bucket.label, propertyId: bucket.propertyId,
              category: cat, month: M,
              typicalAmount: typicalAmount.toFixed(2), actualAmount: actual.amount.toFixed(2), currency: bucket.currency,
              confidence: established ? 'established pattern' : 'recently recurring',
              sampleDescription: actual.descriptions?.[0] || sampleDescription,
            });
          }
        });
      });
    });

    // Condensed rent-gap summary (links through to Arrears Report for detail)
    let rentGapCount = 0;
    activeProps.forEach(prop => {
      (prop.rooms||[]).filter(r => r.status==='occupied').forEach(room => {
        reviewMonths.forEach(M => {
          const paid = payments.some(p => (p.propertyId||p.property_id)==prop.id && (p.roomId||p.room_id)==room.id && p.month===M);
          if (!paid) rentGapCount++;
        });
      });
    });

    const monthLabel = (m) => { const [y,mo]=m.split('-').map(Number); return new Date(y,mo-1,1).toLocaleDateString('en-GB',{month:'short',year:'numeric'}); };
    const rangeLabel = `${monthLabel(safeFrom)} – ${monthLabel(safeTo)}`;

    const handleGenerateNarrative = async () => {
      setHcNarrativeLoading(true); setHcNarrativeError(''); setHcNarrative('');
      try {
        const BASE  = API_URL;
        const token = localStorage.getItem('token');
        const res = await fetch(`${BASE}/api/ai/portfolio-narrative`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ findings: [...activeFindings, ...(rentGapCount>0?[{type:'rent_gaps', count:rentGapCount}]:[])] }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Failed to generate summary');
        setHcNarrative(data.narrative);
      } catch (err) { setHcNarrativeError(err.message || 'Failed to generate summary'); }
      finally { setHcNarrativeLoading(false); }
    };

    const isIgnored = (f) => hcIgnores.some(ig => ig.bucket_key === f.bucketKey && ig.category === f.category);
    const activeFindings   = findings.filter(f => !isIgnored(f));
    const ignoredFindings  = findings.filter(f => isIgnored(f));

    const handleIgnore = async (f) => {
      const BASE  = API_URL;
      const token = localStorage.getItem('token');
      try {
        const res = await fetch(`${BASE}/api/reports/health-check-ignores`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ bucket_key: f.bucketKey, category: f.category }),
        });
        const data = await res.json();
        setHcIgnores(prev => [...prev, data]);
      } catch { /* non-critical — worst case the finding reappears next time */ }
    };

    const handleUnignore = async (bucketKey, category) => {
      const match = hcIgnores.find(ig => ig.bucket_key === bucketKey && ig.category === category);
      if (!match?.id) return;
      const BASE  = API_URL;
      const token = localStorage.getItem('token');
      try {
        await fetch(`${BASE}/api/reports/health-check-ignores/${match.id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
        setHcIgnores(prev => prev.filter(ig => ig.id !== match.id));
      } catch { /* non-critical */ }
    };

    const findingsByBucket = {};
    activeFindings.forEach(f => { findingsByBucket[f.bucket] = findingsByBucket[f.bucket] || []; findingsByBucket[f.bucket].push(f); });

    return (<div><BackBtn /><div className="card">
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'1rem',flexWrap:'wrap',gap:'0.75rem'}}>
        <h2 style={{margin:0}}>🩺 Portfolio Health Check — {rangeLabel}</h2>
        <div style={{display:'flex',gap:'0.75rem',alignItems:'center',flexWrap:'wrap'}}>
          <span style={{fontSize:'0.8rem',color:'var(--text-secondary)'}}>From</span>
          <select value={safeFrom} onChange={e=>setHcFromMonth(e.target.value)} style={{padding:'0.5rem',borderRadius:'0.5rem',border:'1px solid var(--border-light)'}}>
            {monthOptions.map(m=><option key={m.value} value={m.value}>{m.label}</option>)}
          </select>
          <span style={{fontSize:'0.8rem',color:'var(--text-secondary)'}}>To</span>
          <select value={safeTo} onChange={e=>setHcToMonth(e.target.value)} style={{padding:'0.5rem',borderRadius:'0.5rem',border:'1px solid var(--border-light)'}}>
            {monthOptions.map(m=><option key={m.value} value={m.value}>{m.label}</option>)}
          </select>
        </div>
      </div>

      <div style={{background:'#eff6ff',border:'1px solid #bfdbfe',borderRadius:'0.5rem',padding:'0.85rem 1rem',marginBottom:'1.5rem',fontSize:'0.8rem',color:'#1e40af'}}>
        ℹ️ Findings are calculated from your own recorded expenses — no assumptions are made beyond your own data. Repairs & Maintenance, Void Period Costs and Other Allowable Expenses are excluded, since irregular spend there is normal. A category needs at least 3 of the last 6 months, or the last 2 consecutive months, before it's treated as recurring.
      </div>

      <div className="stats-grid" style={{marginBottom:'1.5rem'}}>
        <div className="stat-card"><div className="stat-label">Months Reviewed</div><div className="stat-value">{reviewMonths.length}</div></div>
        <div className="stat-card"><div className="stat-label">Missing Recurring Costs</div><div className="stat-value" style={{color:activeFindings.filter(f=>f.type==='missing').length>0?'var(--danger)':'var(--success)'}}>{activeFindings.filter(f=>f.type==='missing').length}</div></div>
        <div className="stat-card"><div className="stat-label">Unusual Amounts</div><div className="stat-value" style={{color:activeFindings.filter(f=>f.type==='unusual_amount').length>0?'var(--danger)':'var(--success)'}}>{activeFindings.filter(f=>f.type==='unusual_amount').length}</div></div>
        <div className="stat-card"><div className="stat-label">Rent Payment Gaps</div><div className="stat-value" style={{color:rentGapCount>0?'var(--danger)':'var(--success)'}}>{rentGapCount}</div></div>
      </div>

      {/* AI Summary */}
      <div className="card" style={{marginBottom:'1.5rem',background:'#faf5ff',border:'1px solid #e9d5ff'}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'0.75rem'}}>
          <span style={{fontWeight:700,color:'#6b21a8'}}>✨ AI Summary</span>
          <button className="btn btn-secondary" style={{fontSize:'0.8rem'}} onClick={handleGenerateNarrative} disabled={hcNarrativeLoading}>
            {hcNarrativeLoading ? 'Thinking…' : (hcNarrative ? '🔄 Regenerate' : '✨ Generate Summary')}
          </button>
        </div>
        {hcNarrativeError && <div className="alert alert-danger" style={{fontSize:'0.85rem'}}>{hcNarrativeError}</div>}
        {hcNarrative ? (
          <p style={{margin:0,fontSize:'0.9rem',lineHeight:1.6,color:'#581c87'}}>{hcNarrative}</p>
        ) : (
          <p style={{margin:0,fontSize:'0.85rem',color:'var(--text-secondary)'}}>Click Generate Summary for a plain-English readout of the findings below — the detailed list is always available regardless.</p>
        )}
      </div>

      {/* Rent gaps — condensed, links to Arrears Report */}
      {rentGapCount > 0 && (
        <div style={{background:'#fef2f2',border:'1px solid #fca5a5',borderRadius:'0.5rem',padding:'0.85rem 1rem',marginBottom:'1.5rem',fontSize:'0.875rem',color:'#991b1b',display:'flex',justifyContent:'space-between',alignItems:'center',flexWrap:'wrap',gap:'0.75rem'}}>
          <span>⚠️ {rentGapCount} room-month{rentGapCount!==1?'s':''} with no rent payment recorded across {rangeLabel}.</span>
          <button className="btn btn-secondary" style={{fontSize:'0.8rem'}} onClick={()=>setActiveReport('arrears')}>View Arrears Report →</button>
        </div>
      )}

      {/* Findings by property */}
      {Object.keys(findingsByBucket).length === 0 ? (
        <div className="empty-state"><span>✅</span><h3>No gaps or anomalies found</h3><p>Recurring costs look consistent across {rangeLabel}.</p></div>
      ) : (
        Object.entries(findingsByBucket).map(([bucketLabel, bucketFindings]) => (
          <div key={bucketLabel} className="card" style={{marginBottom:'1rem'}}>
            <div style={{fontWeight:700,marginBottom:'0.75rem'}}>{bucketLabel}</div>
            <div className="table-container"><table className="table">
              <thead><tr><th>Month</th><th>Category</th><th>Issue</th><th>Detail</th><th>Description</th><th>Basis</th><th></th></tr></thead>
              <tbody>{bucketFindings.sort((a,b)=>a.month.localeCompare(b.month)).map((f,i)=>{
                const sym = f.currency==='EUR'?'€':'£';
                return (
                  <tr key={i}>
                    <td>{monthLabel(f.month)}</td>
                    <td>{f.category}</td>
                    <td>{f.type==='missing' ? <span className="badge badge-danger">Missing</span> : <span className="badge" style={{background:'#fef3c7',color:'#92400e'}}>Unusual Amount</span>}</td>
                    <td style={{fontSize:'0.85rem'}}>
                      {f.type==='missing'
                        ? <>Expected {f.expected} entr{f.expected===1?'y':'ies'} (~{sym}{f.typicalAmount} typical), found {f.actual}</>
                        : <>{sym}{f.actualAmount} recorded vs typical {sym}{f.typicalAmount}</>
                      }
                    </td>
                    <td style={{fontSize:'0.8rem',color:'var(--text-secondary)',fontStyle:f.sampleDescription?'normal':'italic',maxWidth:'220px'}}>
                      {f.sampleDescription
                        ? (f.type==='missing' ? <>Usually: "{f.sampleDescription}"</> : <>"{f.sampleDescription}"</>)
                        : 'No description on file'}
                    </td>
                    <td style={{fontSize:'0.75rem',color:'var(--text-secondary)'}}>{f.confidence}</td>
                    <td style={{whiteSpace:'nowrap'}}>
                      {f.type==='missing' && f.propertyId && (
                        <button
                          className="btn btn-secondary"
                          style={{fontSize:'0.7rem',padding:'0.2rem 0.5rem',marginRight:'0.4rem'}}
                          onClick={()=>navigate(`/properties?propertyId=${f.propertyId}&tab=expenses&addExpense=1&category=${encodeURIComponent(f.category)}`)}
                        >
                          + Add Expense
                        </button>
                      )}
                      <button className="btn btn-secondary" style={{fontSize:'0.7rem',padding:'0.2rem 0.5rem'}} onClick={()=>handleIgnore(f)} title="Don't flag this category for this property again">
                        Ignore
                      </button>
                    </td>
                  </tr>
                );
              })}</tbody>
            </table></div>
          </div>
        ))
      )}

      {/* Ignored findings — kept visible but tucked away, never silently lost */}
      {ignoredFindings.length > 0 && (
        <div className="card" style={{marginTop:'1rem'}}>
          <button
            onClick={()=>setHcShowIgnored(!hcShowIgnored)}
            style={{background:'none',border:'none',cursor:'pointer',fontWeight:600,color:'var(--text-secondary)',display:'flex',alignItems:'center',gap:'0.5rem',padding:0}}
          >
            {hcShowIgnored ? '▼' : '▶'} Ignored ({ignoredFindings.length})
          </button>
          {hcShowIgnored && (
            <div className="table-container" style={{marginTop:'0.75rem'}}><table className="table">
              <thead><tr><th>Property</th><th>Month</th><th>Category</th><th>Issue</th><th></th></tr></thead>
              <tbody>{ignoredFindings.map((f,i)=>(
                <tr key={i} style={{opacity:0.7}}>
                  <td>{f.bucket}</td>
                  <td>{monthLabel(f.month)}</td>
                  <td>{f.category}</td>
                  <td>{f.type==='missing' ? 'Missing' : 'Unusual Amount'}</td>
                  <td>
                    <button className="btn btn-secondary" style={{fontSize:'0.7rem',padding:'0.2rem 0.5rem'}} onClick={()=>handleUnignore(f.bucketKey, f.category)}>
                      Un-ignore
                    </button>
                  </td>
                </tr>
              ))}</tbody>
            </table></div>
          )}
        </div>
      )}
    </div></div>);
  }

  // ─── ARREARS REPORT ─────────────────────────────────────────────────────────
  if (activeReport === 'arrears') {
    const today = new Date();
    const currentMonth = today.toISOString().substring(0,7);
    // BUG FIX: The old logic only ever checked whether THIS month's rent was
    // paid — if it was, the tenant vanished from arrears entirely, even if
    // an earlier month was still unpaid. This meant (1) a missed month never
    // "carried over" once a later month was paid, and (2) a tenant 2 months
    // behind who caught up on just one of them incorrectly showed as fully
    // current instead of still owing 1 month. Fixed by checking every
    // expected month individually against actual payment records, rather
    // than only the most recent one.
    const monthsBetween = (start, end) => {
      const out = [];
      let [sy, sm] = start.split('-').map(Number);
      const [ey, em] = end.split('-').map(Number);
      let guard = 0;
      while ((sy < ey || (sy === ey && sm <= em)) && guard < 60) {
        out.push(`${sy}-${String(sm).padStart(2,'0')}`);
        sm++; if (sm > 12) { sm = 1; sy++; }
        guard++;
      }
      return out;
    };
    // BUG FIX (BACKDATE-003): This used to check a single CONTINUOUS range
    // from one lease_start to now — meaning a genuine void month between
    // two tenancies (nobody living there, nothing owed) was incorrectly
    // flagged as arrears, since the check never knew occupancy had a gap
    // in it at all. Fixed by deriving expected months from the ACTUAL
    // UNION of every real tenancy period recorded for the room, rather
    // than one assumed continuous stretch. A month nobody was tenanted for
    // is now correctly never "expected" in the first place — silently
    // excluded, not flagged, per Sam's call that a void isn't an arrear.
    // Also fixes a related gap: previously only currently-OCCUPIED rooms
    // were checked at all, so a room that's since gone vacant — where the
    // departed tenant still owes money — never appeared in arrears.
    const arrProps = activeProps
      .filter(p => ownershipMatches(p, arrearsOwnership))
      .filter(p => arrearsCurrency === 'all' || (p.currency||'GBP') === arrearsCurrency);
    const arrearsRows = [];
    arrProps.forEach(prop => {
      const isHMO = (prop.property_type || prop.propertyType || 'hmo') === 'hmo';
      if (isHMO) {
        (prop.rooms||[]).forEach(room => {
          // BUG FIX: previously required lease_start unconditionally,
          // meaning any tenant without one (confirmed as a real, live case —
          // a tenant marked moved_out with lease_end set but no lease_start
          // at all) was COMPLETELY INVISIBLE to arrears, not just
          // miscalculated. Any rent they owed before leaving could never
          // surface. Now includes any tenant with SOME usable date signal
          // (lease_start, lease_end, or move_out_date), and where
          // lease_start is missing, infers a start from whoever occupied
          // the room immediately before them — same predecessor approach
          // used server-side for payment attribution. Where there is
          // neither a lease_start NOR a predecessor to infer from (this
          // tenant is the very first on record for the room), there is
          // genuinely no reliable start date to reconstruct from — skipped
          // rather than inventing arrears that can't be substantiated.
          const roomTenancies = (tenants||[]).filter(t =>
            String(t.room_id) === String(room.id) && String(t.property_id||t.propertyId) === String(prop.id) &&
            (t.lease_start || t.lease_end || t.move_out_date)
          );
          if (!roomTenancies.length) return; // no tenancy history with any usable date at all

          const withRef = roomTenancies.map(t => {
            const ends = [t.lease_end, t.move_out_date].filter(Boolean).map(d => d.substring(0,7));
            return { t, ref: ends.length ? ends.sort()[0] : t.lease_start.substring(0,7) };
          }).sort((a,b) => a.ref.localeCompare(b.ref));

          // Map each genuinely-tenanted month to whichever tenancy covered
          // it, so a missing month is charged at THAT tenancy's actual
          // rent, not necessarily the room's current rent.
          const monthToTenancy = {};
          withRef.forEach(({ t }, idx) => {
            let start = t.lease_start ? t.lease_start.substring(0,7) : null;
            if (!start) {
              const predecessor = withRef[idx - 1];
              if (!predecessor) return; // room's first known tenant, no signal to infer a start from
              const predEnds = [predecessor.t.lease_end, predecessor.t.move_out_date].filter(Boolean).map(d=>d.substring(0,7));
              start = predEnds.length ? predEnds.sort()[0] : null;
              if (!start) return;
            }
            // BUG FIX: this only ever read lease_end, never move_out_date —
            // but the actual "Move Out" action sets move_out_date, not
            // lease_end. A moved-out tenant with no lease_end set (a common,
            // normal state for an open-ended tenancy) was therefore still
            // treated as occupying the room all the way to the current
            // month, continuing to be charged rent after they'd genuinely
            // left. Effective end is now whichever of lease_end / move_out_date
            // is set and earliest — either one alone correctly ends the
            // tenancy for arrears purposes.
            const candidateEnds = [t.lease_end, t.move_out_date].filter(Boolean).map(d => d.substring(0,7));
            const endRaw = candidateEnds.length ? candidateEnds.sort()[0] : currentMonth;
            const end = endRaw < currentMonth ? endRaw : currentMonth;
            if (start > end) return;
            monthsBetween(start, end).forEach(m => { monthToTenancy[m] = t; });
          });

          const roomPayments = payments.filter(p => (p.propertyId||p.property_id)==prop.id && String(p.roomId||p.room_id)===String(room.id));
          const paidMonthsSet = new Set(roomPayments.map(p => p.month));
          // BUG FIX: every past month is unambiguously due — a full
          // calendar month has already elapsed, no due-date nuance needed.
          // The CURRENT month is the only ambiguous one, and this
          // previously had no per-tenant due-date awareness at all: it
          // relied entirely on monthToTenancy excluding months before a
          // tenancy's lease_start, but said nothing about whether THIS
          // month's specific due date has actually arrived yet. A brand
          // new tenant whose own due day is later in the month (e.g. a
          // prorated first payment due the 28th) was flagged as overdue
          // the moment the month began, rather than the moment her own
          // payment was actually late. Fixed to check the RESPONSIBLE
          // tenant's own payment_due_day for the current month specifically
          // — falling back to the room's default only if that tenant has
          // no personal due day recorded.
          const missingMonths = Object.keys(monthToTenancy).filter(m => {
            if (paidMonthsSet.has(m)) return false;
            if (m === currentMonth) {
              const responsibleTenant = monthToTenancy[m];
              const dueDay = parseInt(responsibleTenant.payment_due_day || room.paymentDueDay || 1);
              const dueDateThisMonth = new Date(today.getFullYear(), today.getMonth(), dueDay);
              if (dueDateThisMonth >= today) return false; // this tenant's own due date hasn't arrived yet
            }
            return true;
          }).sort();

          if (missingMonths.length > 0) {
            const lastPaid = roomPayments.sort((a,b) => (b.month||'').localeCompare(a.month||''))[0]?.month || null;
            const amountOwed = missingMonths.reduce((sum, m) => sum + parseFloat(monthToTenancy[m].rent_amount || room.rentAmount || 0), 0);
            // Show the tenant responsible for the OLDEST unpaid month —
            // the most actionable one, and correct even for a room that's
            // now vacant (where room.tenantName would otherwise be blank).
            const oldestTenancy = monthToTenancy[missingMonths[0]];
            const tenantDisplayName = `${oldestTenancy.first_name} ${oldestTenancy.last_name||''}`.trim() + (oldestTenancy.status==='moved_out' ? ' (moved out)' : '');
            const displayRoom = { ...room, tenantName: tenantDisplayName };
            arrearsRows.push({ prop, room: displayRoom, lastPaid, monthsOverdue: missingMonths.length, missingMonths, amountOwed });
          }
        });
      } else {
        // BTL/Single Let — same tenancy-period-union approach as HMO
        // rooms, at property level. Also now checks ALL tenancies (not
        // just the current active one) so a departed BTL tenant who still
        // owes money isn't missed.
        // Same fix as the HMO branch above — includes any tenant with SOME
        // date signal, inferring a missing lease_start from whoever
        // occupied the property immediately before them where possible.
        const propTenancies = (tenants||[]).filter(t => String(t.property_id||t.propertyId)===String(prop.id) && (t.lease_start || t.lease_end || t.move_out_date));
        if (propTenancies.length) {
          const withRef = propTenancies.map(t => {
            const ends = [t.lease_end, t.move_out_date].filter(Boolean).map(d => String(d).substring(0,7));
            return { t, ref: ends.length ? ends.sort()[0] : String(t.lease_start).substring(0,7) };
          }).sort((a,b) => a.ref.localeCompare(b.ref));
          const monthToTenancy = {};
          withRef.forEach(({ t }, idx) => {
            let start = t.lease_start ? String(t.lease_start).substring(0,7) : null;
            if (!start) {
              const predecessor = withRef[idx - 1];
              if (!predecessor) return;
              const predEnds = [predecessor.t.lease_end, predecessor.t.move_out_date].filter(Boolean).map(d=>String(d).substring(0,7));
              start = predEnds.length ? predEnds.sort()[0] : null;
              if (!start) return;
            }
            // Same fix as the HMO branch above — respect move_out_date, not
            // just lease_end.
            const candidateEnds = [t.lease_end, t.move_out_date].filter(Boolean).map(d => String(d).substring(0,7));
            const endRaw = candidateEnds.length ? candidateEnds.sort()[0] : currentMonth;
            const end = endRaw < currentMonth ? endRaw : currentMonth;
            if (start > end) return;
            monthsBetween(start, end).forEach(m => { monthToTenancy[m] = t; });
          });
          const propPayments = payments.filter(p => (p.propertyId||p.property_id)==prop.id);
          const paidMonthsSet = new Set(propPayments.map(p => p.month));
          // Same fix as the HMO branch — only the current month is
          // ambiguous, and it's judged by the responsible tenant's own
          // due day, not assumed due from day one of the month.
          const missingMonths = Object.keys(monthToTenancy).filter(m => {
            if (paidMonthsSet.has(m)) return false;
            if (m === currentMonth) {
              const responsibleTenant = monthToTenancy[m];
              const dueDay = parseInt(responsibleTenant.payment_due_day || 1);
              const dueDateThisMonth = new Date(today.getFullYear(), today.getMonth(), dueDay);
              if (dueDateThisMonth >= today) return false;
            }
            return true;
          }).sort();
          if (missingMonths.length > 0) {
            const lastPaid = propPayments.sort((a,b) => (b.month||'').localeCompare(a.month||''))[0]?.month || null;
            const amountOwed = missingMonths.reduce((sum, m) => sum + parseFloat(monthToTenancy[m].rent_amount || monthToTenancy[m].rentAmount || 0), 0);
            const oldestTenancy = monthToTenancy[missingMonths[0]];
            const tenantName = `${oldestTenancy.first_name||''} ${oldestTenancy.last_name||''}`.trim() + (oldestTenancy.status==='moved_out' ? ' (moved out)' : '');
            const fakeRoom = { roomName: 'Full Property', tenantName, rentAmount: parseFloat(oldestTenancy.rent_amount||oldestTenancy.rentAmount||0) };
            arrearsRows.push({ prop, room: fakeRoom, lastPaid, monthsOverdue: missingMonths.length, missingMonths, amountOwed });
          }
        }
      }
    });
    arrearsRows.sort((a,b) => b.monthsOverdue - a.monthsOverdue);
    // BUG FIX: same currency-mixing issue — group by each property's own currency.
    const owedByCurrency = {};
    arrearsRows.forEach(r => {
      const cur = r.prop.currency || 'GBP';
      owedByCurrency[cur] = (owedByCurrency[cur] || 0) + r.amountOwed;
    });
    const owedCurrencies = Object.keys(owedByCurrency);
    const FilterBar = () => (
      <div style={{display:'flex',gap:'0.75rem',alignItems:'center',flexWrap:'wrap'}}>
        <select value={arrearsOwnership} onChange={e=>setArrearsOwnership(e.target.value)} style={{padding:'0.5rem',borderRadius:'0.5rem',border:'1px solid var(--border-light)'}}>
          <option value="all">All Ownership</option><option value="personal">Personal &amp; joint</option><option value="company">Company</option>
        </select>
        {availableCurrencies.length > 1 && <select value={arrearsCurrency} onChange={e=>setArrearsCurrency(e.target.value)} style={{padding:'0.5rem',borderRadius:'0.5rem',border:'1px solid var(--border-light)'}}>
          <option value="all">All Currencies</option>{availableCurrencies.map(c=><option key={c}>{c}</option>)}
        </select>}
      </div>
    );
    const exportArrears = () => {
      const wb = XLSX.utils.book_new();
      const sheetRows = [
        ['ARREARS REPORT', '', '', '', '', ''],
        [`Generated: ${xlsDate()} — Current month: ${currentMonth}`, '', '', '', '', ''],
        [],
        ['Property', 'Ownership', 'Currency', 'Room', 'Tenant', 'Rent/mo', 'Last Paid', 'Months Overdue', 'Which Months', 'Est. Amount Owed'],
        ...arrearsRows.map(r => [r.prop.address, r.prop.ownership||'', r.prop.currency||'GBP', r.room.roomName||'', r.room.tenantName||'', fmt(r.room.rentAmount||0), r.lastPaid||'Never', r.monthsOverdue, (r.missingMonths||[]).join(', '), fmt(r.amountOwed)]),
        [],
        ...owedCurrencies.map(cur => [`TOTAL ESTIMATED ARREARS (${cur})`, '', '', '', '', '', '', '', '', owedByCurrency[cur].toFixed(2)]),
      ];
      addSheet(wb, 'Arrears', sheetRows, [40,12,10,15,20,10,12,14,24,16]);
      XLSX.writeFile(wb, `Arrears-Report-${xlsDate()}.xlsx`);
    };
    return (<div><BackBtn /><div className="card">
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'1rem',flexWrap:'wrap',gap:'0.75rem'}}>
        <h2 style={{margin:0}}>⚠️ Arrears Report</h2><FilterBar />
      </div>
      <div style={{background:'#fef3c7',border:'1px solid #fde68a',borderRadius:'0.5rem',padding:'0.75rem 1rem',marginBottom:'1.5rem',fontSize:'0.875rem',color:'#92400e'}}>
        ⚠️ Shows occupied rooms where no payment has been recorded for the current month ({currentMonth}) or with multiple months outstanding. Amounts are estimates based on contracted rent.
      </div>
      {arrearsRows.length===0 ? (
        <div className="empty-state"><span>✅</span><h3>No Arrears</h3><p>All occupied rooms have a payment recorded for {currentMonth}</p></div>
      ) : (<>
        <div className="stats-grid" style={{marginBottom:'1.5rem'}}>
          <div className="stat-card"><div className="stat-label">Rooms in Arrears</div><div className="stat-value negative">{arrearsRows.length}</div></div>
          {owedCurrencies.length <= 1 ? (
            <div className="stat-card"><div className="stat-label">Est. Total Outstanding</div><div className="stat-value negative">{formatCurrency(owedCurrencies[0] ? owedByCurrency[owedCurrencies[0]] : 0, owedCurrencies[0] || 'GBP')}</div></div>
          ) : owedCurrencies.map(cur => (
            <div className="stat-card" key={cur}><div className="stat-label">Est. Outstanding ({cur})</div><div className="stat-value negative">{formatCurrency(owedByCurrency[cur], cur)}</div></div>
          ))}
          <div className="stat-card"><div className="stat-label">Avg Months Overdue</div><div className="stat-value negative">{arrearsRows.length>0?(arrearsRows.reduce((s,r)=>s+r.monthsOverdue,0)/arrearsRows.length).toFixed(1):0}</div></div>
          <div className="stat-card"><div className="stat-label">Worst Case</div><div className="stat-value negative">{arrearsRows.length>0?`${Math.max(...arrearsRows.map(r=>r.monthsOverdue))} months`:'-'}</div></div>
        </div>
        <div className="table-container"><table className="table">
          <thead><tr><th>Property</th><th>Room</th><th>Tenant</th><th>Rent/mo</th><th>Last Paid</th><th>Months Overdue</th><th>Which Months</th><th>Est. Owed</th></tr></thead>
          <tbody>{arrearsRows.map((r,i)=>{
            const monthLabel = (m) => { const [y,mo]=m.split('-').map(Number); return new Date(y,mo-1,1).toLocaleDateString('en-GB',{month:'short',year:'2-digit'}); };
            return (
            <tr key={i}>
              <td><strong>{r.prop.address}</strong><div style={{fontSize:'0.7rem',color:'var(--text-secondary)'}}>{r.prop.ownership} · {r.prop.currency||'GBP'}</div></td>
              <td>{r.room.roomName||'—'}</td>
              <td>{r.room.tenantName||'—'}</td>
              <td>{formatCurrency(r.room.rentAmount||0, r.prop.currency||'GBP')}</td>
              <td style={{color:'var(--danger)'}}>{r.lastPaid||'Never recorded'}</td>
              <td><span className={`badge ${r.monthsOverdue>=2?'badge-danger':'badge-warning'}`}>{r.monthsOverdue} month{r.monthsOverdue!==1?'s':''}</span></td>
              <td style={{fontSize:'0.8rem',color:'var(--text-secondary)',maxWidth:'220px'}}>{(r.missingMonths||[]).map(monthLabel).join(', ')}</td>
              <td style={{color:'var(--danger)',fontWeight:700}}>{formatCurrency(r.amountOwed, r.prop.currency||'GBP')}</td>
            </tr>
          );})}</tbody>
          <tfoot><tr style={{fontWeight:700,background:'var(--bg-light)'}}><td colSpan="7">Total Estimated Arrears</td><td style={{color:'var(--danger)'}}>{owedCurrencies.map(cur=>formatCurrency(owedByCurrency[cur],cur)).join(' + ') || formatCurrency(0)}</td></tr></tfoot>
        </table></div>
        <button className="btn btn-success" style={{marginTop:'1rem'}} onClick={exportArrears}><i className="ti ti-file-spreadsheet" /> Export to Excel</button>
      </>)}
    </div></div>);
  }

  // ─── VOID PERIOD REPORT ─────────────────────────────────────────────────────
  if (activeReport === 'voids') {
    const today = new Date();
    const voidProps = activeProps
      .filter(p => ownershipMatches(p, voidOwnership))
      .filter(p => voidCurrency === 'all' || (p.currency||'GBP') === voidCurrency);
    const voidRows = [];
    voidProps.forEach(prop => {
      (prop.rooms||[]).filter(r => r.status!=='occupied').forEach(room => {
        const vacantSince = room.vacantSince ? new Date(room.vacantSince) : null;
        const daysVacant = vacantSince ? Math.floor((today - vacantSince) / 86400000) : null;
        const monthlyLost = parseFloat(room.rentAmount||0);
        const totalLost = daysVacant !== null ? (monthlyLost / 30) * daysVacant : null;
        voidRows.push({ prop, room, vacantSince, daysVacant, monthlyLost, totalLost });
      });
    });
    voidRows.sort((a,b) => (b.daysVacant||0) - (a.daysVacant||0));
    // BUG FIX: same currency-mixing issue across both totals here.
    const monthlyLostByCurrency = {};
    const totalLostByCurrency = {};
    voidRows.forEach(r => {
      const cur = r.prop.currency || 'GBP';
      monthlyLostByCurrency[cur] = (monthlyLostByCurrency[cur] || 0) + r.monthlyLost;
      totalLostByCurrency[cur] = (totalLostByCurrency[cur] || 0) + (r.totalLost || 0);
    });
    const voidCurrencies = [...new Set([...Object.keys(monthlyLostByCurrency), ...Object.keys(totalLostByCurrency)])];
    const FilterBar = () => (
      <div style={{display:'flex',gap:'0.75rem',alignItems:'center',flexWrap:'wrap'}}>
        <select value={voidOwnership} onChange={e=>setVoidOwnership(e.target.value)} style={{padding:'0.5rem',borderRadius:'0.5rem',border:'1px solid var(--border-light)'}}>
          <option value="all">All Ownership</option><option value="personal">Personal &amp; joint</option><option value="company">Company</option>
        </select>
        {availableCurrencies.length > 1 && <select value={voidCurrency} onChange={e=>setVoidCurrency(e.target.value)} style={{padding:'0.5rem',borderRadius:'0.5rem',border:'1px solid var(--border-light)'}}>
          <option value="all">All Currencies</option>{availableCurrencies.map(c=><option key={c}>{c}</option>)}
        </select>}
      </div>
    );
    const exportVoids = () => {
      const wb = XLSX.utils.book_new();
      const sheetRows = [
        ['VOID PERIOD REPORT', '', '', '', '', ''],
        [`Generated: ${xlsDate()}`, '', '', '', '', ''],
        [],
        ['Property', 'Ownership', 'Currency', 'Room', 'Vacant Since', 'Days Vacant', 'Monthly Lost', 'Total Lost (est.)'],
        ...voidRows.map(r => [r.prop.address, r.prop.ownership||'', r.prop.currency||'GBP', r.room.roomName||'', r.vacantSince?r.vacantSince.toISOString().substring(0,10):'Unknown', r.daysVacant??'Unknown', fmt(r.monthlyLost), r.totalLost!==null?fmt(r.totalLost):'Unknown']),
        [],
        ...voidCurrencies.map(cur => ['TOTALS', '', '', '', '', '', `${cur}: ${(monthlyLostByCurrency[cur]||0).toFixed(2)}`, `${cur}: ${(totalLostByCurrency[cur]||0).toFixed(2)}`]),
      ];
      addSheet(wb, 'Void Periods', sheetRows, [40,12,10,15,12,12,14,16]);
      XLSX.writeFile(wb, `Void-Report-${xlsDate()}.xlsx`);
    };
    return (<div><BackBtn /><div className="card">
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'1rem',flexWrap:'wrap',gap:'0.75rem'}}>
        <h2 style={{margin:0}}>🔑 Void Period Report</h2><FilterBar />
      </div>
      {voidRows.length===0 ? (
        <div className="empty-state"><span>🏠</span><h3>No Vacant Rooms</h3><p>All rooms across the filtered properties are occupied</p></div>
      ) : (<>
        <div className="stats-grid" style={{marginBottom:'1.5rem'}}>
          <div className="stat-card"><div className="stat-label">Vacant Rooms</div><div className="stat-value negative">{voidRows.length}</div></div>
          {voidCurrencies.length <= 1 ? (
            <>
              <div className="stat-card"><div className="stat-label">Monthly Income Lost</div><div className="stat-value negative">{formatCurrency(voidCurrencies[0] ? monthlyLostByCurrency[voidCurrencies[0]] : 0, voidCurrencies[0] || 'GBP')}</div></div>
              <div className="stat-card"><div className="stat-label">Annual Projection (if unchanged)</div><div className="stat-value negative">{formatCurrency((voidCurrencies[0] ? monthlyLostByCurrency[voidCurrencies[0]] : 0)*12, voidCurrencies[0] || 'GBP')}</div></div>
              <div className="stat-card"><div className="stat-label">Total Lost to Date (est.)</div><div className="stat-value negative">{formatCurrency(voidCurrencies[0] ? totalLostByCurrency[voidCurrencies[0]] : 0, voidCurrencies[0] || 'GBP')}</div></div>
            </>
          ) : voidCurrencies.map(cur => (
            <React.Fragment key={cur}>
              <div className="stat-card"><div className="stat-label">Monthly Lost ({cur})</div><div className="stat-value negative">{formatCurrency(monthlyLostByCurrency[cur]||0, cur)}</div></div>
              <div className="stat-card"><div className="stat-label">Annual Proj. ({cur})</div><div className="stat-value negative">{formatCurrency((monthlyLostByCurrency[cur]||0)*12, cur)}</div></div>
              <div className="stat-card"><div className="stat-label">Total Lost ({cur})</div><div className="stat-value negative">{formatCurrency(totalLostByCurrency[cur]||0, cur)}</div></div>
            </React.Fragment>
          ))}
          <div className="stat-card"><div className="stat-label">Avg Days Vacant</div><div className="stat-value">{voidRows.filter(r=>r.daysVacant!==null).length>0?Math.round(voidRows.filter(r=>r.daysVacant!==null).reduce((s,r)=>s+r.daysVacant,0)/voidRows.filter(r=>r.daysVacant!==null).length):'-'} days</div></div>
          <div className="stat-card"><div className="stat-label">Longest Void</div><div className="stat-value negative">{voidRows.filter(r=>r.daysVacant!==null).length>0?`${Math.max(...voidRows.filter(r=>r.daysVacant!==null).map(r=>r.daysVacant))} days`:'-'}</div></div>
        </div>
        <div className="table-container"><table className="table">
          <thead><tr><th>Property</th><th>Room</th><th>Vacant Since</th><th>Days Vacant</th><th>Monthly Lost</th><th>Total Lost (est.)</th></tr></thead>
          <tbody>{voidRows.map((r,i)=>(
            <tr key={i}>
              <td><strong>{r.prop.address}</strong><div style={{fontSize:'0.7rem',color:'var(--text-secondary)'}}>{r.prop.ownership} · {r.prop.currency||'GBP'}</div></td>
              <td>{r.room.roomName||'—'}</td>
              <td>{r.vacantSince?r.vacantSince.toLocaleDateString('en-GB'):<span style={{color:'var(--text-secondary)'}}>Unknown</span>}</td>
              <td><span className={`badge ${(r.daysVacant||0)>60?'badge-danger':(r.daysVacant||0)>30?'badge-warning':'badge-secondary'}`}>{r.daysVacant??'Unknown'} days</span></td>
              <td style={{color:'var(--danger)',fontWeight:600}}>{formatCurrency(r.monthlyLost, r.prop.currency||'GBP')}</td>
              <td style={{color:'var(--danger)'}}>{r.totalLost!==null?formatCurrency(r.totalLost, r.prop.currency||'GBP'):'—'}</td>
            </tr>
          ))}</tbody>
          <tfoot><tr style={{fontWeight:700,background:'var(--bg-light)'}}><td colSpan="4">Totals</td><td style={{color:'var(--danger)'}}>{voidCurrencies.map(cur=>`${formatCurrency(monthlyLostByCurrency[cur]||0,cur)}/mo`).join(' + ') || formatCurrency(0)}</td><td style={{color:'var(--danger)'}}>{voidCurrencies.map(cur=>formatCurrency(totalLostByCurrency[cur]||0,cur)).join(' + ') || formatCurrency(0)}</td></tr></tfoot>
        </table></div>
        <button className="btn btn-success" style={{marginTop:'1rem'}} onClick={exportVoids}><i className="ti ti-file-spreadsheet" /> Export to Excel</button>
      </>)}
    </div></div>);
  }

  // ─── EXPENSE CATEGORY ANALYSIS ──────────────────────────────────────────────
  if (activeReport === 'expcategory') {
    const ecProps = activeProps
      .filter(p => ownershipMatches(p, expCatOwnership))
      .filter(p => expCatCurrency === 'all' || (p.currency||'GBP') === expCatCurrency);
    const ecPropIds = new Set(ecProps.map(p => p.id));
    const filteredExp = expenses.filter(e =>
      ecPropIds.has(e.propertyId||e.property_id) && e.date?.startsWith(expCatYear)
    );
    // Include company expenses when showing all or company
    const inclCorpExp = expCatOwnership === 'all' || expCatOwnership === 'company';
    const filteredCorp = inclCorpExp
      ? companyExpenses.filter(e => e.date?.startsWith(expCatYear))
      : [];
    // Build category map
    const catMap = {};
    filteredExp.forEach(e => {
      const cat = e.category || 'Other';
      if (!catMap[cat]) catMap[cat] = { total:0, paid:0, unpaid:0, count:0, items:[] };
      const amt = parseFloat(e.amount||0);
      catMap[cat].total += amt;
      if (e.paid===true||e.paid==='true') catMap[cat].paid += amt; else catMap[cat].unpaid += amt;
      catMap[cat].count++;
      catMap[cat].items.push(e);
    });
    if (filteredCorp.length > 0) {
      filteredCorp.forEach(e => {
        const cat = `Company Overhead: ${e.category||'Other'}`;
        if (!catMap[cat]) catMap[cat] = { total:0, paid:0, unpaid:0, count:0, items:[] };
        const amt = parseFloat(e.amount||0);
        catMap[cat].total += amt;
        if (e.paid===true||e.paid==='true') catMap[cat].paid += amt; else catMap[cat].unpaid += amt;
        catMap[cat].count++;
      });
    }
    const sortedCats = Object.entries(catMap).sort((a,b) => b[1].total - a[1].total);
    const grandTotal = sortedCats.reduce((s,[,d]) => s+d.total, 0);
    const grandPaid = sortedCats.reduce((s,[,d]) => s+d.paid, 0);
    const grandUnpaid = sortedCats.reduce((s,[,d]) => s+d.unpaid, 0);
    // BUG FIX: category totals blend expenses across ALL filtered properties
    // into one figure per category — genuinely meaningless (not just
    // mislabelled) if that set spans both GBP and EUR properties, since a
    // single category row could combine both currencies. Unlike the other
    // reports, this can't be fixed by grouping the total alone — the
    // category grouping itself would need to become currency-aware. Simpler
    // and safer for now: require a specific currency to be selected before
    // showing figures, rather than risk a silently wrong blended number.
    const ecCurrenciesPresent = [...new Set(ecProps.map(p => p.currency || 'GBP'))];
    const ecCurrencyAmbiguous = expCatCurrency === 'all' && ecCurrenciesPresent.length > 1;
    const ecResolvedCurrency = expCatCurrency !== 'all' ? expCatCurrency : (ecCurrenciesPresent[0] || 'GBP');
    const exportExpCat = () => {
      const wb = XLSX.utils.book_new();
      const sheetRows = [
        [`EXPENSE CATEGORY ANALYSIS — ${expCatYear}`, '', '', '', ''],
        [`Generated: ${xlsDate()}`, '', '', '', ''],
        [],
        ['Category', 'Transactions', 'Total', 'Paid', 'Unpaid', '% of Total'],
        ...sortedCats.map(([cat,d]) => [cat, d.count, fmt(d.total), fmt(d.paid), fmt(d.unpaid), fmtPct(grandTotal>0?(d.total/grandTotal)*100:0)]),
        [],['GRAND TOTAL', sortedCats.reduce((s,[,d])=>s+d.count,0), fmt(grandTotal), fmt(grandPaid), fmt(grandUnpaid), '100%'],
      ];
      addSheet(wb, 'Category Summary', sheetRows, [35,12,14,14,14,10]);
      // Expense detail
      const detailRows = [
        [`EXPENSE DETAIL BY CATEGORY — ${expCatYear}`, '', '', '', '', '', ''],
        [],
        ['Date', 'Property', 'Ownership', 'Category', 'Amount', 'Paid', 'Description'],
        ...filteredExp.sort((a,b)=>(a.category||'').localeCompare(b.category||'')).map(e => {
          const prop = properties.find(p=>p.id==(e.propertyId||e.property_id));
          return [e.date||'', prop?.address||'Unknown', prop?.ownership||'', e.category||'', fmt(e.amount), (e.paid===true||e.paid==='true')?'Yes':'No', e.description||''];
        }),
      ];
      addSheet(wb, 'Expense Detail', detailRows, [12,40,12,20,12,8,30]);
      XLSX.writeFile(wb, `Expense-Category-Analysis-${expCatYear}.xlsx`);
    };
    const maxCatTotal = sortedCats.length > 0 ? sortedCats[0][1].total : 1;
    return (<div><BackBtn /><div className="card">
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'1rem',flexWrap:'wrap',gap:'0.75rem'}}>
        <h2 style={{margin:0}}>🗂️ Expense Category Analysis</h2>
        <div style={{display:'flex',gap:'0.75rem',alignItems:'center',flexWrap:'wrap'}}>
          <select value={expCatYear} onChange={e=>setExpCatYear(e.target.value)} style={{padding:'0.5rem',borderRadius:'0.5rem',border:'1px solid var(--border-light)'}}>{years.map(y=><option key={y}>{y}</option>)}</select>
          <select value={expCatOwnership} onChange={e=>setExpCatOwnership(e.target.value)} style={{padding:'0.5rem',borderRadius:'0.5rem',border:'1px solid var(--border-light)'}}>
            <option value="all">All Ownership</option><option value="personal">Personal &amp; joint</option><option value="company">Company</option>
          </select>
          {availableCurrencies.length > 1 && <select value={expCatCurrency} onChange={e=>setExpCatCurrency(e.target.value)} style={{padding:'0.5rem',borderRadius:'0.5rem',border:'1px solid var(--border-light)'}}>
            <option value="all">All Currencies</option>{availableCurrencies.map(c=><option key={c}>{c}</option>)}
          </select>}
        </div>
      </div>
      {ecCurrencyAmbiguous ? (
        <div style={{background:'#fef3c7',border:'1px solid #fde68a',borderRadius:'0.5rem',padding:'1rem 1.25rem',color:'#92400e'}}>
          ⚠️ Your portfolio has properties in more than one currency ({ecCurrenciesPresent.join(' and ')}). Category totals can't be meaningfully combined across currencies — please select a specific currency above to view this report.
        </div>
      ) : (<>
      <div className="stats-grid" style={{marginBottom:'1.5rem'}}>
        <div className="stat-card"><div className="stat-label">Total Spending</div><div className="stat-value negative">{formatCurrency(grandTotal, ecResolvedCurrency)}</div></div>
        <div className="stat-card"><div className="stat-label">Paid</div><div className="stat-value negative">{formatCurrency(grandPaid, ecResolvedCurrency)}</div></div>
        <div className="stat-card"><div className="stat-label">Unpaid / Outstanding</div><div className="stat-value negative">{formatCurrency(grandUnpaid, ecResolvedCurrency)}</div></div>
        <div className="stat-card"><div className="stat-label">Categories</div><div className="stat-value">{sortedCats.length}</div></div>
      </div>
      {sortedCats.length===0 ? (
        <div className="empty-state"><span>🗂️</span><h3>No Expenses</h3><p>No expenses recorded for {expCatYear} with the selected filters</p></div>
      ) : (
        <div className="table-container"><table className="table">
          <thead><tr><th>Category</th><th>Transactions</th><th>Total</th><th>Paid</th><th>Unpaid</th><th>% of Total</th><th>Proportion</th></tr></thead>
          <tbody>{sortedCats.map(([cat,d],i)=>(
            <tr key={i}>
              <td><strong>{cat}</strong></td>
              <td>{d.count}</td>
              <td style={{fontWeight:600}}>{formatCurrency(d.total, ecResolvedCurrency)}</td>
              <td style={{color:'var(--success)'}}>{formatCurrency(d.paid, ecResolvedCurrency)}</td>
              <td style={{color:d.unpaid>0?'var(--danger)':'var(--text-secondary)'}}>{formatCurrency(d.unpaid, ecResolvedCurrency)}</td>
              <td>{grandTotal>0?`${((d.total/grandTotal)*100).toFixed(1)}%`:'—'}</td>
              <td style={{width:'120px'}}>
                <div style={{background:'var(--bg-light)',borderRadius:'4px',height:'8px',overflow:'hidden'}}>
                  <div style={{background:'#ef4444',height:'100%',width:`${maxCatTotal>0?(d.total/maxCatTotal)*100:0}%`,borderRadius:'4px'}} />
                </div>
              </td>
            </tr>
          ))}</tbody>
          <tfoot><tr style={{fontWeight:700,background:'var(--bg-light)'}}><td>Grand Total</td><td>{sortedCats.reduce((s,[,d])=>s+d.count,0)}</td><td>{formatCurrency(grandTotal, ecResolvedCurrency)}</td><td style={{color:'var(--success)'}}>{formatCurrency(grandPaid, ecResolvedCurrency)}</td><td style={{color:'var(--danger)'}}>{formatCurrency(grandUnpaid, ecResolvedCurrency)}</td><td>100%</td><td></td></tr></tfoot>
        </table></div>
      )}
      </>)}
      {!ecCurrencyAmbiguous && sortedCats.length>0 && <button className="btn btn-success" style={{marginTop:'1rem'}} onClick={exportExpCat}><i className="ti ti-file-spreadsheet" /> Export to Excel</button>}
    </div></div>);
  }

  // ─── YIELD ANALYSIS ─────────────────────────────────────────────────────────
  if (activeReport === 'yield') {
    const yieldProps = activeProps
      .filter(p => ownershipMatches(p, yieldOwnership))
      .filter(p => yieldCurrency === 'all' || (p.currency||'GBP') === yieldCurrency);
    const currentYear = new Date().getFullYear().toString();
    const yieldRows = yieldProps.map(prop => {
      const cd = prop.capitalData || prop.capital_data || {};
      const purchasePrice = parseFloat(cd.purchasePrice||0);
      const currentValue = parseFloat(cd.currentValue||0);
      const mortgageBalance = parseFloat(cd.mortgageBalance||0);
      const rooms = prop.rooms || [];
      const monthlyRent = getPropertyMonthlyRent(prop, rooms);
      const annualRent = monthlyRent * 12;
      // YTD actual income this year
      const actualIncome = payments
        .filter(p => (p.propertyId||p.property_id)==prop.id && p.month?.startsWith(currentYear))
        .reduce((s,p) => s+parseFloat(p.amount||0), 0);
      // YTD actual expenses this year
      const actualExpenses = expenses
        .filter(e => (e.propertyId||e.property_id)==prop.id && (e.paid===true||e.paid==='true') && e.date?.startsWith(currentYear))
        .reduce((s,e) => s+parseFloat(e.amount||0), 0);
      const grossYield = purchasePrice > 0 ? (annualRent / purchasePrice * 100) : 0;
      const annualExpEst = actualExpenses * (12 / Math.max(new Date().getMonth()+1, 1)); // annualise YTD
      const netAnnualIncome = annualRent - annualExpEst;
      const netYield = purchasePrice > 0 ? (netAnnualIncome / purchasePrice * 100) : 0;
      const equity = currentValue - mortgageBalance;
      const roi = equity > 0 ? (netAnnualIncome / equity * 100) : 0;
      const occupancy = rooms.length > 0 ? (rooms.filter(r=>r.status==='occupied').length / rooms.length * 100) : 0;
      return { ...prop, purchasePrice, currentValue, mortgageBalance, monthlyRent, annualRent, grossYield, netYield, roi, equity, actualIncome, actualExpenses, occupancy, rooms };
    }).filter(p => p.purchasePrice > 0 || p.annualRent > 0);
    // Sort by gross yield descending
    yieldRows.sort((a,b) => b.grossYield - a.grossYield);
    const avgGrossYield = yieldRows.length > 0 ? yieldRows.reduce((s,p)=>s+p.grossYield,0)/yieldRows.length : 0;
    const avgNetYield = yieldRows.length > 0 ? yieldRows.reduce((s,p)=>s+p.netYield,0)/yieldRows.length : 0;
    const exportYield = () => {
      const wb = XLSX.utils.book_new();
      const sheetRows = [
        ['YIELD ANALYSIS REPORT', '', '', '', '', '', ''],
        [`Generated: ${xlsDate()} — YTD expenses annualised from ${currentYear}`, '', '', '', '', '', ''],
        [],
        ['Property', 'Ownership', 'Currency', 'Purchase Price', 'Monthly Rent', 'Annual Rent', 'Gross Yield %', 'Net Yield % (est.)', 'ROI % (est.)', 'Occupancy %'],
        ...yieldRows.map(p => [p.address, p.ownership||'', p.currency||'GBP', p.purchasePrice>0?fmt(p.purchasePrice):'', fmt(p.monthlyRent), fmt(p.annualRent), fmtPct(p.grossYield), fmtPct(p.netYield), fmtPct(p.roi), fmtPct(p.occupancy)]),
        [],['AVERAGES', '', '', '', '', '', fmtPct(avgGrossYield), fmtPct(avgNetYield), '', ''],
      ];
      addSheet(wb, 'Yield Analysis', sheetRows, [40,12,10,16,14,14,12,14,12,12]);
      XLSX.writeFile(wb, `Yield-Analysis-${xlsDate()}.xlsx`);
    };
    return (<div><BackBtn /><div className="card">
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'1rem',flexWrap:'wrap',gap:'0.75rem'}}>
        <h2 style={{margin:0}}>📐 Yield Analysis</h2>
        <div style={{display:'flex',gap:'0.75rem',alignItems:'center',flexWrap:'wrap'}}>
          <select value={yieldOwnership} onChange={e=>setYieldOwnership(e.target.value)} style={{padding:'0.5rem',borderRadius:'0.5rem',border:'1px solid var(--border-light)'}}>
            <option value="all">All Ownership</option><option value="personal">Personal &amp; joint</option><option value="company">Company</option>
          </select>
          {availableCurrencies.length > 1 && <select value={yieldCurrency} onChange={e=>setYieldCurrency(e.target.value)} style={{padding:'0.5rem',borderRadius:'0.5rem',border:'1px solid var(--border-light)'}}>
            <option value="all">All Currencies</option>{availableCurrencies.map(c=><option key={c}>{c}</option>)}
          </select>}
        </div>
      </div>
      <div style={{background:'#eff6ff',border:'1px solid #bfdbfe',borderRadius:'0.5rem',padding:'0.75rem 1rem',marginBottom:'1.5rem',fontSize:'0.875rem',color:'#1e40af'}}>
        💡 Gross yield uses contracted annual rent ÷ purchase price. Net yield and ROI are estimates based on YTD {currentYear} expenses annualised. For guidance only.
      </div>
      {yieldRows.length===0 ? (
        <div className="empty-state"><span>📐</span><h3>No Data</h3><p>Add purchase prices and rent to your properties to see yield analysis</p></div>
      ) : (<>
        <div className="stats-grid" style={{marginBottom:'1.5rem'}}>
          <div className="stat-card"><div className="stat-label">Properties Analysed</div><div className="stat-value">{yieldRows.length}</div></div>
          <div className="stat-card"><div className="stat-label">Avg Gross Yield</div><div className={`stat-value ${avgGrossYield>=6?'positive':avgGrossYield>=4?'':'negative'}`}>{avgGrossYield.toFixed(2)}%</div></div>
          <div className="stat-card"><div className="stat-label">Avg Net Yield (est.)</div><div className={`stat-value ${avgNetYield>=4?'positive':avgNetYield>=2?'':'negative'}`}>{avgNetYield.toFixed(2)}%</div></div>
          <div className="stat-card"><div className="stat-label">Best Gross Yield</div><div className="stat-value positive">{yieldRows.length>0?`${Math.max(...yieldRows.map(p=>p.grossYield)).toFixed(2)}%`:'-'}</div></div>
        </div>
        <div className="table-container"><table className="table">
          <thead><tr><th>Rank</th><th>Property</th><th>Ownership</th><th>Currency</th><th>Monthly Rent</th><th>Gross Yield</th><th>Net Yield (est.)</th><th>ROI (est.)</th><th>Occupancy</th></tr></thead>
          <tbody>{yieldRows.map((p,i)=>(
            <tr key={p.id}>
              <td><strong style={{color:i===0?'#15803d':i===1?'#1e40af':i===2?'#6b21a8':'inherit'}}>#{i+1}</strong></td>
              <td><strong>{p.address}</strong>{p.purchasePrice>0&&<div style={{fontSize:'0.7rem',color:'var(--text-secondary)'}}>Purchased: {formatCurrency(p.purchasePrice, p.currency||'GBP')}</div>}</td>
              <td><span className="badge badge-info">{p.ownership}</span></td>
              <td>{p.currency||'GBP'}</td>
              <td style={{fontWeight:600}}>{formatCurrency(p.monthlyRent, p.currency||'GBP')}</td>
              <td><span className={`badge ${p.grossYield>=8?'badge-success':p.grossYield>=6?'badge-warning':'badge-danger'}`}>{p.grossYield.toFixed(2)}%</span></td>
              <td style={{color:p.netYield>=4?'var(--success)':p.netYield>=2?'var(--warning)':'var(--danger)',fontWeight:600}}>{p.netYield.toFixed(2)}%</td>
              <td style={{color:p.roi>=10?'var(--success)':p.roi>=5?'var(--warning)':'var(--danger)'}}>{p.roi.toFixed(2)}%</td>
              <td><span className={`badge ${p.occupancy===100?'badge-success':p.occupancy>=75?'badge-warning':'badge-danger'}`}>{p.occupancy.toFixed(0)}%</span></td>
            </tr>
          ))}</tbody>
          <tfoot><tr style={{fontWeight:700,background:'var(--bg-light)'}}><td colSpan="5">Portfolio Average</td><td>{avgGrossYield.toFixed(2)}%</td><td>{avgNetYield.toFixed(2)}%</td><td>—</td><td>—</td></tr></tfoot>
        </table></div>
        <button className="btn btn-success" style={{marginTop:'1rem'}} onClick={exportYield}><i className="ti ti-file-spreadsheet" /> Export to Excel</button>
      </>)}
    </div></div>);
  }

  return null;
}
